// frontend/src/components/Compliance.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import "../dashboard.css";
import RemediationTaskModal from "./remediation/RemediationTaskModal";
import FixPlanModal from "./remediation/FixPlanModal";
import ValidationRunModal from "./remediation/ValidationRunModal";
import RemediationOperationsPanel from "./remediation/RemediationOperationsPanel";
import PostureIntelligencePanel from "./posture/PostureIntelligencePanel";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import StaleControlsModal from "./compliance/modals/StaleControlsModal";
import ActiveFindingsModal from "./compliance/modals/ActiveFindingsModal";
import ApprovedExceptionsModal from "./compliance/modals/ApprovedExceptionsModal";
import ValidationFailedModal from "./compliance/modals/ValidationFailedModal";
import OverdueTasksModal from "./compliance/modals/OverdueTasksModal";
import InProgressTasksModal from "./compliance/modals/InProgressTasksModal";
import ClosedTasksModal from "./compliance/modals/ClosedTasksModal";
import AllActiveTasksModal from "./compliance/modals/AllActiveTasksModal";
import QueueStatusTasksModal from "./compliance/modals/QueueStatusTasksModal";
import PostureStatusModal from "./compliance/modals/PostureStatusModal";
import RiskQueueModal from "./compliance/modals/RiskQueueModal";
import RiskGuidanceModal from "./compliance/modals/RiskGuidanceModal";
import {
  Button,
  StatusChip,
} from "../design-system/components";
import ScoreDetailsModal from "./compliance/modals/ScoreDetailsModal";
import TaskNextStepModal from "./compliance/modals/TaskNextStepModal";
import {
  MetricCard,
  MetaField,
  TaskStatusPill,
  getSelectableCardStyle,
  getSelectableTableRowStyle,
} from "./remediation/SharedRemediationUI";
import {
  safeNumber,
  formatTimestamp,
  formatDateInput,
  formatRelativeTime,
  normalizeText,
  includesNormalized,
  cleanValue,
  normalizeRouteParamValue,
  buildControlLabel,
  isCredentialActive,
  getTaskStatusLabel,
  getDisplayStateLabel,
  getSummaryChipStyle,
} from "../utils/complianceFormatters";

import {
  getAwsEvidenceContext,
  getFullAssessmentPlanForRule,
  getAccessKeySlotLabel,
  isIamAccessKeyRule,
  isIamCredentialReportRule,
} from "../utils/awsEvidenceContext";

import {
  buildFixPlanItem,
  buildFixPlanSteps,
  buildValidationText,
  findTopRiskContextForFixPlan,
  getRulePlanDiagnostic,
  mergeFixPlanWithSourceContext,
} from "../utils/remediationFixPlans";

import {
  buildInvestigationDraftFromTask,
  buildReadyForValidationBlockedMessage,
  canShowReadyForValidationNextStep,
  findTaskForItem,
  getTaskBlockReason,
  isClosedTask,
  isTaskOverdue,
  normalizeRemediationTaskEvents,
  normalizeRemediationTasks,
  sortRemediationTasksNewestFirst,
  sortTaskEventsNewestFirst,
  taskMatchesItem,
} from "../utils/remediationTaskHelpers";
import {
  fetchRemediationOperationsQueue,
  fetchRemediationTaskClosureGuard,
  recoverRemediationTaskWorkflow,
  rerunRemediationTaskValidation,
} from "../services/remediationOperationsApi";

const FRAMEWORKS = [
  "SOC2",
  "NIST",
  "ISO",
  "FedRAMP",
  "FDA",
  "AML/KYC",
  "CCPA",
  "OSHA",
];

const REQUEST_TIMEOUT_MS = 30000;
const POSTURE_EXPLAIN_PROMPTS = [
  "Why is this control in this state?",
  "Can I trust this control state?",
  "What is affecting this control?",
  "What should I review next?",
];
const CLOSURE_GUARD_TIMEOUT_MS = 90000;
const FULL_CLOUD_ASSESSMENT_TIMEOUT_MS = 6 * 60 * 1000;
const SYNC_JOB_POLL_INTERVAL_MS = 3000;
const SUCCESS_NOTICE_AUTO_DISMISS_MS = 7000;
const SHOW_LEGACY_REMEDIATION_TASKS = false;

const REMEDIATION_QUEUE_ROUTE_LABELS = {
  all: ["all active"],
  active: ["all active"],
  open: ["open"],
  in_progress: ["in progress"],
  ready: ["ready"],
  ready_for_validation: ["ready"],
  validation_failed: ["validation failed"],
  overdue: ["overdue"],
  approved_exception: ["approved exceptions"],
  approved_exceptions: ["approved exceptions"],
  closed: ["closed"],
  unassigned: ["unassigned"],
};

function normalizeRouteQueue(value) {
  const normalized = normalizeRouteParamValue(value);
  if (!normalized) return "";
  return normalized.toLowerCase().replace(/[\s-]+/g, "_");
}

function normalizeSummaryFrameworks(payload) {
  if (Array.isArray(payload?.frameworks)) return payload.frameworks;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeTopRisks(payload) {
  if (Array.isArray(payload?.controls)) return payload.controls;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeFindings(payload) {
  if (Array.isArray(payload?.findings)) return payload.findings;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeStaleControls(payload) {
  if (Array.isArray(payload?.controls)) return payload.controls;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizePostureDelta(payload) {
  if (Array.isArray(payload?.frameworks)) return payload.frameworks;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeControlStatus(payload) {
  if (Array.isArray(payload?.controls)) return payload.controls;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function matchControlStatusRow(row, context) {
  if (!row || !context) return false;

  const frameworkMatch = !context.framework
    ? true
    : normalizeText(row.framework) === normalizeText(context.framework);

  const controlMatch =
    !context.controlId ||
    normalizeText(row.control_id) === normalizeText(context.controlId) ||
    includesNormalized(row.control_id, context.controlId);

  const ruleMatch =
    !context.ruleId ||
    normalizeText(row.rule_id) === normalizeText(context.ruleId) ||
    includesNormalized(row.rule_id, context.ruleId);

  const resourceMatch =
    !context.resourceId ||
    includesNormalized(row.resource_id, context.resourceId);

  const hasAnyScopedField = Boolean(
    context.framework || context.controlId || context.ruleId || context.resourceId
  );

  return hasAnyScopedField && frameworkMatch && controlMatch && ruleMatch && resourceMatch;
}

function matchRiskRow(row, context) {
  if (!row || !context) return false;

  const frameworkMatch = !context.framework
    ? true
    : normalizeText(row.framework) === normalizeText(context.framework);

  const controlMatch =
    !context.controlId ||
    normalizeText(row.control_id) === normalizeText(context.controlId) ||
    includesNormalized(row.control_id, context.controlId) ||
    includesNormalized(row.reason_summary, context.controlId) ||
    includesNormalized(row.remediation_hint, context.controlId);

  const ruleMatch =
    !context.ruleId ||
    normalizeText(row.rule_id) === normalizeText(context.ruleId) ||
    includesNormalized(row.rule_id, context.ruleId) ||
    includesNormalized(row.reason_summary, context.ruleId) ||
    includesNormalized(row.remediation_hint, context.ruleId);

  const resourceMatch =
    !context.resourceId ||
    includesNormalized(row.resource_id, context.resourceId) ||
    includesNormalized(row.reason_summary, context.resourceId) ||
    includesNormalized(row.remediation_hint, context.resourceId);

  const hasAnyScopedField = Boolean(
    context.framework || context.controlId || context.ruleId || context.resourceId
  );

  return hasAnyScopedField && frameworkMatch && controlMatch && ruleMatch && resourceMatch;
}

function pickBestFocus({ controlRows, topRisks, staleControls, context }) {
  const hasContext = Boolean(
    context?.framework || context?.controlId || context?.ruleId || context?.resourceId
  );

  if (!hasContext) {
    return { kind: null, item: null, matched: false };
  }

  const exactControl = controlRows.find((row) => matchControlStatusRow(row, context));
  if (exactControl) return { kind: "control", item: exactControl, matched: true };

  const exactRisk = topRisks.find((row) => matchRiskRow(row, context));
  if (exactRisk) return { kind: "risk", item: exactRisk, matched: true };

  const exactStale = staleControls.find((row) => matchRiskRow(row, context));
  if (exactStale) return { kind: "stale", item: exactStale, matched: true };

  return {
    kind: "context",
    matched: false,
    item: {
      framework: context.framework || "—",
      control_id: context.controlId || "—",
      rule_id: context.ruleId || "—",
      resource_id: context.resourceId || "—",
      state: "—",
      freshness_state: "—",
      open_findings_total: "—",
      last_checked_at: null,
      reason_summary: "No direct posture row match was found for the selected drift context.",
    },
  };
}

function normalizeRelatedSops(payload) {
  if (Array.isArray(payload?.linked_sops)) return payload.linked_sops;
  if (Array.isArray(payload?.sops)) return payload.sops;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeRecommendedActions(payload) {
  if (Array.isArray(payload?.recommended_actions)) return payload.recommended_actions;
  if (Array.isArray(payload?.remediation_card?.what_to_do)) {
    return payload.remediation_card.what_to_do;
  }
  return [];
}


function getTaskLastValidation(task = {}) {
  const metadata = task?.task_metadata || task?.metadata || {};
  return metadata?.last_validation || metadata?.lastValidation || null;
}

function isApprovedExceptionTask(task = {}) {
  const validationStatus = normalizeText(task?.validation_status || task?.validationStatus || "");
  const lastValidation = getTaskLastValidation(task);
  const lastStatus = normalizeText(
    lastValidation?.status ||
      lastValidation?.validation_status ||
      lastValidation?.result ||
      lastValidation?.validation_result ||
      ""
  );
  const governance = normalizeText(
    lastValidation?.metadata?.validation_governance ||
      lastValidation?.metadata?.governance ||
      lastValidation?.metadata?.exception_type ||
      ""
  );
  const decisionPath = normalizeText(lastValidation?.metadata?.decision_path || "");

  return (
    validationStatus === "approved_exception" ||
    lastStatus === "approved_exception" ||
    governance === "approved_exception" ||
    decisionPath.includes("approved_exception") ||
    decisionPath.includes("retain_as_approved_exception")
  );
}

function mergeTaskPreservingApprovedException(currentTask = {}, updatedTask = {}) {
  if (!updatedTask) return currentTask;

  const currentIsApprovedException = isApprovedExceptionTask(currentTask);
  const updatedIsFailed = normalizeText(updatedTask?.validation_status || "") === "failed";

  if (!currentIsApprovedException || !updatedIsFailed) {
    return updatedTask;
  }

  const currentMetadata = currentTask?.task_metadata || currentTask?.metadata || {};
  const updatedMetadata = updatedTask?.task_metadata || updatedTask?.metadata || {};

  return {
    ...updatedTask,
    validation_status: "approved_exception",
    validated_at: currentTask?.validated_at || updatedTask?.validated_at || null,
    task_metadata: {
      ...updatedMetadata,
      ...currentMetadata,
      last_validation: currentMetadata?.last_validation || updatedMetadata?.last_validation || null,
    },
  };
}

function mergeTaskCollectionsPreservingApprovedExceptions(currentTasks = [], incomingTasks = []) {
  const currentById = new Map(
    (Array.isArray(currentTasks) ? currentTasks : [])
      .filter((task) => task?.id)
      .map((task) => [task.id, task])
  );

  return (Array.isArray(incomingTasks) ? incomingTasks : []).map((task) => {
    if (!task?.id) return task;
    const currentTask = currentById.get(task.id);
    return currentTask ? mergeTaskPreservingApprovedException(currentTask, task) : task;
  });
}

function buildApprovedExceptionValidationUiState({ validationSummary = "", task = {}, fallbackLabel = "" } = {}) {
  const lastValidation = getTaskLastValidation(task) || {};
  const metadata = lastValidation?.metadata || {};
  const reviewDate = cleanValue(metadata.review_date, "");
  const decisionPath = cleanValue(metadata.decision_path, "approved exception").replace(/_/g, " ");
  const owner = cleanValue(metadata.business_owner || task?.owner_name, "the approved decision owner");

  return {
    tone: "warn",
    title: "Approved Exception Recorded",
    subtitle: task?.id ? buildControlLabel(task.control_id, task.rule_id) : fallbackLabel,
    summary: "Approved exception recorded. Source finding may still exist by design.",
    detail:
      `The source finding may still appear during validation, but this task has an approved, evidence-backed exception. Retained risk is documented, approved by ${owner}, and time-bound${reviewDate ? ` for review on ${reviewDate}` : ""}.`,
    nextSteps: [
      "Open Review / Manage for this remediation task.",
      "Set the task status to Validated and save the task.",
      "Close the task with a closure reason that states the role was retained through an approved exception.",
      `Decision path: ${decisionPath}.`,
    ],
  };
}

function getFocusControlContext(focusedContext, frameworkFallback) {
  const item = focusedContext?.item || {};
  const controlId = cleanValue(item.control_id, "");
  const ruleId = cleanValue(item.rule_id, "");
  const focusFramework = cleanValue(item.framework, frameworkFallback || "");

  if (!controlId || controlId === "—") return null;

  return {
    framework: focusFramework === "—" ? frameworkFallback : focusFramework,
    controlId,
    ruleId: ruleId === "—" ? "" : ruleId,
  };
}

function buildControlIdCandidates(controlId, frameworkValue) {
  const rawControlId = normalizeRouteParamValue(controlId);
  const rawFramework = normalizeRouteParamValue(frameworkValue);
  if (!rawControlId) return [];

  const candidates = [];
  const pushCandidate = (value) => {
    const cleaned = normalizeRouteParamValue(value);
    const key = cleaned.toLowerCase();
    if (cleaned && !candidates.some((item) => item.toLowerCase() === key)) {
      candidates.push(cleaned);
    }
  };

  pushCandidate(rawControlId);

  if (rawFramework && !rawControlId.toLowerCase().startsWith(`${rawFramework.toLowerCase()}-`)) {
    pushCandidate(`${rawFramework}-${rawControlId}`);
  }

  return candidates;
}

function hasLinkedSops(payload) {
  return normalizeRelatedSops(payload).length > 0;
}

function buildRemediationFetchAttempts(focusControlContext, frameworkFallback) {
  if (!focusControlContext?.controlId) return [];

  const frameworkValue = focusControlContext.framework || frameworkFallback;
  const controlCandidates = buildControlIdCandidates(
    focusControlContext.controlId,
    frameworkValue
  );
  const ruleId = normalizeRouteParamValue(focusControlContext.ruleId);

  const attempts = [];

  controlCandidates.forEach((controlId) => {
    if (ruleId) {
      attempts.push({
        controlId,
        params: {
          framework: frameworkValue,
          rule_id: ruleId,
          finding_summary: buildControlLabel(controlId, ruleId),
        },
      });

      return;
    }

    attempts.push({
      controlId,
      params: {
        framework: frameworkValue,
        finding_summary: buildControlLabel(controlId, ruleId),
      },
    });
  });

  return attempts;
}

function getRelatedSopTitle(sop) {
  return (
    sop?.title ||
    sop?.sop_title ||
    sop?.name ||
    sop?.code ||
    "Untitled SOP"
  );
}

function getRelatedSopVersionLabel(sop) {
  const version =
    sop?.current_version_number ||
    sop?.version_number ||
    sop?.latest_version_number ||
    null;

  return version ? `v${version}` : "version unknown";
}

function getRelatedSopReleaseLabel(sop) {
  const status = cleanValue(sop?.release_status || sop?.current_version_status || sop?.status, "unknown");
  const effective = sop?.effective_date ? ` · effective ${sop.effective_date}` : "";
  return `${status.replace(/_/g, " ")}${effective}`;
}

function getRelatedSopId(sop) {
  return (
    sop?.sop_id ||
    sop?.sopId ||
    sop?.id ||
    sop?.raw?.sop_id ||
    sop?.raw?.id ||
    ""
  );
}


const RULE_PLAN_FAMILY_ORDER = ["iam", "s3", "cloudtrail", "config", "generic"];

const RULE_PLAN_FAMILY_SHORT_LABELS = {
  iam: "IAM-specific",
  s3: "S3-specific",
  cloudtrail: "CloudTrail-specific",
  config: "Config-specific",
  generic: "Standard",
};

const riskMatchesQueueFilterWithEc2 = (row, key, options) => {
  if (key === "ec2") {
    const ruleId = String(row?.rule_id || row?.ruleId || "").toUpperCase();
    return ruleId.startsWith("EC2_");
  }
  return riskMatchesQueueFilter(row, key, options);
};

const RISK_QUEUE_FILTERS = [
  { key: "all", label: "All" },
  { key: "s3", label: "S3" },
  { key: "iam", label: "IAM" },
  { key: "identity", label: "Identity" },
  { key: "code", label: "Code" },
  { key: "edge", label: "Edge" },
  { key: "cloudtrail", label: "CloudTrail" },
  { key: "config", label: "Config" },
  { key: "ec2", label: "EC2" },
  { key: "high", label: "High Severity" },
  { key: "has_task", label: "Has Task" },
  { key: "no_task", label: "No Task" },
  { key: "validation_failed", label: "Validation Failed" },
  { key: "in_progress", label: "In Progress" },
  { key: "ready", label: "Ready" },
  { key: "blocked", label: "Blocked" },
  { key: "validated", label: "Validated" },
];

const IDENTITY_FINDING_RULE_LABELS = {
  GW_2SV_DISABLED: "Google Workspace 2SV disabled",
  GW_UNUSED_USER: "Google Workspace unused user",
  GW_INACTIVE_ADMIN: "Google Workspace inactive admin",
  GW_ADMIN_COUNT: "Google Workspace admin coverage",
  GW_SUPER_ADMIN_EXPOSURE: "Google Workspace super admin exposure",
  GW_LOGIN_ACTIVITY_OBSERVED: "Google Workspace login activity observed",
  OKTA_MFA_DISABLED: "Okta MFA disabled",
  OKTA_UNUSED_ACCOUNT: "Okta unused account",
  OKTA_STALE_ADMIN: "Okta stale admin",
  OKTA_ADMIN_EXPOSURE: "Okta admin exposure",
  OKTA_PRIVILEGED_GROUP_EXPOSURE: "Okta privileged group exposure",
  OKTA_SYSTEM_LOG_ACTIVITY_OBSERVED: "Okta system log activity observed",
};

const IDENTITY_RULE_CONTROL_BY_FRAMEWORK = {
  GW_2SV_DISABLED: {
    SOC2: "CC6.1",
    NIST: "IA",
    FedRAMP: "IA",
    HIPAA: "164.312",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_MFA_DISABLED: {
    SOC2: "CC6.1",
    NIST: "IA",
    FedRAMP: "IA",
    HIPAA: "164.312",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_SUPER_ADMIN_EXPOSURE: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_PRIVILEGED_GROUP_EXPOSURE: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_INACTIVE_ADMIN: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_STALE_ADMIN: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_UNUSED_USER: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_UNUSED_ACCOUNT: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_ADMIN_COUNT: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  OKTA_ADMIN_EXPOSURE: {
    SOC2: "CC6.1",
    NIST: "AC",
    FedRAMP: "AC",
    HIPAA: "164.308",
    ISO27001: "A.5",
    ISO: "A.5",
  },
  GW_LOGIN_ACTIVITY_OBSERVED: {
    SOC2: "CC7.2",
    NIST: "AU",
    FedRAMP: "AU",
    HIPAA: "164.312",
    ISO27001: "A.8",
    ISO: "A.8",
  },
  OKTA_SYSTEM_LOG_ACTIVITY_OBSERVED: {
    SOC2: "CC7.2",
    NIST: "AU",
    FedRAMP: "AU",
    HIPAA: "164.312",
    ISO27001: "A.8",
    ISO: "A.8",
  },
};


const CODE_EDGE_FINDING_RULE_LABELS = {
  GH_PUBLIC_REPOSITORY: "GitHub public repository",
  GH_BRANCH_PROTECTION_DISABLED: "GitHub branch protection disabled",
  GH_REQUIRED_REVIEWS_DISABLED: "GitHub required reviews disabled",
  GH_ADMIN_BYPASS_ENABLED: "GitHub admin bypass enabled",
  GH_SECRET_SCANNING_DISABLED: "GitHub secret scanning disabled",
  GH_DEPENDABOT_DISABLED: "GitHub Dependabot disabled",
  CF_WAF_DISABLED: "Cloudflare WAF disabled",
  CF_TLS_MIN_VERSION_WEAK: "Cloudflare weak TLS minimum",
  CF_ALWAYS_USE_HTTPS_DISABLED: "Cloudflare HTTPS enforcement disabled",
  CF_SECURITY_LEVEL_LOW: "Cloudflare security level low",
  CF_DNSSEC_DISABLED: "Cloudflare DNSSEC disabled or unreadable",
  CF_ZONE_ACTIVITY_OBSERVED: "Cloudflare zone inventory observed",
};

const CODE_EDGE_RULE_CONTROL_BY_FRAMEWORK = {
  GH_PUBLIC_REPOSITORY: { SOC2: "CC6.6", NIST: "AC", FedRAMP: "AC", ISO: "A.5" },
  GH_BRANCH_PROTECTION_DISABLED: { SOC2: "CC8.1", NIST: "CM", FedRAMP: "CM", ISO: "A.8" },
  GH_REQUIRED_REVIEWS_DISABLED: { SOC2: "CC8.1", NIST: "CM", FedRAMP: "CM", ISO: "A.8" },
  GH_ADMIN_BYPASS_ENABLED: { SOC2: "CC6.1", NIST: "AC", FedRAMP: "AC", ISO: "A.5" },
  GH_SECRET_SCANNING_DISABLED: { SOC2: "CC6.6", NIST: "SI", FedRAMP: "SI", ISO: "A.8" },
  GH_DEPENDABOT_DISABLED: { SOC2: "CC7.1", NIST: "RA", FedRAMP: "RA", ISO: "A.8" },
  CF_WAF_DISABLED: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_TLS_MIN_VERSION_WEAK: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_ALWAYS_USE_HTTPS_DISABLED: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_SECURITY_LEVEL_LOW: { SOC2: "CC7.1", NIST: "SI", FedRAMP: "SI", ISO: "A.8" },
  CF_DNSSEC_DISABLED: { SOC2: "CC6.6", NIST: "SC", FedRAMP: "SC", ISO: "A.8" },
  CF_ZONE_ACTIVITY_OBSERVED: { SOC2: "CC7.2", NIST: "AU", FedRAMP: "AU", ISO: "A.8" },
};

function isIdentityConnectorFinding(item = {}) {
  const source = normalizeText(item.source || item.provider || "");
  const stream = normalizeText(item.stream || "");
  const ruleId = String(item.rule_id || item.ruleId || "").toUpperCase();

  return (
    source === "google_workspace" ||
    source === "okta" ||
    stream.includes("google_workspace") ||
    stream.includes("okta") ||
    ruleId.startsWith("GW_") ||
    ruleId.startsWith("OKTA_")
  );
}

function getIdentitySourceLabel(item = {}) {
  const source = normalizeText(item.source || item.provider || "");
  const stream = normalizeText(item.stream || "");

  if (source === "google_workspace" || stream.includes("google_workspace")) {
    return "Google Workspace";
  }
  if (source === "okta" || stream.includes("okta")) {
    return "Okta";
  }
  return cleanValue(item.source || item.provider || item.stream, "Identity");
}

function getIdentityRuleLabel(ruleId) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  if (IDENTITY_FINDING_RULE_LABELS[normalizedRule]) {
    return IDENTITY_FINDING_RULE_LABELS[normalizedRule];
  }

  return normalizedRule
    ? normalizedRule
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Identity finding";
}

function getIdentityRiskBucket(item = {}) {
  const ruleId = String(item.rule_id || item.ruleId || "").toUpperCase();
  const resourceType = normalizeText(item.resource_type || item.resourceType || "");

  if (
    ruleId.includes("MFA") ||
    ruleId.includes("2SV") ||
    ruleId.includes("LOGIN") ||
    ruleId.includes("SYSTEM_LOG")
  ) {
    return "mfa";
  }

  if (
    ruleId.includes("ADMIN") ||
    ruleId.includes("PRIVILEGED") ||
    resourceType.includes("admin") ||
    resourceType.includes("privileged")
  ) {
    return "admin";
  }

  if (
    ruleId.includes("UNUSED") ||
    ruleId.includes("STALE") ||
    ruleId.includes("INACTIVE") ||
    resourceType.includes("user")
  ) {
    return "lifecycle";
  }

  return "identity";
}

function getIdentityControlIdForFramework(ruleId, frameworkValue) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  const mapping = IDENTITY_RULE_CONTROL_BY_FRAMEWORK[normalizedRule] || {};
  const exactFramework = cleanValue(frameworkValue, "");
  return mapping[exactFramework] || mapping[exactFramework.toUpperCase()] || mapping.SOC2 || "";
}

function buildIdentityFindingPostureItem(finding = {}, frameworkValue = "SOC2") {
  const ruleId = cleanValue(finding.rule_id || finding.ruleId, "");
  const state = cleanValue(finding.state || finding.status, "unknown").toLowerCase();
  const resourceId = cleanValue(
    finding.resource_id || finding.resourceId || finding.target_email || finding.actor_email,
    ""
  );
  const sourceLabel = getIdentitySourceLabel(finding);
  const ruleLabel = getIdentityRuleLabel(ruleId);
  const controlId = getIdentityControlIdForFramework(ruleId, frameworkValue);
  const isFail = ["fail", "failed", "error"].includes(state);

  return {
    ...finding,
    framework: frameworkValue,
    control_id: controlId,
    rule_id: ruleId,
    resource_id: resourceId,
    source: finding.source || finding.provider || normalizeText(sourceLabel).replace(/\s+/g, "_"),
    stream: finding.stream || "",
    state,
    severity_weight: isFail ? 3 : 1,
    score: isFail ? 100 : 0,
    reason_summary:
      finding.reason_summary ||
      `${ruleLabel} is ${getDisplayStateLabel(state)}${resourceId ? ` for ${resourceId}` : ""}.`,
    remediation_hint:
      finding.remediation_hint ||
      (isFail
        ? `Review ${sourceLabel} identity settings and remediate ${ruleLabel.toLowerCase()}.`
        : `${sourceLabel} identity evidence is currently passing for this rule.`),
  };
}


function isCodeEdgeConnectorFinding(item = {}) {
  const source = normalizeText(item.source || item.provider || "");
  const stream = normalizeText(item.stream || "");
  const ruleId = String(item.rule_id || item.ruleId || "").toUpperCase();

  return (
    source === "github" ||
    source === "cloudflare" ||
    stream.includes("github") ||
    stream.includes("cloudflare") ||
    ruleId.startsWith("GH_") ||
    ruleId.startsWith("CF_")
  );
}

function getCodeEdgeSourceLabel(item = {}) {
  const source = normalizeText(item.source || item.provider || "");
  const stream = normalizeText(item.stream || "");
  const ruleId = String(item.rule_id || item.ruleId || "").toUpperCase();

  if (source === "github" || stream.includes("github") || ruleId.startsWith("GH_")) {
    return "GitHub";
  }
  if (source === "cloudflare" || stream.includes("cloudflare") || ruleId.startsWith("CF_")) {
    return "Cloudflare";
  }
  return cleanValue(item.source || item.provider || item.stream, "Code & Edge");
}

function getCodeEdgeRuleLabel(ruleId) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  if (CODE_EDGE_FINDING_RULE_LABELS[normalizedRule]) {
    return CODE_EDGE_FINDING_RULE_LABELS[normalizedRule];
  }

  return normalizedRule
    ? normalizedRule
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Code / edge finding";
}


function getPostureExplainRuleLabel(ruleId) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  if (!normalizedRule) return "";

  if (CODE_EDGE_FINDING_RULE_LABELS[normalizedRule]) {
    return CODE_EDGE_FINDING_RULE_LABELS[normalizedRule];
  }

  if (IDENTITY_FINDING_RULE_LABELS[normalizedRule]) {
    return IDENTITY_FINDING_RULE_LABELS[normalizedRule];
  }

  return normalizedRule
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => {
      if (part === "iam") return "IAM";
      if (part === "mfa") return "MFA";
      if (part === "s3") return "S3";
      if (part === "tls") return "TLS";
      if (part === "https") return "HTTPS";
      if (part === "waf") return "WAF";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
}

function buildPostureExplainLabel(target) {
  if (!target) return "Selected posture item";

  const controlId = cleanValue(target.control_id || target.controlId, "");
  const ruleId = cleanValue(target.rule_id || target.ruleId, "");
  const resourceId = cleanValue(target.resource_id || target.resourceId, "");
  const ruleLabel = getPostureExplainRuleLabel(ruleId);

  return [controlId, ruleLabel, resourceId].filter(Boolean).join(" · ") || "Selected posture item";
}

function getCodeEdgeControlIdForFramework(ruleId, frameworkValue) {
  const normalizedRule = String(ruleId || "").toUpperCase();
  const mapping = CODE_EDGE_RULE_CONTROL_BY_FRAMEWORK[normalizedRule] || {};
  const exactFramework = cleanValue(frameworkValue, "");
  return mapping[exactFramework] || mapping[exactFramework.toUpperCase()] || mapping.SOC2 || "";
}

function getCodeEdgeRiskBucket(item = {}) {
  const ruleId = String(item.rule_id || item.ruleId || "").toUpperCase();
  const source = normalizeText(item.source || item.provider || item.stream || "");

  if (source.includes("github") || ruleId.startsWith("GH_")) return "code";
  if (source.includes("cloudflare") || ruleId.startsWith("CF_")) return "edge";
  return "code_edge";
}

function buildCodeEdgeFindingPostureItem(finding = {}, frameworkValue = "SOC2") {
  const ruleId = cleanValue(finding.rule_id || finding.ruleId, "");
  const state = cleanValue(finding.state || finding.status, "unknown").toLowerCase();
  const resourceId = cleanValue(
    finding.resource_id || finding.resourceId || finding.repository || finding.zone_name,
    ""
  );
  const sourceLabel = getCodeEdgeSourceLabel(finding);
  const ruleLabel = getCodeEdgeRuleLabel(ruleId);
  const controlId = getCodeEdgeControlIdForFramework(ruleId, frameworkValue);
  const isFail = ["fail", "failed", "error"].includes(state);
  const isUnknown = state === "unknown";
  const metadata = finding.metadata || finding.details || {};
  const riskReason = cleanValue(metadata.risk_reason || finding.risk_reason, "");
  const controlEvidence = cleanValue(metadata.control_evidence || finding.control_evidence, "");

  return {
    ...finding,
    framework: frameworkValue,
    control_id: controlId,
    rule_id: ruleId,
    resource_id: resourceId,
    source: finding.source || finding.provider || normalizeText(sourceLabel).replace(/\s+/g, "_"),
    stream: finding.stream || "",
    state,
    severity_weight: isFail ? 3 : isUnknown ? 2 : 1,
    score: isFail ? 100 : isUnknown ? 25 : 0,
    reason_summary:
      finding.reason_summary ||
      riskReason ||
      controlEvidence ||
      `${ruleLabel} is ${getDisplayStateLabel(state)}${resourceId ? ` for ${resourceId}` : ""}.`,
    remediation_hint:
      finding.remediation_hint ||
      (isFail
        ? `Review ${sourceLabel} settings and remediate ${ruleLabel.toLowerCase()}.`
        : isUnknown
        ? `Review ${sourceLabel} connector permissions or source settings to resolve the unknown evidence state.`
        : `${sourceLabel} evidence is currently passing for this rule.`),
  };
}

function getRiskQueueFamily(item = {}) {
  const rule = String(item.rule_id || item.ruleId || "").toUpperCase();
  const source = normalizeText(item.source || item.stream || item.source_table || item.sourceTable || "");
  const resourceType = normalizeText(item.resource_type || item.resourceType || "");

  if (isIdentityConnectorFinding(item)) return "identity";
  if (isCodeEdgeConnectorFinding(item)) return getCodeEdgeRiskBucket(item);
  if (rule.includes("CLOUDTRAIL") || source.includes("cloudtrail")) return "cloudtrail";
  if (rule.includes("CONFIG_") || source.includes("aws_config") || source.includes("config_resource")) return "config";
  if (rule.includes("S3_") || rule.includes("CONFIG_S3") || resourceType.includes("s3")) return "s3";
  if (
    rule.includes("IAM") ||
    rule.includes("ROLE") ||
    rule.includes("ACCESS_KEY") ||
    rule.includes("PASSWORD") ||
    rule.includes("USER") ||
    rule.includes("GROUP") ||
    source.includes("iam") ||
    resourceType.includes("iam")
  ) {
    return "iam";
  }

  return "other";
}

function isHighSeverityRisk(item = {}) {
  const severityText = normalizeText(item.severity || item.severity_label || item.severityLabel || "");
  const severityWeight = safeNumber(item.severity_weight || item.severityWeight || item.severity);

  return (
    severityWeight >= 3 ||
    ["high", "critical", "severe"].includes(severityText) ||
    severityText.includes("high") ||
    severityText.includes("critical")
  );
}

function riskMatchesQueueFilter(item = {}, filterKey = "all", context = {}) {
  if (!filterKey || filterKey === "all") return true;

  const family = getRiskQueueFamily(item);
  const linkedTask = context.linkedTask || null;
  const hasLinkedTask = Boolean(linkedTask) || context.hasTask === true;
  const taskStatus = normalizeText(linkedTask?.status || linkedTask?.task_status || "");
  const taskValidationStatus = normalizeText(
    linkedTask?.validation_status || linkedTask?.validationStatus || ""
  );

  if (["s3", "iam", "identity", "code", "edge", "cloudtrail", "config"].includes(filterKey)) {
    return family === filterKey;
  }

  if (filterKey === "high") return isHighSeverityRisk(item);
  if (filterKey === "has_task") return hasLinkedTask;
  if (filterKey === "no_task") return !hasLinkedTask;
  if (filterKey === "validation_failed") {
    return hasLinkedTask && taskValidationStatus === "failed";
  }
  if (filterKey === "in_progress") {
    return hasLinkedTask && taskStatus === "in_progress";
  }
  if (filterKey === "ready") {
    return hasLinkedTask && taskStatus === "ready_for_validation";
  }
  if (filterKey === "blocked") {
    return hasLinkedTask && taskStatus === "blocked";
  }
  if (filterKey === "validated") {
    return (
      hasLinkedTask &&
      (taskStatus === "validated" || taskValidationStatus === "passed")
    );
  }

  return true;
}

function getRulePlanCoverageKey(item = {}, diagnostic = {}) {
  const frameworkValue = cleanValue(item.framework || diagnostic.framework, "");
  const controlId = cleanValue(item.control_id || item.controlId, "");
  const ruleId = cleanValue(item.rule_id || item.ruleId || diagnostic.rule_id, "");
  const resourceId = cleanValue(item.resource_id || item.resourceId, "");

  return [frameworkValue, controlId, ruleId, resourceId].filter(Boolean).join("::");
}

function buildRulePlanCoverageSummary(items = []) {
  const byKey = new Map();

  items.forEach((item = {}) => {
    const diagnostic = getRulePlanDiagnostic(item);
    const ruleId = cleanValue(item.rule_id || item.ruleId || diagnostic.rule_id, "");
    const controlId = cleanValue(item.control_id || item.controlId, "");
    const key = getRulePlanCoverageKey(item, diagnostic);

    if (!key || (!ruleId && !controlId)) return;

    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        item,
        diagnostic,
        framework: cleanValue(item.framework, ""),
        control_id: controlId,
        rule_id: ruleId,
        resource_id: cleanValue(item.resource_id || item.resourceId, ""),
      });
    }
  });

  const rows = Array.from(byKey.values());
  const counts = RULE_PLAN_FAMILY_ORDER.reduce((acc, family) => {
    acc[family] = 0;
    return acc;
  }, {});

  rows.forEach((row) => {
    const family = row?.diagnostic?.family || "generic";
    counts[family] = (counts[family] || 0) + 1;
  });

  return {
    total: rows.length,
    counts,
    rows,
    standardRows: rows.filter((row) => row?.diagnostic?.family === "generic"),
  };
}

function clampPercentScore(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
}

function estimateOperationalScore({ rawScore = 0, rawFailCount = 0, errorCount = 0, unknownCount = 0, acceptedRiskFindings = 0 } = {}) {
  const score = clampPercentScore(rawScore);
  const accepted = Math.max(0, safeNumber(acceptedRiskFindings, 0));
  const burden = Math.max(0, safeNumber(rawFailCount, 0) + safeNumber(errorCount, 0) + safeNumber(unknownCount, 0));

  if (accepted <= 0 || burden <= 0 || score >= 100) return score;

  const acceptedShare = Math.min(1, accepted / burden);
  const remainingGap = Math.max(0, 100 - score);
  return clampPercentScore(score + remainingGap * acceptedShare);
}

function coalesceNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function formatSignedNumber(value, digits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric > 0 ? `+${numeric.toFixed(digits)}` : numeric.toFixed(digits);
}



export default function Compliance({
  apiBaseUrl,
  getAccessToken,
  tenantId: tenantIdProp = "",
  userId: userIdProp = "",
  routeParams = {},
  setMode,
  themeMode = "dark",
}) {
  const { getAccessTokenSilently, user } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();
  const resolvedThemeMode =
    String(themeMode || "").trim().toLowerCase() === "light" ? "light" : "dark";
  const dashboardThemeClass = resolvedThemeMode === "dark" ? " dark" : "";

  const initialFramework = FRAMEWORKS.includes(routeParams.framework)
    ? routeParams.framework
    : "SOC2";

  const [framework, setFramework] = useState(initialFramework);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [panelWarnings, setPanelWarnings] = useState([]);

  const [overview, setOverview] = useState(null);
  const [summaryFrameworks, setSummaryFrameworks] = useState([]);
  const [topRisks, setTopRisks] = useState([]);
  const [riskQueue, setRiskQueue] = useState([]);
  const [identityFindings, setIdentityFindings] = useState([]);
  const [codeEdgeFindings, setCodeEdgeFindings] = useState([]);
  const [staleControls, setStaleControls] = useState([]);
  const [controlStatusRows, setControlStatusRows] = useState([]);
  const [postureDelta, setPostureDelta] = useState([]);
  const [remediationTasks, setRemediationTasks] = useState([]);
  const [showClosedTasks, setShowClosedTasks] = useState(false);
  const [showRulePlanDiagnostics, setShowRulePlanDiagnostics] = useState(false);
  const [showRemainingRisksModal, setShowRemainingRisksModal] = useState(false);
  const [showAllActiveTasksModal, setShowAllActiveTasksModal] = useState(false);
  const [allActiveModalTasks, setAllActiveModalTasks] = useState([]);
  const [allActiveModalLoading, setAllActiveModalLoading] = useState(false);
  const [allActiveModalError, setAllActiveModalError] = useState("");
  const [allActiveModalTotal, setAllActiveModalTotal] = useState(0);
  const [showQueueStatusTasksModal, setShowQueueStatusTasksModal] = useState(false);
  const [queueStatusModalType, setQueueStatusModalType] = useState("open");
  const [queueStatusModalTasks, setQueueStatusModalTasks] = useState([]);
  const [queueStatusModalLoading, setQueueStatusModalLoading] = useState(false);
  const [queueStatusModalError, setQueueStatusModalError] = useState("");
  const [queueStatusModalTotal, setQueueStatusModalTotal] = useState(0);
  const [showActiveFindingsModal, setShowActiveFindingsModal] = useState(false);
  const [showApprovedExceptionsModal, setShowApprovedExceptionsModal] = useState(false);
  const [showValidationFailedModal, setShowValidationFailedModal] = useState(false);
  const [showOverdueTasksModal, setShowOverdueTasksModal] = useState(false);
  const [overdueModalTasks, setOverdueModalTasks] = useState([]);
  const [overdueModalLoading, setOverdueModalLoading] = useState(false);
  const [overdueModalError, setOverdueModalError] = useState("");
  const [overdueModalTotal, setOverdueModalTotal] = useState(0);
  const [showInProgressTasksModal, setShowInProgressTasksModal] = useState(false);
  const [inProgressModalTasks, setInProgressModalTasks] = useState([]);
  const [inProgressModalLoading, setInProgressModalLoading] = useState(false);
  const [inProgressModalError, setInProgressModalError] = useState("");
  const [inProgressModalTotal, setInProgressModalTotal] = useState(0);
  const [showClosedTasksModal, setShowClosedTasksModal] = useState(false);
  const [closedModalTasks, setClosedModalTasks] = useState([]);
  const [closedModalLoading, setClosedModalLoading] = useState(false);
  const [closedModalError, setClosedModalError] = useState("");
  const [closedModalTotal, setClosedModalTotal] = useState(0);
  const [showPostureStatusModal, setShowPostureStatusModal] = useState(false);
  const [postureStatusModalType, setPostureStatusModalType] = useState("pass");
  const [postureStatusModalRows, setPostureStatusModalRows] = useState([]);
  const [postureStatusModalLoading, setPostureStatusModalLoading] = useState(false);
  const [postureStatusModalError, setPostureStatusModalError] = useState("");
  const [postureStatusModalTotal, setPostureStatusModalTotal] = useState(0);
  const [postureStatusModalHasMore, setPostureStatusModalHasMore] = useState(false);
  const [postureStatusModalOffset, setPostureStatusModalOffset] = useState(0);
  const [showStaleControlsModal, setShowStaleControlsModal] = useState(false);
  const [riskQueueFilter, setRiskQueueFilter] = useState("all");
  const [selectedRemainingRiskKeys, setSelectedRemainingRiskKeys] = useState(() => new Set());
  const [remainingRiskBulkCreating, setRemainingRiskBulkCreating] = useState(false);
  const [remainingRiskBulkNotice, setRemainingRiskBulkNotice] = useState("");
  const [remainingRiskBulkError, setRemainingRiskBulkError] = useState("");
  const [remediationTaskLoadError, setRemediationTaskLoadError] = useState("");
  const [relatedSopRemediation, setRelatedSopRemediation] = useState(null);
  const [relatedSopLoading, setRelatedSopLoading] = useState(false);
  const [relatedSopError, setRelatedSopError] = useState("");
  const [selectedGuidanceTarget, setSelectedGuidanceTarget] = useState(null);
  const [postureExplainTarget, setPostureExplainTarget] = useState(null);
  const [postureExplainOpen, setPostureExplainOpen] = useState(false);
  const [postureExplainQuestion, setPostureExplainQuestion] = useState(POSTURE_EXPLAIN_PROMPTS[0]);
  const [postureExplainCard, setPostureExplainCard] = useState(null);
  const [postureExplainLoading, setPostureExplainLoading] = useState(false);
  const [postureExplainError, setPostureExplainError] = useState("");
  const [guidanceModalItem, setGuidanceModalItem] = useState(null);
  const [guidanceReturnTarget, setGuidanceReturnTarget] = useState(null);
  const [assessmentRefreshing, setAssessmentRefreshing] = useState(false);
  const [assessmentRefreshNotice, setAssessmentRefreshNotice] = useState("");
  const [assessmentRefreshError, setAssessmentRefreshError] = useState("");
  const [assessmentRefreshTick, setAssessmentRefreshTick] = useState(0);
  const relatedSopCardRef = useRef(null);
  const investigationSummaryRef = useRef(null);
  const topRisksCardRef = useRef(null);
  const staleControlsCardRef = useRef(null);
  const controlStatusCardRef = useRef(null);
  const remediationOperationsCardRef = useRef(null);
  const routeNavigationHandledRef = useRef("");
  const routeTaskOpenHandledRef = useRef("");

  const [fullAssessmentRunning, setFullAssessmentRunning] = useState(false);
  const [fullAssessmentStatus, setFullAssessmentStatus] = useState("");
  const [fullAssessmentError, setFullAssessmentError] = useState("");
  const [validationRunModal, setValidationRunModal] = useState(null);
  const [lastSyncJobId, setLastSyncJobId] = useState("");
  const [fixPlanItem, setFixPlanItem] = useState(null);
  const [fixPlanReturnTarget, setFixPlanReturnTarget] = useState(null);
  const [taskModalReturnTarget, setTaskModalReturnTarget] = useState(null);
  const [remediationTaskCreating, setRemediationTaskCreating] = useState(false);
  const [remediationTaskNotice, setRemediationTaskNotice] = useState("");
  const [remediationTaskError, setRemediationTaskError] = useState("");
  const [createdRemediationTask, setCreatedRemediationTask] = useState(null);
  const [remediationTaskUpdating, setRemediationTaskUpdating] = useState(false);
  const [managedTask, setManagedTask] = useState(null);
  const [managedTaskDraft, setManagedTaskDraft] = useState(null);
  const [managedTaskInvestigationDraft, setManagedTaskInvestigationDraft] = useState(null);
  const [managedTaskInvestigationSaving, setManagedTaskInvestigationSaving] = useState(false);
  const [managedTaskEvents, setManagedTaskEvents] = useState([]);
  const [managedTaskEventsLoading, setManagedTaskEventsLoading] = useState(false);
  const [managedTaskEventsError, setManagedTaskEventsError] = useState("");
  const [managedTaskValidationHistory, setManagedTaskValidationHistory] = useState([]);
  const [managedTaskValidationHistoryLoading, setManagedTaskValidationHistoryLoading] = useState(false);
  const [managedTaskValidationHistoryError, setManagedTaskValidationHistoryError] = useState("");
  const [managedTaskEvidence, setManagedTaskEvidence] = useState([]);
  const [managedTaskEvidenceLoading, setManagedTaskEvidenceLoading] = useState(false);
  const [managedTaskEvidenceError, setManagedTaskEvidenceError] = useState("");
  const [managedTaskEvidenceCreating, setManagedTaskEvidenceCreating] = useState(false);
  const [managedTaskEvidenceCreateError, setManagedTaskEvidenceCreateError] = useState("");
  const [managedTaskWorkflowRecovering, setManagedTaskWorkflowRecovering] = useState(false);
  const [managedTaskWorkflowRecoveryError, setManagedTaskWorkflowRecoveryError] = useState("");
  const [managedTaskClosureGuard, setManagedTaskClosureGuard] = useState(null);
  const [managedTaskClosureGuardLoading, setManagedTaskClosureGuardLoading] = useState(false);
  const [managedTaskClosureGuardError, setManagedTaskClosureGuardError] = useState("");
  const [managedTaskValidationActionRunning, setManagedTaskValidationActionRunning] = useState(false);
  const [managedTaskValidationActionError, setManagedTaskValidationActionError] = useState("");
  const [taskNextStepModal, setTaskNextStepModal] = useState(null);
  const [showScoreDetailsModal, setShowScoreDetailsModal] = useState(false);
  const [investigationSummaryHighlighted, setInvestigationSummaryHighlighted] = useState(false);
  const remediationTasksRef = useRef([]);

  useEffect(() => {
    remediationTasksRef.current = remediationTasks;
  }, [remediationTasks]);

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId = String(tenantIdProp || "").trim();
  const userId = String(userIdProp || "").trim();
  const userName = useMemo(() => {
    return (
      user?.name ||
      user?.nickname ||
      user?.email ||
      (process.env.REACT_APP_USER_NAME || "").trim()
    );
  }, [user]);

  const routeFrameworkParam = routeParams.framework;
  const routeControlIdParam = routeParams.controlId || routeParams.control_id;
  const routeRuleIdParam = routeParams.ruleId || routeParams.rule_id;
  const routeResourceIdParam = routeParams.resourceId || routeParams.resource_id;
  const routeSectionParam = routeParams.section || routeParams.targetSection || routeParams.target_section;
  const routeRemediationQueueParam =
    routeParams.remediationQueue ||
    routeParams.remediation_queue ||
    routeParams.operationsQueue ||
    routeParams.operations_queue ||
    routeParams.queue;
  const routeNavNonceParam =
    routeParams.routeNavNonce || routeParams.navNonce || routeParams.nav_nonce || "";
  const routeReturnModeParam =
    routeParams.returnMode || routeParams.return_mode || "";
  const routeReturnToParam =
    routeParams.returnTo || routeParams.return_to || "";
  const routeReturnSectionParam =
    routeParams.returnSection || routeParams.return_section || "";
  const routeReturnWindowParam =
    routeParams.window || routeParams.returnWindow || routeParams.return_window || "";
  const routeSourceParam =
    routeParams.source || routeParams.routeSource || routeParams.route_source || "";
  const routeSourceModeParam =
    routeParams.sourceMode || routeParams.source_mode || "";
  const routeWorkflowIdParam =
    routeParams.workflowId || routeParams.workflow_id || "";
  const routeOpenTaskIdParam =
    routeParams.remediationTaskId ||
    routeParams.remediation_task_id ||
    routeParams.openTaskId ||
    routeParams.open_task_id ||
    routeParams.taskId ||
    routeParams.task_id ||
    "";

  const routeReturnContext = useMemo(() => {
    const returnMode = normalizeRouteParamValue(routeReturnModeParam).toLowerCase();
    const returnTo = normalizeRouteParamValue(routeReturnToParam).toLowerCase();
    const source = normalizeRouteParamValue(routeSourceParam).toLowerCase();
    const sourceMode = normalizeRouteParamValue(routeSourceModeParam).toLowerCase();
    const returnSection = normalizeRouteParamValue(routeReturnSectionParam).toLowerCase();

    const shouldReturnToMissionControl =
      returnMode === "dashboard" ||
      returnTo === "mission_control" ||
      source === "mission_control" ||
      sourceMode === "dashboard";

    const shouldReturnToExecutiveIntelligence =
      returnMode === "executive" ||
      returnTo === "executive_intelligence" ||
      source === "executive_intelligence" ||
      sourceMode === "executive";

    const shouldReturnToWorkflowCenter =
      returnMode === "workflows" ||
      returnTo === "workflows" ||
      returnTo === "workflow_center" ||
      source === "workflow_center" ||
      sourceMode === "workflows";

    return {
      returnSection,
      shouldReturnToMissionControl,
      shouldReturnToExecutiveIntelligence,
      shouldReturnToWorkflowCenter,
      shouldReturnToExternalSource:
        shouldReturnToMissionControl || shouldReturnToExecutiveIntelligence || shouldReturnToWorkflowCenter,
    };
  }, [
    routeReturnModeParam,
    routeReturnSectionParam,
    routeReturnToParam,
    routeSourceModeParam,
    routeSourceParam,
  ]);

  const shouldReturnToMissionControl = routeReturnContext.shouldReturnToMissionControl;
  const shouldReturnToExecutiveIntelligence =
    routeReturnContext.shouldReturnToExecutiveIntelligence;
  const shouldReturnToExternalSource = routeReturnContext.shouldReturnToExternalSource;
  const shouldReturnToWorkflowCenter = routeReturnContext.shouldReturnToWorkflowCenter;
  const executiveReturnSection = routeReturnContext.returnSection;

  const resetRouteReturnScroll = useCallback(() => {
    if (typeof window === "undefined") return;

    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }

    const scrollTargets = [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.querySelector("#root"),
      document.querySelector(".App"),
      document.querySelector("main"),
    ];

    scrollTargets.forEach((target) => {
      if (!target) return;
      try {
        target.scrollTop = 0;
        target.scrollLeft = 0;
      } catch {
        // Ignore non-scrollable nodes.
      }
    });
  }, []);

  const scheduleRouteReturnScroll = useCallback(() => {
    resetRouteReturnScroll();

    if (typeof window === "undefined") return;

    window.requestAnimationFrame(() => {
      resetRouteReturnScroll();
      window.requestAnimationFrame(resetRouteReturnScroll);
    });

    window.setTimeout(resetRouteReturnScroll, 0);
    window.setTimeout(resetRouteReturnScroll, 80);
    window.setTimeout(resetRouteReturnScroll, 180);
  }, [resetRouteReturnScroll]);

  const returnToMissionControl = useCallback(() => {
    if (!shouldReturnToMissionControl || typeof setMode !== "function") return;

    scheduleRouteReturnScroll();

    setMode("dashboard", {
      returnedFrom: "posture",
      returnFrom: "posture",
      source: "posture",
      routeNavNonce: String(Date.now()),
      scrollTop: "1",
    });

    scheduleRouteReturnScroll();
  }, [scheduleRouteReturnScroll, setMode, shouldReturnToMissionControl]);

  const returnToExecutiveIntelligence = useCallback(() => {
    if (!shouldReturnToExecutiveIntelligence || typeof setMode !== "function") return;

    const returnsToBriefing = executiveReturnSection === "leadership_action_briefing";

    if (!returnsToBriefing) {
      scheduleRouteReturnScroll();
    }

    setMode("executive", {
      returnedFrom: "posture",
      returnFrom: "posture",
      source: "posture",
      framework,
      window: routeReturnWindowParam || undefined,
      returnSection: returnsToBriefing ? executiveReturnSection : undefined,
      routeNavNonce: String(Date.now()),
      scrollTop: returnsToBriefing ? undefined : "1",
    });

    if (!returnsToBriefing) {
      scheduleRouteReturnScroll();
    }
  }, [
    executiveReturnSection,
    framework,
    routeReturnWindowParam,
    scheduleRouteReturnScroll,
    setMode,
    shouldReturnToExecutiveIntelligence,
  ]);

  const returnToWorkflowCenter = useCallback(() => {
    if (!shouldReturnToWorkflowCenter || typeof setMode !== "function") return;

    const workflowId = normalizeRouteParamValue(routeWorkflowIdParam);

    scheduleRouteReturnScroll();

    setMode("workflows", {
      workflowId: workflowId || undefined,
      workflow_id: workflowId || undefined,
      returnedFrom: "posture",
      returnFrom: "posture",
      source: "posture",
      framework,
      routeNavNonce: String(Date.now()),
      scrollTop: "1",
    });

    scheduleRouteReturnScroll();
  }, [
    framework,
    routeWorkflowIdParam,
    scheduleRouteReturnScroll,
    setMode,
    shouldReturnToWorkflowCenter,
  ]);

  const closeRouteModal = useCallback(
    (closeFn) => {
      if (typeof closeFn === "function") {
        closeFn();
      }

      if (shouldReturnToMissionControl) {
        window.setTimeout(returnToMissionControl, 0);
        return;
      }

      if (shouldReturnToExecutiveIntelligence) {
        window.setTimeout(returnToExecutiveIntelligence, 0);
        return;
      }

      if (shouldReturnToWorkflowCenter) {
        window.setTimeout(returnToWorkflowCenter, 0);
      }
    },
    [
      returnToExecutiveIntelligence,
      returnToMissionControl,
      returnToWorkflowCenter,
      shouldReturnToExecutiveIntelligence,
      shouldReturnToMissionControl,
      shouldReturnToWorkflowCenter,
    ]
  );

  const routeSelectionContext = useMemo(
    () => ({
      framework: normalizeRouteParamValue(routeFrameworkParam),
      controlId: normalizeRouteParamValue(routeControlIdParam),
      ruleId: normalizeRouteParamValue(routeRuleIdParam),
      resourceId: normalizeRouteParamValue(routeResourceIdParam),
    }),
    [routeFrameworkParam, routeControlIdParam, routeRuleIdParam, routeResourceIdParam]
  );

  useEffect(() => {
    if (FRAMEWORKS.includes(routeParams.framework) && routeParams.framework !== framework) {
      setFramework(routeParams.framework);
    }
  }, [routeParams.framework, framework]);

  useEffect(() => {
    const nextParams = {
      ...routeParams,
      framework,
      controlId: routeSelectionContext.controlId || undefined,
      ruleId: routeSelectionContext.ruleId || undefined,
      resourceId: routeSelectionContext.resourceId || undefined,
    };
    setMode("posture", nextParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    framework,
    routeSelectionContext.controlId,
    routeSelectionContext.ruleId,
    routeSelectionContext.resourceId,
  ]);

  useEffect(() => {
    if (!assessmentRefreshNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAssessmentRefreshNotice("");
    }, SUCCESS_NOTICE_AUTO_DISMISS_MS);

    return () => window.clearTimeout(timeoutId);
  }, [assessmentRefreshNotice]);

  useEffect(() => {
    if (!remediationTaskNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setRemediationTaskNotice("");
    }, SUCCESS_NOTICE_AUTO_DISMISS_MS);

    return () => window.clearTimeout(timeoutId);
  }, [remediationTaskNotice]);

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") return await getAccessToken();
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let parsed = null;
        try { parsed = JSON.parse(text); } catch (_) { /* plain-text error */ }
        throw new Error(`${res.status}: ${readableApiError(parsed?.detail || parsed?.message || text)}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  // Render FastAPI/Pydantic structured errors as readable text.
  const readableApiError = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((entry) => {
      const path = Array.isArray(entry?.loc) ? entry.loc.join(".") : "request";
      return `${path}: ${entry?.msg || JSON.stringify(entry)}`;
    }).join("; ");
    if (detail && typeof detail === "object") return detail.message || detail.msg || JSON.stringify(detail);
    return String(detail || "Unknown API error");
  };

  const postJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userName) headers["X-User-Name"] = userName;

      const res = await fetch(`${normalizedBase}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body || {}),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      const responseText = await res.text().catch(() => "");
      return responseText ? JSON.parse(responseText) : {};
    },
    [normalizedBase, tenantId, userId, userName]
  );

  const requestPostureIntelligence = useCallback(
    async (path, options = {}) => {
      const token = await fetchToken();
      const method = String(options?.method || "POST").toUpperCase();

      if (method !== "POST") {
        throw new Error("Posture Intelligence only supports grounded POST requests.");
      }

      return postJsonWithToken(
        token,
        path,
        options?.body || {},
        options?.signal
      );
    },
    [fetchToken, postJsonWithToken]
  );

  const askPostureExplain = useCallback(
    async (question, targetOverride = null) => {
      const target = targetOverride || postureExplainTarget;
      if (!target?.control_id) return;

      const nextQuestion = String(question || POSTURE_EXPLAIN_PROMPTS[0]).trim();
      setPostureExplainQuestion(nextQuestion);
      setPostureExplainLoading(true);
      setPostureExplainError("");
      setPostureExplainCard(null);

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("posture-intelligence-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            scope: "posture",
            question: nextQuestion,
            copilot_context: {
              page: "posture",
              surface: "posture_page",
              intelligence_mode: "contextual_explain",
              record_type: "posture_control",
              framework: target.framework || framework,
              control_id: target.control_id,
              rule_id: target.rule_id || null,
              resource_id: target.resource_id || null,
            },
          },
          controller.signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error("Posture explanation did not return an intelligence card.");
        }

        setPostureExplainCard(card);
      } catch (error) {
        console.error("Contextual Posture Explain failed", error);
        setPostureExplainError(
          error?.name === "AbortError" ||
          String(error?.message || "").includes("posture-intelligence-timeout")
            ? "Explanation unavailable: posture-intelligence-timeout"
            : error?.message || "Unable to explain this posture item right now."
        );
      } finally {
        window.clearTimeout(timeoutId);
        setPostureExplainLoading(false);
      }
    },
    [fetchToken, framework, postJsonWithToken, postureExplainTarget]
  );

  const openPostureExplain = useCallback(
    (item = {}, sourceKind = "posture_control") => {
      const target = {
        ...item,
        framework: item.framework || framework,
        control_id: item.control_id || item.controlId || "",
        rule_id: item.rule_id || item.ruleId || "",
        resource_id: item.resource_id || item.resourceId || "",
        freshness_state:
          item.freshness_state || item.freshness_status || "",
        source_kind: sourceKind,
      };

      if (!target.control_id) return;

      setPostureExplainTarget(target);
      setPostureExplainOpen(true);
      setPostureExplainQuestion(POSTURE_EXPLAIN_PROMPTS[0]);
      setPostureExplainCard(null);
      setPostureExplainError("");
      askPostureExplain(POSTURE_EXPLAIN_PROMPTS[0], target);
    },
    [askPostureExplain, framework]
  );

  const closePostureExplain = useCallback(() => {
    setPostureExplainOpen(false);
    setPostureExplainLoading(false);
    setPostureExplainError("");
  }, []);

  const patchJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userName) headers["X-User-Name"] = userName;

      const res = await fetch(`${normalizedBase}${path}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body || {}),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      const responseText = await res.text().catch(() => "");
      return responseText ? JSON.parse(responseText) : {};
    },
    [normalizedBase, tenantId, userId, userName]
  );

  const scrollToCard = useCallback((ref, offset = 96) => {
    const card = ref?.current;
    if (!card) return;

    const top = card.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (loading) return undefined;

    const queueKey = normalizeRouteQueue(routeRemediationQueueParam);
    const sectionKey = normalizeRouteQueue(routeSectionParam);

    if (!queueKey && !sectionKey) return undefined;

    const navigationKey = [framework, queueKey, sectionKey, routeNavNonceParam].filter(Boolean).join("::");
    if (routeNavigationHandledRef.current === navigationKey) return undefined;
    routeNavigationHandledRef.current = navigationKey;

    const timers = [];
    const schedule = (callback, delay) => {
      const timerId = window.setTimeout(callback, delay);
      timers.push(timerId);
    };

    if (queueKey) {
      if (!shouldReturnToExternalSource) {
        scrollToCard(remediationOperationsCardRef, 96);
      }

      schedule(() => {
        const labels = REMEDIATION_QUEUE_ROUTE_LABELS[queueKey] || [queueKey.replace(/_/g, " ")];
        const root = remediationOperationsCardRef.current;
        const buttons = Array.from(root?.querySelectorAll("button") || []);
        const targetButton = buttons.find((button) => {
          const text = String(button?.textContent || "").trim().toLowerCase();
          return labels.some((label) => text.includes(label));
        });

        if (targetButton) {
          targetButton.click();
        }
      }, shouldReturnToExternalSource ? 0 : 350);

      return () => {
        timers.forEach((timerId) => window.clearTimeout(timerId));
      };
    }

    const scrollWithRetry = (targetRef, offset = 96) => {
      scrollToCard(targetRef, offset);
      schedule(() => scrollToCard(targetRef, offset), 200);
      schedule(() => scrollToCard(targetRef, offset), 650);
    };

    if (["stale", "stale_controls", "freshness"].includes(sectionKey)) {
      setShowStaleControlsModal(true);
    } else if (sectionKey === "risk_queue") {
      setRiskQueueFilter("all");
      setShowRemainingRisksModal(true);
      if (!shouldReturnToExternalSource) {
        scrollWithRetry(topRisksCardRef, 96);
      }
    } else if (["active_findings", "findings"].includes(sectionKey)) {
      setShowActiveFindingsModal(true);
    } else if (["accepted_risk", "approved_exceptions"].includes(sectionKey)) {
      setShowApprovedExceptionsModal(true);
    } else if (["validation_failed", "failed_validation"].includes(sectionKey)) {
      setShowValidationFailedModal(true);
    } else if (["pass", "passed", "error", "unknown", "fresh", "freshness_unknown"].includes(sectionKey)) {
      setPostureStatusModalType(sectionKey === "passed" ? "pass" : sectionKey);
      setShowPostureStatusModal(true);
    } else if (["top_risks"].includes(sectionKey)) {
      if (shouldReturnToExternalSource) {
        setRiskQueueFilter("all");
        setShowRemainingRisksModal(true);
      } else {
        scrollWithRetry(topRisksCardRef, 96);
      }
    } else if (["control_status", "controls"].includes(sectionKey)) {
      scrollWithRetry(controlStatusCardRef, 96);
    } else if (["score", "score_details"].includes(sectionKey)) {
      setShowScoreDetailsModal(true);
    }

    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [
    framework,
    loading,
    routeNavNonceParam,
    routeRemediationQueueParam,
    routeSectionParam,
    scrollToCard,
    shouldReturnToExternalSource,
  ]);

  const extractSyncJobId = useCallback((payload) => {
    return (
      payload?.job_id ||
      payload?.jobId ||
      payload?.id ||
      payload?.sync_job_id ||
      payload?.job?.id ||
      ""
    );
  }, []);

  const waitForSyncJob = useCallback(
    async (token, jobId, signal, options = {}) => {
      if (!jobId) return null;

      const timeoutMs = options.timeoutMs || FULL_CLOUD_ASSESSMENT_TIMEOUT_MS;
      const intervalMs = options.intervalMs || SYNC_JOB_POLL_INTERVAL_MS;
      const onPoll = typeof options.onPoll === "function" ? options.onPoll : null;
      const startedAt = Date.now();

      const terminalStates = new Set([
        "completed",
        "complete",
        "success",
        "succeeded",
        "failed",
        "error",
        "cancelled",
        "canceled",
        "terminal",
      ]);

      let latest = null;
      let attempt = 0;

      while (Date.now() - startedAt < timeoutMs) {
        if (signal?.aborted) {
          throw new DOMException("Sync job polling aborted.", "AbortError");
        }

        attempt += 1;
        latest = await getJsonWithToken(token, `/sync/jobs/${jobId}`, {}, signal);
        const status = String(
          latest?.status ||
            latest?.state ||
            latest?.job_status ||
            latest?.phase ||
            ""
        ).toLowerCase();

        if (onPoll) {
          onPoll({
            attempt,
            job: latest,
            status: status || "running",
            elapsedMs: Date.now() - startedAt,
          });
        }

        if (terminalStates.has(status)) {
          if (["failed", "error", "cancelled", "canceled", "terminal"].includes(status)) {
            throw new Error(
              latest?.error ||
                latest?.error_message ||
                latest?.error_log ||
                latest?.message ||
                `Sync job ${jobId} ended with status ${status}.`
            );
          }

          return latest;
        }

        await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
      }

      const lastStatus = String(
        latest?.status || latest?.state || latest?.job_status || latest?.phase || "running"
      ).toLowerCase();

      const timeoutError = new Error(
        `Sync job ${jobId} is still ${lastStatus} after ${Math.round(timeoutMs / 1000)}s.`
      );
      timeoutError.code = "sync-job-still-running";
      timeoutError.job = latest;
      throw timeoutError;
    },
    [getJsonWithToken]
  );

  const handleViewGuidance = useCallback((item = {}, options = {}) => {
    const target = {
      framework: normalizeRouteParamValue(item.framework || framework),
      controlId: normalizeRouteParamValue(item.control_id || item.controlId),
      ruleId: normalizeRouteParamValue(item.rule_id || item.ruleId),
      resourceId: normalizeRouteParamValue(item.resource_id || item.resourceId),
    };

    if (!target.controlId) return;

    const shouldReturnToRiskQueue = options?.returnTo === "risk_queue";
    const shouldReturnToAllActiveTasks = options?.returnTo === "all_active_tasks";
    const shouldReturnToQueueStatusTasks = options?.returnTo === "queue_status_tasks";
    const shouldReturnToActiveFindings = options?.returnTo === "active_findings";
    const shouldReturnToApprovedExceptions = options?.returnTo === "approved_exceptions";
    const shouldReturnToValidationFailed = options?.returnTo === "validation_failed";
    const shouldReturnToOverdueTasks = options?.returnTo === "overdue_tasks";
    const shouldReturnToInProgressTasks = options?.returnTo === "in_progress_tasks";
    const shouldReturnToClosedTasks = options?.returnTo === "closed_tasks";
    const shouldReturnToPostureStatus = options?.returnTo === "posture_status";
    const shouldReturnToStaleControls = options?.returnTo === "stale_controls";

    setSelectedGuidanceTarget(target);
    setGuidanceModalItem({
      ...item,
      framework: item.framework || framework,
    });
    setGuidanceReturnTarget(
      shouldReturnToRiskQueue
        ? "risk_queue"
        : shouldReturnToAllActiveTasks
        ? "all_active_tasks"
        : shouldReturnToQueueStatusTasks
        ? "queue_status_tasks"
        : shouldReturnToActiveFindings
        ? "active_findings"
        : shouldReturnToApprovedExceptions
        ? "approved_exceptions"
        : shouldReturnToValidationFailed
        ? "validation_failed"
        : shouldReturnToOverdueTasks
        ? "overdue_tasks"
        : shouldReturnToInProgressTasks
        ? "in_progress_tasks"
        : shouldReturnToClosedTasks
        ? "closed_tasks"
        : shouldReturnToPostureStatus
        ? "posture_status"
        : shouldReturnToStaleControls
        ? "stale_controls"
        : "posture"
    );

    if (shouldReturnToRiskQueue) {
      setShowRemainingRisksModal(false);
    }
    if (shouldReturnToAllActiveTasks) {
      setShowAllActiveTasksModal(false);
    }
    if (shouldReturnToQueueStatusTasks) {
      setShowQueueStatusTasksModal(false);
    }
    if (shouldReturnToActiveFindings) {
      setShowActiveFindingsModal(false);
    }
    if (shouldReturnToApprovedExceptions) {
      setShowApprovedExceptionsModal(false);
    }
    if (shouldReturnToValidationFailed) {
      setShowValidationFailedModal(false);
    }
    if (shouldReturnToOverdueTasks) {
      setShowOverdueTasksModal(false);
    }
    if (shouldReturnToInProgressTasks) {
      setShowInProgressTasksModal(false);
    }
    if (shouldReturnToClosedTasks) {
      setShowClosedTasksModal(false);
    }
    if (shouldReturnToPostureStatus) {
      setShowPostureStatusModal(false);
    }
    if (shouldReturnToStaleControls) {
      setShowStaleControlsModal(false);
    }
  }, [framework]);

  const handleCloseGuidanceModal = useCallback(() => {
    const shouldReturnToRiskQueue = guidanceReturnTarget === "risk_queue";
    const shouldReturnToAllActiveTasks = guidanceReturnTarget === "all_active_tasks";
    const shouldReturnToQueueStatusTasks = guidanceReturnTarget === "queue_status_tasks";
    const shouldReturnToActiveFindings = guidanceReturnTarget === "active_findings";
    const shouldReturnToApprovedExceptions = guidanceReturnTarget === "approved_exceptions";
    const shouldReturnToValidationFailed = guidanceReturnTarget === "validation_failed";
    const shouldReturnToOverdueTasks = guidanceReturnTarget === "overdue_tasks";
    const shouldReturnToInProgressTasks = guidanceReturnTarget === "in_progress_tasks";
    const shouldReturnToClosedTasks = guidanceReturnTarget === "closed_tasks";
    const shouldReturnToPostureStatus = guidanceReturnTarget === "posture_status";
    const shouldReturnToStaleControls = guidanceReturnTarget === "stale_controls";

    setGuidanceModalItem(null);
    setGuidanceReturnTarget(null);

    if (shouldReturnToRiskQueue) {
      setShowRemainingRisksModal(true);
    }
    if (shouldReturnToAllActiveTasks) {
      setShowAllActiveTasksModal(true);
    }
    if (shouldReturnToQueueStatusTasks) {
      setShowQueueStatusTasksModal(true);
    }
    if (shouldReturnToActiveFindings) {
      setShowActiveFindingsModal(true);
    }
    if (shouldReturnToApprovedExceptions) {
      setShowApprovedExceptionsModal(true);
    }
    if (shouldReturnToValidationFailed) {
      setShowValidationFailedModal(true);
    }
    if (shouldReturnToOverdueTasks) {
      setShowOverdueTasksModal(true);
    }
    if (shouldReturnToInProgressTasks) {
      setShowInProgressTasksModal(true);
    }
    if (shouldReturnToClosedTasks) {
      setShowClosedTasksModal(true);
    }
    if (shouldReturnToPostureStatus) {
      setShowPostureStatusModal(true);
    }
    if (shouldReturnToStaleControls) {
      setShowStaleControlsModal(true);
    }
  }, [guidanceReturnTarget]);

  const handleOpenFixPlan = useCallback(
    (item = {}, source = "control", options = {}) => {
      const planItem = buildFixPlanItem(
        {
          ...item,
          framework: item.framework || framework,
        },
        source
      );

      const shouldReturnToRiskQueue = options?.returnTo === "risk_queue";
      const shouldReturnToAllActiveTasks = options?.returnTo === "all_active_tasks";
      const shouldReturnToQueueStatusTasks = options?.returnTo === "queue_status_tasks";
      const shouldReturnToActiveFindings = options?.returnTo === "active_findings";
      const shouldReturnToApprovedExceptions = options?.returnTo === "approved_exceptions";
      const shouldReturnToValidationFailed = options?.returnTo === "validation_failed";
      const shouldReturnToOverdueTasks = options?.returnTo === "overdue_tasks";
      const shouldReturnToInProgressTasks = options?.returnTo === "in_progress_tasks";
      const shouldReturnToClosedTasks = options?.returnTo === "closed_tasks";
      const shouldReturnToPostureStatus = options?.returnTo === "posture_status";
      const shouldReturnToStaleControls = options?.returnTo === "stale_controls";

      setFixPlanReturnTarget(
        shouldReturnToRiskQueue
          ? "risk_queue"
          : shouldReturnToAllActiveTasks
          ? "all_active_tasks"
          : shouldReturnToQueueStatusTasks
          ? "queue_status_tasks"
          : shouldReturnToActiveFindings
          ? "active_findings"
          : shouldReturnToApprovedExceptions
          ? "approved_exceptions"
          : shouldReturnToValidationFailed
          ? "validation_failed"
          : shouldReturnToOverdueTasks
          ? "overdue_tasks"
          : shouldReturnToInProgressTasks
          ? "in_progress_tasks"
          : shouldReturnToClosedTasks
          ? "closed_tasks"
          : shouldReturnToPostureStatus
          ? "posture_status"
          : shouldReturnToStaleControls
          ? "stale_controls"
          : null
      );
      if (shouldReturnToRiskQueue) {
        setShowRemainingRisksModal(false);
      }
      if (shouldReturnToAllActiveTasks) {
        setShowAllActiveTasksModal(false);
      }
      if (shouldReturnToQueueStatusTasks) {
        setShowQueueStatusTasksModal(false);
      }
      if (shouldReturnToActiveFindings) {
        setShowActiveFindingsModal(false);
      }
      if (shouldReturnToApprovedExceptions) {
        setShowApprovedExceptionsModal(false);
      }
      if (shouldReturnToValidationFailed) {
        setShowValidationFailedModal(false);
      }
      if (shouldReturnToOverdueTasks) {
        setShowOverdueTasksModal(false);
      }
      if (shouldReturnToInProgressTasks) {
        setShowInProgressTasksModal(false);
      }
      if (shouldReturnToClosedTasks) {
        setShowClosedTasksModal(false);
      }
      if (shouldReturnToPostureStatus) {
        setShowPostureStatusModal(false);
      }
      if (shouldReturnToStaleControls) {
        setShowStaleControlsModal(false);
      }
      setFixPlanItem(planItem);
      setRemediationTaskNotice("");
      setRemediationTaskError("");
      setCreatedRemediationTask(null);
      setSelectedGuidanceTarget({
        framework: normalizeRouteParamValue(planItem.framework || framework),
        controlId: normalizeRouteParamValue(planItem.control_id),
        ruleId: normalizeRouteParamValue(planItem.rule_id),
        resourceId: normalizeRouteParamValue(planItem.resource_id),
      });
    },
    [framework]
  );

  const handleCloseFixPlan = useCallback(() => {
    const shouldReturnToRiskQueue = fixPlanReturnTarget === "risk_queue";
    const shouldReturnToAllActiveTasks = fixPlanReturnTarget === "all_active_tasks";
    const shouldReturnToQueueStatusTasks = fixPlanReturnTarget === "queue_status_tasks";
    const shouldReturnToActiveFindings = fixPlanReturnTarget === "active_findings";
    const shouldReturnToApprovedExceptions = fixPlanReturnTarget === "approved_exceptions";
    const shouldReturnToValidationFailed = fixPlanReturnTarget === "validation_failed";
    const shouldReturnToOverdueTasks = fixPlanReturnTarget === "overdue_tasks";
    const shouldReturnToInProgressTasks = fixPlanReturnTarget === "in_progress_tasks";
    const shouldReturnToClosedTasks = fixPlanReturnTarget === "closed_tasks";
    const shouldReturnToPostureStatus = fixPlanReturnTarget === "posture_status";
    const shouldReturnToStaleControls = fixPlanReturnTarget === "stale_controls";

    setFixPlanItem(null);
    setRemediationTaskNotice("");
    setRemediationTaskError("");
    setCreatedRemediationTask(null);
    setFixPlanReturnTarget(null);

    if (shouldReturnToRiskQueue) {
      setShowRemainingRisksModal(true);
    }
    if (shouldReturnToAllActiveTasks) {
      setShowAllActiveTasksModal(true);
    }
    if (shouldReturnToQueueStatusTasks) {
      setShowQueueStatusTasksModal(true);
    }
    if (shouldReturnToActiveFindings) {
      setShowActiveFindingsModal(true);
    }
    if (shouldReturnToApprovedExceptions) {
      setShowApprovedExceptionsModal(true);
    }
    if (shouldReturnToValidationFailed) {
      setShowValidationFailedModal(true);
    }
    if (shouldReturnToOverdueTasks) {
      setShowOverdueTasksModal(true);
    }
    if (shouldReturnToInProgressTasks) {
      setShowInProgressTasksModal(true);
    }
    if (shouldReturnToClosedTasks) {
      setShowClosedTasksModal(true);
    }
    if (shouldReturnToPostureStatus) {
      setShowPostureStatusModal(true);
    }
    if (shouldReturnToStaleControls) {
      setShowStaleControlsModal(true);
    }
  }, [fixPlanReturnTarget]);

  const handleCloseRemainingRisksModal = useCallback(() => {
    closeRouteModal(() => {
      setShowRemainingRisksModal(false);
      setRiskQueueFilter("all");
      setSelectedRemainingRiskKeys(new Set());
      setRemainingRiskBulkError("");
    });
  }, [closeRouteModal]);

  const handleFrameworkChange = useCallback((nextFramework) => {
    setSelectedGuidanceTarget(null);
    setGuidanceModalItem(null);
    setGuidanceReturnTarget(null);
    setShowRemainingRisksModal(false);
    setShowAllActiveTasksModal(false);
    setAllActiveModalTasks([]);
    setAllActiveModalError("");
    setAllActiveModalTotal(0);
    setShowQueueStatusTasksModal(false);
    setQueueStatusModalType("open");
    setQueueStatusModalTasks([]);
    setQueueStatusModalLoading(false);
    setQueueStatusModalError("");
    setQueueStatusModalTotal(0);
    setShowActiveFindingsModal(false);
    setShowApprovedExceptionsModal(false);
    setShowValidationFailedModal(false);
    setShowOverdueTasksModal(false);
    setOverdueModalTasks([]);
    setOverdueModalError("");
    setOverdueModalTotal(0);
    setShowInProgressTasksModal(false);
    setInProgressModalTasks([]);
    setInProgressModalError("");
    setInProgressModalTotal(0);
    setShowClosedTasksModal(false);
    setClosedModalTasks([]);
    setClosedModalError("");
    setClosedModalTotal(0);
    setShowPostureStatusModal(false);
    setPostureStatusModalType("pass");
    setPostureStatusModalRows([]);
    setPostureStatusModalLoading(false);
    setPostureStatusModalError("");
    setPostureStatusModalTotal(0);
    setPostureStatusModalHasMore(false);
    setPostureStatusModalOffset(0);
    setShowStaleControlsModal(false);
    setRiskQueueFilter("all");
    setSelectedRemainingRiskKeys(new Set());
    setRemainingRiskBulkNotice("");
    setRemainingRiskBulkError("");
    setRelatedSopRemediation(null);
    setRelatedSopError("");
    setAssessmentRefreshNotice("");
    setAssessmentRefreshError("");
    setFullAssessmentStatus("");
    setFullAssessmentError("");
    setLastSyncJobId("");
    setFixPlanItem(null);
    setFixPlanReturnTarget(null);
    setTaskModalReturnTarget(null);
    setRemediationTaskNotice("");
    setRemediationTaskError("");
    setCreatedRemediationTask(null);
    setFramework(nextFramework);
  }, []);

  const handleRunFreshAssessment = useCallback(async () => {
    setAssessmentRefreshing(true);
    setAssessmentRefreshNotice("");
    setAssessmentRefreshError("");

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("posture-refresh-timeout"),
      REQUEST_TIMEOUT_MS
    );

    try {
      const token = await fetchToken();

      await postJsonWithToken(
        token,
        "/normalize/posture/refresh",
        {
          framework,
          dry_run: false,
          run_normalization_first: true,
        },
        controller.signal
      );

      setAssessmentRefreshNotice(
        `${framework} posture refreshed. Latest score, controls, stale items, and risks are up to date.`
      );
      setAssessmentRefreshTick((value) => value + 1);
    } catch (error) {
      console.error("Failed to run fresh posture assessment", error);
      setAssessmentRefreshError(
        error?.name === "AbortError"
          ? `Fresh assessment timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
          : String(error?.message || error || "Unable to run fresh assessment.")
      );
    } finally {
      window.clearTimeout(timeoutId);
      setAssessmentRefreshing(false);
    }
  }, [fetchToken, framework, postJsonWithToken]);

  const handleRunFullCloudAssessment = useCallback(async () => {
    if (String(fixPlanItem?.rule_id || "").toUpperCase().startsWith("EC2_")) {
      setFullAssessmentError("EC2 verification must use the governed five-service Capture Evidence Now pathway; this legacy assessment button is not connected to that pathway yet. No capture was started.");
      return;
    }
    const activeTasksForFramework = remediationTasks.filter(
      (task) => !isClosedTask(task) && normalizeText(task.framework) === normalizeText(framework)
    );
    const targetTask = fixPlanItem
      ? findTaskForItem(activeTasksForFramework, fixPlanItem, framework)
      : activeTasksForFramework.find(
          (task) => normalizeText(task.status) === "ready_for_validation"
        ) || null;

    const targetLabel = fixPlanItem
      ? buildControlLabel(fixPlanItem.control_id, fixPlanItem.rule_id)
      : targetTask
      ? buildControlLabel(targetTask.control_id, targetTask.rule_id)
      : `${framework} ready remediation tasks`;

    const targetRule = fixPlanItem?.rule_id || targetTask?.rule_id || "";
    const targetSteps = buildFixPlanSteps(fixPlanItem || targetTask || {}, [], targetTask || {});
    const assessmentPlan = getFullAssessmentPlanForRule(targetRule);

    setFullAssessmentRunning(true);
    setFullAssessmentStatus(`Starting ${assessmentPlan.syncLabel} sync…`);
    setFullAssessmentError("");
    setAssessmentRefreshNotice("");
    setAssessmentRefreshError("");
    setRemediationTaskNotice("");
    setRemediationTaskError("");
    setLastSyncJobId("");
    setValidationRunModal({
      open: true,
      phase: "running",
      tone: "accent",
      title: "Running Validation Check",
      subtitle: targetLabel,
      detail: assessmentPlan.validationDetail,
      taskId: targetTask?.id || "",
      controlId: fixPlanItem?.control_id || targetTask?.control_id || "",
      ruleId: targetRule,
      resourceId: fixPlanItem?.resource_id || targetTask?.resource_id || "",
      summary: "Starting cloud assessment…",
      nextSteps: [],
    });

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("full-cloud-assessment-timeout"),
      FULL_CLOUD_ASSESSMENT_TIMEOUT_MS
    );

    try {
      const token = await fetchToken();

      setValidationRunModal((current) => ({
        ...(current || {}),
        open: true,
        phase: "running",
        tone: "accent",
        summary: `Starting ${assessmentPlan.syncLabel} sync…`,
        detail: `OASYS is collecting fresh ${assessmentPlan.syncLabel} evidence before validation.`,
      }));

      const syncPayload = await postJsonWithToken(
        token,
        `/sync/run?provider=aws&stream=${assessmentPlan.syncStream}`,
        {},
        controller.signal
      );

      const jobId = extractSyncJobId(syncPayload);
      if (jobId) {
        setLastSyncJobId(jobId);
        setFullAssessmentStatus(`${assessmentPlan.syncLabel} sync started. Waiting for job ${jobId}…`);
        setValidationRunModal((current) => ({
          ...(current || {}),
          open: true,
          phase: "running",
          tone: "accent",
          summary: `${assessmentPlan.syncLabel} sync is running…`,
          detail: `Waiting for ${assessmentPlan.syncLabel} sync job ${jobId} to finish before validating remediation tasks.`,
          syncJobId: jobId,
        }));
        await waitForSyncJob(token, jobId, controller.signal, {
          timeoutMs: FULL_CLOUD_ASSESSMENT_TIMEOUT_MS,
          intervalMs: SYNC_JOB_POLL_INTERVAL_MS,
          onPoll: ({ attempt, status, elapsedMs }) => {
            const elapsedSeconds = Math.max(1, Math.round(elapsedMs / 1000));
            const statusLabel = status ? status.replace(/_/g, " ") : "running";

            setFullAssessmentStatus(
              `${assessmentPlan.syncLabel} sync job ${jobId} is ${statusLabel}. Waiting for completion…`
            );
            setValidationRunModal((current) => ({
              ...(current || {}),
              open: true,
              phase: "running",
              tone: "accent",
              summary: `${assessmentPlan.syncLabel} sync is ${statusLabel}…`,
              detail: `Polling ${assessmentPlan.syncLabel} sync job ${jobId}. Attempt ${attempt}. Elapsed ${elapsedSeconds}s. OASYS will continue waiting before validating remediation tasks.`,
              syncJobId: jobId,
            }));
          },
        });
      } else {
        setFullAssessmentStatus(`${assessmentPlan.syncLabel} sync started. Proceeding to posture recalculation…`);
      }

      if (assessmentPlan.normalizeStream) {
        setFullAssessmentStatus(`Normalizing ${assessmentPlan.normalizeLabel} findings…`);
        setValidationRunModal((current) => ({
          ...(current || {}),
          open: true,
          phase: "running",
          tone: "accent",
          summary: `Normalizing ${assessmentPlan.normalizeLabel} evidence…`,
          detail: `OASYS is updating normalized findings from the latest ${assessmentPlan.normalizeLabel} source data before posture recalculation.`,
          syncJobId: jobId || "",
        }));

        await postJsonWithToken(
          token,
          `/normalize/run?source=aws&stream=${assessmentPlan.normalizeStream}`,
          {
            source: "aws",
            stream: assessmentPlan.normalizeStream,
            dry_run: false,
          },
          controller.signal
        );
      }

      setFullAssessmentStatus("Recalculating posture from refreshed evidence…");
      setValidationRunModal((current) => ({
        ...(current || {}),
        open: true,
        phase: "running",
        tone: "accent",
        summary: "Recalculating posture…",
        detail: "OASYS is rebuilding control status from the latest evidence.",
      }));

      await postJsonWithToken(
        token,
        "/normalize/posture/refresh",
        {
          framework,
          dry_run: false,
          run_normalization_first: assessmentPlan.postureRunNormalizationFirst,
        },
        controller.signal
      );

      setFullAssessmentStatus("Validating ready remediation tasks against latest evidence…");
      setValidationRunModal((current) => ({
        ...(current || {}),
        open: true,
        phase: "running",
        tone: "accent",
        summary: "Validating remediation tasks…",
        detail: "OASYS is checking whether ready tasks now pass against normalized findings and control status.",
      }));

      const validationPayload = await postJsonWithToken(
        token,
        "/remediation/tasks/validate-ready",
        {
          framework,
          last_assessment_job_id: jobId || null,
        },
        controller.signal
      );

      const validatedTasks = Array.isArray(validationPayload?.validated_tasks)
        ? validationPayload.validated_tasks
        : [];
      const failedTasks = Array.isArray(validationPayload?.failed_tasks)
        ? validationPayload.failed_tasks
        : [];
      const skippedTasks = Array.isArray(validationPayload?.skipped_tasks)
        ? validationPayload.skipped_tasks
        : [];
      const updatedTasks = [...validatedTasks, ...failedTasks, ...skippedTasks];

      if (updatedTasks.length > 0) {
        setRemediationTasks((current) =>
          current.map((task) => {
            const updated = updatedTasks.find((item) => item.id === task.id);
            return updated ? mergeTaskPreservingApprovedException(task, updated) : task;
          })
        );

        setCreatedRemediationTask((current) => {
          if (!current?.id) return current;
          const updated = updatedTasks.find((item) => item.id === current.id);
          return updated ? mergeTaskPreservingApprovedException(current, updated) : current;
        });
      }

      const checkedCount = safeNumber(validationPayload?.checked_count);
      const validatedCount = safeNumber(validationPayload?.validated_count);
      const failedCount = safeNumber(validationPayload?.failed_count);
      const skippedCount = safeNumber(validationPayload?.skipped_count);

      const validationSummary =
        checkedCount > 0
          ? `Validation complete: ${validatedCount} passed, ${failedCount} failed, ${skippedCount} skipped.`
          : "No ready remediation tasks needed validation.";

      const rawFocusedUpdatedTask =
        (targetTask?.id && updatedTasks.find((task) => task.id === targetTask.id)) ||
        failedTasks[0] ||
        validatedTasks[0] ||
        null;

      const focusedUpdatedTask = targetTask?.id
        ? mergeTaskPreservingApprovedException(targetTask, rawFocusedUpdatedTask || targetTask)
        : rawFocusedUpdatedTask;

      const approvedExceptionAccepted =
        isApprovedExceptionTask(targetTask) || isApprovedExceptionTask(focusedUpdatedTask);
      const lastValidation = getTaskLastValidation(focusedUpdatedTask) || {};
      const focusedValidationStatus = normalizeText(focusedUpdatedTask?.validation_status);
      const focusedTaskStatus = normalizeText(focusedUpdatedTask?.status);
      const targetPassed =
        focusedValidationStatus === "passed" || focusedTaskStatus === "validated";
      const targetFailed = !approvedExceptionAccepted && focusedValidationStatus === "failed";
      const failed = targetFailed || (!approvedExceptionAccepted && !focusedUpdatedTask && failedCount > 0);
      const passed =
        approvedExceptionAccepted ||
        targetPassed ||
        (!focusedUpdatedTask && validatedCount > 0 && failedCount === 0);
      const approvedExceptionUi = approvedExceptionAccepted
        ? buildApprovedExceptionValidationUiState({
            validationSummary,
            task: focusedUpdatedTask || targetTask || {},
            fallbackLabel: targetLabel,
          })
        : null;

      setRemediationTaskNotice(
        approvedExceptionAccepted
          ? "Approved exception recorded. Source finding may still exist by design; finalize the task as Validated, then close with a closure reason."
          : validationSummary
      );

      setValidationRunModal({
        open: true,
        phase: "complete",
        tone: approvedExceptionUi?.tone || (failed ? "danger" : passed ? "ok" : "warn"),
        title:
          approvedExceptionUi?.title ||
          (failed ? "Validation Failed" : passed ? "Validation Passed" : "Validation Complete"),
        subtitle: approvedExceptionUi?.subtitle || (focusedUpdatedTask
          ? buildControlLabel(focusedUpdatedTask.control_id, focusedUpdatedTask.rule_id)
          : targetLabel),
        taskId: focusedUpdatedTask?.id || targetTask?.id || "",
        controlId: focusedUpdatedTask?.control_id || targetTask?.control_id || fixPlanItem?.control_id || "",
        ruleId: focusedUpdatedTask?.rule_id || targetRule,
        resourceId: focusedUpdatedTask?.resource_id || targetTask?.resource_id || fixPlanItem?.resource_id || "",
        syncJobId: jobId || "",
        summary: approvedExceptionUi?.summary || validationSummary,
        detail:
          approvedExceptionUi?.detail ||
          lastValidation?.reason ||
          (failed
            ? "The task did not pass validation. The related finding or control condition is still failing."
            : passed
            ? "The task passed validation against the latest source evidence."
            : "No ready remediation tasks were available to validate."),
        nextSteps: approvedExceptionUi?.nextSteps || (failed
          ? [
              "Open the Fix Plan and complete the remaining source-system remediation.",
              ...(targetSteps || []).slice(0, 4),
              "Run Full Cloud Assessment again after the source fix is complete.",
            ]
          : passed
          ? [
              "Review the validated task details.",
              "Close the remediation task to complete the audit trail.",
              "Confirm the related risk no longer appears as active after the page reloads.",
            ]
          : [
              "Move a remediation task to Ready for Validation when the source fix is complete.",
              "Run Full Cloud Assessment again to validate it.",
            ]),
        stats: {
          checked: checkedCount,
          passed: validatedCount,
          failed: failedCount,
          skipped: skippedCount,
        },
      });

      setFullAssessmentStatus("");
      setAssessmentRefreshNotice(
        approvedExceptionAccepted
          ? `${framework} cloud assessment complete. Approved exception recognized; posture refreshed.`
          : `${framework} cloud assessment complete. ${validationSummary} Posture refreshed.`
      );
      setAssessmentRefreshTick((value) => value + 1);
    } catch (error) {
      console.error("Failed to run full cloud assessment", error);
      const message =
        error?.name === "AbortError"
          ? `Full cloud assessment timed out after ${FULL_CLOUD_ASSESSMENT_TIMEOUT_MS / 1000}s.`
          : String(error?.message || error || "Unable to run full cloud assessment.");

      setValidationRunModal((current) => ({
        ...(current || {}),
        open: true,
        phase: "error",
        tone: "danger",
        title: "Validation Check Failed",
        summary: "The cloud assessment did not complete.",
        detail: message,
        nextSteps: [
          "Review the error details.",
          "Confirm the API, worker, AWS credentials, and sync services are healthy.",
          "Run Full Cloud Assessment again after the issue is resolved.",
        ],
      }));

      setFullAssessmentError(message);
    } finally {
      window.clearTimeout(timeoutId);
      setFullAssessmentRunning(false);
    }
  }, [
    extractSyncJobId,
    fetchToken,
    fixPlanItem,
    framework,
    postJsonWithToken,
    remediationTasks,
    waitForSyncJob,
  ]);


  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr(null);
    setPanelWarnings([]);

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("posture-page-timeout"),
      REQUEST_TIMEOUT_MS
    );

    (async () => {
      try {
        const token = await fetchToken();

        const requests = [
          {
            key: "overview",
            label: "Posture overview",
            required: true,
            promise: getJsonWithToken(
              token,
              "/dashboard/posture/overview",
              { framework },
              controller.signal
            ),
          },
          {
            key: "summary",
            label: "Framework summary",
            required: true,
            promise: getJsonWithToken(
              token,
              "/dashboard/summary",
              {},
              controller.signal
            ),
          },
          {
            key: "topRisks",
            label: "Top risks",
            required: false,
            promise: getJsonWithToken(
              token,
              "/dashboard/top-risks",
              { framework, limit: 50 },
              controller.signal
            ),
          },
          {
            key: "riskQueue",
            label: "Risk queue",
            required: false,
            promise: getJsonWithToken(
              token,
              "/dashboard/risk-queue",
              { framework, limit: 200 },
              controller.signal
            ),
          },
          {
            key: "identityFindings",
            label: "Connector findings",
            required: false,
            promise: getJsonWithToken(
              token,
              "/normalize/findings",
              { limit: 250 },
              controller.signal
            ),
          },
          {
            key: "staleControls",
            label: "Stale controls",
            required: false,
            promise: getJsonWithToken(
              token,
              "/dashboard/stale-controls",
              { framework, limit: 12 },
              controller.signal
            ),
          },
          {
            key: "controlStatus",
            label: "Control status",
            required: false,
            promise: getJsonWithToken(
              token,
              "/dashboard/control-status/latest",
              { framework, limit: 500, include_passed: true, include_freshness: true },
              controller.signal
            ),
          },
          {
            key: "postureDelta",
            label: "Posture delta",
            required: false,
            promise: getJsonWithToken(
              token,
              "/dashboard/posture/delta",
              { framework },
              controller.signal
            ),
          },
          {
            key: "remediationTasks",
            label: "Remediation tasks",
            required: false,
            promise: getJsonWithToken(
              token,
              "/remediation/tasks",
              { framework, include_closed: true, limit: 100 },
              controller.signal
            ),
          },
        ];

        const settled = await Promise.allSettled(requests.map((r) => r.promise));
        if (!mounted) return;

        const nextWarnings = [];
        let requiredFailure = null;

        let nextOverview = null;
        let nextSummaryFrameworks = [];
        let nextTopRisks = [];
        let nextRiskQueue = [];
        let nextIdentityFindings = [];
        let nextCodeEdgeFindings = [];
        let nextStaleControls = [];
        let nextControlStatusRows = [];
        let nextPostureDelta = [];
        let nextRemediationTasks = [];

        settled.forEach((result, index) => {
          const req = requests[index];

          if (result.status === "fulfilled") {
            const value = result.value;

            if (req.key === "overview") nextOverview = value || null;
            if (req.key === "summary") nextSummaryFrameworks = normalizeSummaryFrameworks(value);
            if (req.key === "topRisks") nextTopRisks = normalizeTopRisks(value);
            if (req.key === "riskQueue") nextRiskQueue = normalizeTopRisks(value);
            if (req.key === "identityFindings") {
              const normalizedConnectorFindings = normalizeFindings(value);
              nextIdentityFindings = normalizedConnectorFindings.filter(isIdentityConnectorFinding);
              nextCodeEdgeFindings = normalizedConnectorFindings.filter(isCodeEdgeConnectorFinding);
            }
            if (req.key === "staleControls") nextStaleControls = normalizeStaleControls(value);
            if (req.key === "controlStatus") nextControlStatusRows = normalizeControlStatus(value);
            if (req.key === "postureDelta") nextPostureDelta = normalizePostureDelta(value);
            if (req.key === "remediationTasks") nextRemediationTasks = normalizeRemediationTasks(value);

            return;
          }

          const reason = result.reason;
          const message =
            reason?.name === "AbortError"
              ? `${req.label} timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
              : String(reason?.message || reason || `${req.label} failed.`);

          if (req.required) {
            requiredFailure = `${req.label} failed: ${message}`;
          } else {
            nextWarnings.push(`${req.label} unavailable: ${message}`);
          }
        });

        if (requiredFailure) throw new Error(requiredFailure);

        setOverview(nextOverview);
        setSummaryFrameworks(nextSummaryFrameworks);
        setTopRisks(nextTopRisks);
        setRiskQueue(nextRiskQueue);
        setIdentityFindings(nextIdentityFindings);
        setCodeEdgeFindings(nextCodeEdgeFindings);
        setStaleControls(nextStaleControls);
        setControlStatusRows(nextControlStatusRows);
        setPostureDelta(nextPostureDelta);
        setRemediationTasks(
          mergeTaskCollectionsPreservingApprovedExceptions(
            remediationTasksRef.current,
            nextRemediationTasks
          )
        );
        setRemediationTaskLoadError("");
        setPanelWarnings(nextWarnings);
      } catch (e) {
        if (!mounted) return;
        console.error("Posture page load error:", e);
        setErr(String(e?.message || e));
      } finally {
        clearTimeout(timeoutId);
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [assessmentRefreshTick, fetchToken, getJsonWithToken, framework]);

  const selectedFrameworkSummary = useMemo(() => {
    return summaryFrameworks.find((item) => item.framework === framework) || null;
  }, [summaryFrameworks, framework]);

  const selectedDelta = useMemo(() => {
    return postureDelta.find((item) => item.framework === framework) || null;
  }, [postureDelta, framework]);

  const heroMetrics = useMemo(() => {
    const latestScore = safeNumber(
      overview?.raw_latest_score ?? overview?.latest_score,
      selectedFrameworkSummary?.latest_score ?? 0
    );

    const scoreCounts = overview?.score_counts || selectedFrameworkSummary?.score_counts || {};
    const rawFailCount = safeNumber(scoreCounts.fail_count);
    const errorCount = safeNumber(scoreCounts.error_count);
    const unknownCount = safeNumber(scoreCounts.unknown_count);

    const acceptedRiskFindings = safeNumber(overview?.accepted_risk_findings, 0);
    const acceptedRiskControls = safeNumber(overview?.accepted_risk_controls, 0);
    const approvedExceptionControls = safeNumber(overview?.approved_exception_controls, 0);
    const rawNonpassingFindings = safeNumber(
      overview?.raw_nonpassing_findings,
      rawFailCount + errorCount + unknownCount
    );
    const activeFailCount = safeNumber(
      overview?.active_fail_findings,
      Math.max(0, rawFailCount - acceptedRiskFindings)
    );

    const operationalScore =
      overview?.operational_score !== undefined && overview?.operational_score !== null
        ? clampPercentScore(overview.operational_score)
        : estimateOperationalScore({
            rawScore: latestScore,
            rawFailCount,
            errorCount,
            unknownCount,
            acceptedRiskFindings,
          });

    const previousRawScore = coalesceNumber(
      overview?.previous_raw_score,
      selectedDelta?.raw_previous_score,
      selectedDelta?.previous_score
    );
    const previousOperationalScore = coalesceNumber(
      overview?.previous_operational_score,
      selectedDelta?.previous_operational_score
    );

    const rawScoreDeltaValue = coalesceNumber(
      overview?.raw_score_delta,
      selectedDelta?.raw_score_delta,
      selectedDelta?.delta,
      previousRawScore !== null ? latestScore - previousRawScore : null
    );

    const operationalScoreDeltaValue = coalesceNumber(
      overview?.operational_score_delta,
      selectedDelta?.operational_score_delta,
      previousOperationalScore !== null ? operationalScore - previousOperationalScore : null,
      rawScoreDeltaValue
    );

    const delta = safeNumber(rawScoreDeltaValue, 0);
    const operationalScoreDelta = safeNumber(operationalScoreDeltaValue, 0);

    const activeFailingControls =
      overview?.active_failing_controls !== undefined && overview?.active_failing_controls !== null
        ? safeNumber(overview.active_failing_controls)
        : activeFailCount + errorCount;

    const staleCount =
      safeNumber(overview?.stale_controls) ||
      safeNumber(selectedFrameworkSummary?.freshness?.stale) ||
      safeNumber(selectedFrameworkSummary?.stale_controls);

    const dataAsOf =
      overview?.data_as_of ||
      overview?.score_captured_at ||
      overview?.freshness_captured_at ||
      selectedFrameworkSummary?.latest_score_captured_at ||
      null;

    return {
      latestScore,
      operationalScore,
      previousRawScore,
      previousOperationalScore,
      rawScoreDelta: delta,
      operationalScoreDelta,
      delta,
      failingControls: activeFailingControls,
      rawFailCount,
      rawNonpassingFindings,
      activeFailCount,
      errorCount,
      unknownCount,
      acceptedRiskFindings,
      acceptedRiskControls,
      approvedExceptionControls,
      staleControls: staleCount,
      dataAsOf,
      scoreMode: overview?.score_mode || "raw_and_operational",
      scoreAdjustmentReason:
        overview?.score_adjustment_reason ||
        "Operational score separates evidence-backed approved exceptions from active unresolved work while preserving the raw score.",
    };
  }, [overview, selectedFrameworkSummary, selectedDelta]);

  const postureActionPlan = useMemo(() => {
    const failingControls = safeNumber(heroMetrics.failingControls);
    const staleCount = safeNumber(heroMetrics.staleControls);
    const scoreGap = Math.max(0, 100 - safeNumber(heroMetrics.operationalScore));

    const items = [];

    if (failingControls > 0) {
      items.push({
        title: "Fix failing controls first",
        detail: `${failingControls} failing/error control condition${failingControls === 1 ? "" : "s"} are currently reducing the ${framework} score.`,
        action: "Review top risks",
        onClick: () => scrollToCard(topRisksCardRef),
        tone: "danger",
      });
    } else {
      items.push({
        title: "No failing controls",
        detail: "No failing/error control conditions are currently counted for this framework.",
        action: "View controls",
        onClick: () => scrollToCard(controlStatusCardRef),
        tone: "ok",
      });
    }

    if (staleCount > 0) {
      items.push({
        title: "Clear stale evidence",
        detail: `${staleCount} control${staleCount === 1 ? "" : "s"} need fresh evidence or source checks before the score can be trusted as current.`,
        action: "Review stale controls",
        onClick: () => setShowStaleControlsModal(true),
        tone: "warn",
      });
    } else {
      items.push({
        title: "Evidence is fresh",
        detail: "No stale controls are currently counted for this framework.",
        action: "View freshness",
        onClick: () => scrollToCard(controlStatusCardRef),
        tone: "ok",
      });
    }

    items.push({
      title: "Recalculate after remediation",
      detail:
        scoreGap > 0
          ? `${scoreGap.toFixed(1)} percentage points remain between this posture and 100%. Run a fresh assessment after fixes are completed.`
          : "This framework is at 100%. Continue monitoring for drift and stale evidence.",
      action: "Run fresh assessment",
      onClick: handleRunFreshAssessment,
      tone: scoreGap > 0 ? "default" : "ok",
    });

    return items;
  }, [
    framework,
    handleRunFreshAssessment,
    heroMetrics.failingControls,
    heroMetrics.operationalScore,
    heroMetrics.staleControls,
    scrollToCard,
  ]);

  const scoreBreakdown = useMemo(() => {
    const scoreCounts = overview?.score_counts || selectedFrameworkSummary?.score_counts || {};
    const freshness = overview?.freshness || selectedFrameworkSummary?.freshness || {};
    const rawFail = safeNumber(scoreCounts.fail_count);
    const acceptedRiskFindings = safeNumber(overview?.accepted_risk_findings, 0);
    const activeFail = Math.max(0, rawFail - acceptedRiskFindings);

    return {
      pass: safeNumber(scoreCounts.pass_count),
      fail: activeFail,
      rawFail,
      acceptedRiskFindings,
      acceptedRiskControls: safeNumber(overview?.accepted_risk_controls, 0),
      approvedExceptionControls: safeNumber(overview?.approved_exception_controls, 0),
      error: safeNumber(scoreCounts.error_count),
      unknown: safeNumber(scoreCounts.unknown_count),
      fresh: safeNumber(freshness.fresh),
      stale: safeNumber(freshness.stale),
      freshnessUnknown: safeNumber(freshness.unknown),
    };
  }, [overview, selectedFrameworkSummary]);

  const getPostureStatusSnapshotTotal = useCallback(
    (statusType) => {
      const normalized = normalizeText(statusType || "pass");
      if (normalized === "freshness_unknown" || normalized === "freshnessunknown") {
        return safeNumber(scoreBreakdown.freshnessUnknown, 0);
      }
      return safeNumber(scoreBreakdown[normalized], 0);
    },
    [scoreBreakdown]
  );

  const getPostureStatusQueryParams = useCallback(
    (statusType, offset = 0, limit = 25) => {
      const normalized = normalizeText(statusType || "pass");
      const params = {
        framework,
        limit,
        offset,
      };

      if (normalized === "pass" || normalized === "passed") {
        params.state = "pass";
      } else if (normalized === "error") {
        params.state = "error";
      } else if (normalized === "unknown") {
        params.state = "unknown";
      } else if (normalized === "fresh") {
        params.freshness_state = "fresh";
      } else if (normalized === "freshness_unknown" || normalized === "freshnessunknown") {
        params.freshness_state = "unknown";
      }

      return params;
    },
    [framework]
  );

  const normalizeControlStatusModalRows = useCallback(
    (payload) => normalizeControlStatus(payload).map((row) => ({
      framework: row.framework || framework,
      control_id: row.control_id || row.controlId || "—",
      rule_id: row.rule_id || row.ruleId || "",
      resource_id: row.resource_id || row.resourceId || "",
      state: row.state || row.status || row.control_state || "unknown",
      freshness_state:
        row.freshness_state ||
        row.freshness_status ||
        row.freshness ||
        "",
      last_checked_at:
        row.last_checked_at ||
        row.captured_at ||
        row.updated_at ||
        row.last_seen_at ||
        null,
      open_findings_total:
        safeNumber(row.open_findings_total) ||
        safeNumber(row.findings_open) ||
        safeNumber(row.open_findings),
    })),
    [framework]
  );

  const loadPostureStatusModalPage = useCallback(
    async ({ statusType = postureStatusModalType, offset = 0, append = false } = {}) => {
      const normalizedType = normalizeText(statusType || "pass");
      const snapshotTotal = getPostureStatusSnapshotTotal(normalizedType);
      const pageSize = normalizedType === "pass" ? 25 : 50;

      if (snapshotTotal <= 0) {
        setPostureStatusModalRows([]);
        setPostureStatusModalTotal(0);
        setPostureStatusModalOffset(0);
        setPostureStatusModalHasMore(false);
        setPostureStatusModalError("");
        setPostureStatusModalLoading(false);
        return;
      }

      setPostureStatusModalLoading(true);
      setPostureStatusModalError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("posture-status-page-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const payload = await getJsonWithToken(
          token,
          "/dashboard/control-status/latest",
          getPostureStatusQueryParams(normalizedType, offset, pageSize),
          controller.signal
        );

        let nextRows = normalizeControlStatusModalRows(payload);
        let backendTotal = safeNumber(payload?.total_count, 0);
        let backendHasMore = Boolean(payload?.has_more);

        if (
          normalizedType === "unknown" &&
          offset === 0 &&
          nextRows.length === 0 &&
          scoreBreakdown.unknown > 0 &&
          scoreBreakdown.freshnessUnknown === 0
        ) {
          const fallbackPayload = await getJsonWithToken(
            token,
            "/dashboard/control-status/latest",
            {
              ...getPostureStatusQueryParams("freshness_unknown", 0, pageSize),
              limit: Math.max(1, Math.min(pageSize, scoreBreakdown.unknown)),
            },
            controller.signal
          );

          nextRows = normalizeControlStatusModalRows(fallbackPayload);
          backendTotal = safeNumber(fallbackPayload?.total_count, 0);
          backendHasMore = false;
        }

        const total = Math.max(snapshotTotal, backendTotal || 0, nextRows.length);

        const nextLoadedCount = offset + nextRows.length;

        setPostureStatusModalRows((current) =>
          append ? [...current, ...nextRows] : nextRows
        );
        setPostureStatusModalTotal(total);
        setPostureStatusModalOffset(nextLoadedCount);
        setPostureStatusModalHasMore(backendHasMore || nextLoadedCount < total);
      } catch (error) {
        console.error("Failed to load posture status detail rows", error);
        setPostureStatusModalError(
          error?.name === "AbortError"
            ? `Posture status detail rows timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to load posture status detail rows.")
        );
        if (!append) {
          setPostureStatusModalRows([]);
          setPostureStatusModalTotal(snapshotTotal);
          setPostureStatusModalOffset(0);
          setPostureStatusModalHasMore(false);
        }
      } finally {
        window.clearTimeout(timeoutId);
        setPostureStatusModalLoading(false);
      }
    },
    [
      fetchToken,
      getJsonWithToken,
      getPostureStatusQueryParams,
      getPostureStatusSnapshotTotal,
      normalizeControlStatusModalRows,
      postureStatusModalType,
      scoreBreakdown.freshnessUnknown,
      scoreBreakdown.unknown,
    ]
  );

  const openPostureStatusModal = useCallback((statusType) => {
    const normalizedType = normalizeText(statusType || "pass");

    setPostureStatusModalType(normalizedType);
    setPostureStatusModalRows([]);
    setPostureStatusModalError("");
    setPostureStatusModalTotal(getPostureStatusSnapshotTotal(normalizedType));
    setPostureStatusModalOffset(0);
    setPostureStatusModalHasMore(false);
    setShowPostureStatusModal(true);
  }, [getPostureStatusSnapshotTotal]);

  useEffect(() => {
    if (!showPostureStatusModal) return;
    loadPostureStatusModalPage({
      statusType: postureStatusModalType,
      offset: 0,
      append: false,
    });
  }, [loadPostureStatusModalPage, postureStatusModalType, showPostureStatusModal]);

  const filteredControlStatusRows = useMemo(() => {
    const rows = Array.isArray(controlStatusRows) ? controlStatusRows : [];
    return rows
      .filter((row) => !framework || row.framework === framework)
      .map((row) => ({
        framework: row.framework || framework,
        control_id: row.control_id || row.controlId || "—",
        rule_id: row.rule_id || row.ruleId || "",
        resource_id: row.resource_id || row.resourceId || "",
        state: row.state || row.status || row.control_state || "unknown",
        freshness_state:
          row.freshness_state ||
          row.freshness_status ||
          row.freshness ||
          "",
        last_checked_at:
          row.last_checked_at ||
          row.captured_at ||
          row.updated_at ||
          row.last_seen_at ||
          null,
        open_findings_total:
          safeNumber(row.open_findings_total) ||
          safeNumber(row.findings_open) ||
          safeNumber(row.open_findings),
      }));
  }, [controlStatusRows, framework]);


  const hasExplicitPostureSelection = useMemo(() => {
    return Boolean(
      selectedGuidanceTarget?.controlId ||
        selectedGuidanceTarget?.ruleId ||
        selectedGuidanceTarget?.resourceId ||
        routeSelectionContext.controlId ||
        routeSelectionContext.ruleId ||
        routeSelectionContext.resourceId
    );
  }, [selectedGuidanceTarget, routeSelectionContext]);

  const effectiveFocusContext = useMemo(() => {
    if (!hasExplicitPostureSelection) return {};
    return selectedGuidanceTarget || routeSelectionContext;
  }, [hasExplicitPostureSelection, selectedGuidanceTarget, routeSelectionContext]);

  const focusedContext = useMemo(() => {
    return pickBestFocus({
      controlRows: filteredControlStatusRows,
      topRisks,
      staleControls,
      context: effectiveFocusContext,
    });
  }, [filteredControlStatusRows, topRisks, staleControls, effectiveFocusContext]);

  const focusControlContext = useMemo(
    () => getFocusControlContext(focusedContext, framework),
    [focusedContext, framework]
  );

  const focusFrameworkValue = focusControlContext?.framework || "";
  const focusControlIdValue = focusControlContext?.controlId || "";
  const focusRuleIdValue = focusControlContext?.ruleId || "";

  const riskTone = useMemo(() => {
    if (heroMetrics.latestScore >= 90) return "low";
    if (heroMetrics.latestScore >= 75) return "medium";
    return "high";
  }, [heroMetrics.latestScore]);

  const riskLabel = useMemo(() => {
    if (riskTone === "low") return "Stable • Low Risk";
    if (riskTone === "medium") return "Watchlist • Medium Risk";
    return "Action Required • High Risk";
  }, [riskTone]);

  const contextLine = useMemo(() => {
    const parts = [];
    if (effectiveFocusContext.framework) parts.push(`framework ${effectiveFocusContext.framework}`);
    if (effectiveFocusContext.controlId) parts.push(`control ${effectiveFocusContext.controlId}`);
    if (effectiveFocusContext.ruleId) parts.push(`rule ${effectiveFocusContext.ruleId}`);
    if (effectiveFocusContext.resourceId) parts.push(`resource ${effectiveFocusContext.resourceId}`);
    if (!parts.length) return null;
    return `${selectedGuidanceTarget ? "Guidance selected" : "Scoped from Drift/Alerts"}: ${parts.join(" · ")}`;
  }, [effectiveFocusContext, selectedGuidanceTarget]);

  const jumpToInvestigationSummary = useCallback((behavior = "smooth") => {
    const target = investigationSummaryRef.current;
    if (!target) return false;

    try {
      target.scrollIntoView({ behavior, block: "start", inline: "nearest" });
    } catch {
      target.scrollIntoView();
    }

    setInvestigationSummaryHighlighted(true);
    window.setTimeout(() => {
      setInvestigationSummaryHighlighted(false);
    }, 1800);

    return true;
  }, []);

  useEffect(() => {
    const hasRouteInvestigationTarget = Boolean(
      routeSelectionContext.controlId ||
        routeSelectionContext.ruleId ||
        routeSelectionContext.resourceId
    );

    if (
      loading ||
      !hasRouteInvestigationTarget ||
      !contextLine ||
      !focusedContext.kind
    ) {
      return undefined;
    }

    const navigationKey = [
      routeSelectionContext.framework || framework,
      routeSelectionContext.controlId,
      routeSelectionContext.ruleId,
      routeSelectionContext.resourceId,
      routeNavNonceParam,
    ]
      .filter(Boolean)
      .join("::");

    if (navigationKey && routeNavigationHandledRef.current === navigationKey) {
      return undefined;
    }

    routeNavigationHandledRef.current = navigationKey || String(Date.now());

    const timeoutId = window.setTimeout(() => {
      jumpToInvestigationSummary("smooth");
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [
    contextLine,
    focusedContext.kind,
    framework,
    jumpToInvestigationSummary,
    loading,
    routeNavNonceParam,
    routeSelectionContext.controlId,
    routeSelectionContext.framework,
    routeSelectionContext.resourceId,
    routeSelectionContext.ruleId,
  ]);

  useEffect(() => {
    let mounted = true;

    if (!hasExplicitPostureSelection || !focusControlContext?.controlId) {
      setRelatedSopRemediation(null);
      setRelatedSopLoading(false);
      setRelatedSopError("");
      return () => {
        mounted = false;
      };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("related-sop-remediation-timeout"),
      REQUEST_TIMEOUT_MS
    );

    (async () => {
      try {
        setRelatedSopLoading(true);
        setRelatedSopError("");

        const token = await fetchToken();
        const attempts = buildRemediationFetchAttempts(focusControlContext, framework);

        let selectedPayload = null;
        let fallbackPayload = null;

        for (const attempt of attempts) {
          const payload = await getJsonWithToken(
            token,
            `/controls/${encodeURIComponent(attempt.controlId)}/remediation`,
            attempt.params,
            controller.signal
          );

          if (!fallbackPayload) {
            fallbackPayload = payload || null;
          }

          if (hasLinkedSops(payload)) {
            selectedPayload = payload;
            break;
          }
        }

        if (!mounted) return;
        setRelatedSopRemediation(selectedPayload || fallbackPayload || null);
      } catch (e) {
        if (!mounted) return;
        if (e?.name === "AbortError") {
          setRelatedSopError(
            `Related SOP guidance timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
          );
        } else {
          setRelatedSopError(
            String(e?.message || e || "Unable to load related SOP guidance.")
          );
        }
        setRelatedSopRemediation(null);
      } finally {
        clearTimeout(timeoutId);
        if (mounted) setRelatedSopLoading(false);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [
    fetchToken,
    focusControlContext,
    focusControlIdValue,
    focusFrameworkValue,
    focusRuleIdValue,
    framework,
    getJsonWithToken,
    hasExplicitPostureSelection,
  ]);

  const relatedSops = useMemo(
    () => normalizeRelatedSops(relatedSopRemediation),
    [relatedSopRemediation]
  );

  const recommendedActions = useMemo(
    () => normalizeRecommendedActions(relatedSopRemediation),
    [relatedSopRemediation]
  );

  const fixPlanRelatedSops = useMemo(() => {
    const sops = normalizeRelatedSops(relatedSopRemediation);
    const activeRuleId = normalizeText(fixPlanItem?.rule_id || "");
    const activeFramework = normalizeText(fixPlanItem?.framework || framework || "");
    const activeControlId = normalizeText(fixPlanItem?.control_id || "");

    if (!activeRuleId) return sops;

    return sops.filter((sop) => {
      const link = sop?.link || {};
      const sopRuleId = normalizeText(link.rule_id || sop.rule_id || "");
      const sopFramework = normalizeText(link.framework || sop.framework || "");
      const sopControlId = normalizeText(link.control_id || sop.control_id || "");

      const ruleMatches = sopRuleId === activeRuleId;
      const frameworkMatches = !activeFramework || !sopFramework || sopFramework === activeFramework;
      const controlMatches = !activeControlId || !sopControlId || sopControlId === activeControlId;

      return ruleMatches && frameworkMatches && controlMatches;
    });
  }, [relatedSopRemediation, fixPlanItem, framework]);

  const activeRemediationTasks = useMemo(
    () => remediationTasks.filter((task) => !isClosedTask(task)),
    [remediationTasks]
  );

  const closedRemediationTasks = useMemo(
    () => remediationTasks.filter((task) => isClosedTask(task)),
    [remediationTasks]
  );

  const approvedExceptionTasks = useMemo(() => {
    const frameworkValue = normalizeText(framework);
    return sortRemediationTasksNewestFirst(
      remediationTasks.filter((task) => {
        const frameworkMatches = !frameworkValue || normalizeText(task.framework) === frameworkValue;
        return frameworkMatches && isApprovedExceptionTask(task);
      })
    );
  }, [framework, remediationTasks]);

const validationFailedTasks = useMemo(() => {
    const frameworkValue = normalizeText(framework);
    return sortRemediationTasksNewestFirst(
      remediationTasks.filter((task) => {
        const frameworkMatches = !frameworkValue || normalizeText(task.framework) === frameworkValue;
        const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
        return frameworkMatches && validationStatus === "failed" && !isClosedTask(task);
      })
    );
  }, [framework, remediationTasks]);

  const inProgressTasks = useMemo(() => {
    const frameworkValue = normalizeText(framework);
    return sortRemediationTasksNewestFirst(
      remediationTasks.filter((task) => {
        const frameworkMatches = !frameworkValue || normalizeText(task.framework) === frameworkValue;
        return frameworkMatches && !isClosedTask(task) && normalizeText(task.status) === "in_progress";
      })
    );
  }, [framework, remediationTasks]);

  const overdueTasks = useMemo(() => {
    const frameworkValue = normalizeText(framework);
    return sortRemediationTasksNewestFirst(
      remediationTasks.filter((task) => {
        const frameworkMatches = !frameworkValue || normalizeText(task.framework) === frameworkValue;
        return frameworkMatches && !isClosedTask(task) && isTaskOverdue(task);
      })
    );
  }, [framework, remediationTasks]);

  const allActiveTasks = useMemo(() => {
    const frameworkValue = normalizeText(framework);
    return sortRemediationTasksNewestFirst(
      remediationTasks.filter((task) => {
        const frameworkMatches = !frameworkValue || normalizeText(task.framework) === frameworkValue;
        return frameworkMatches && !isClosedTask(task) && !isApprovedExceptionTask(task);
      })
    );
  }, [framework, remediationTasks]);

  const handleOpenAllActiveTasksModal = useCallback(async () => {
    setShowAllActiveTasksModal(true);
    setAllActiveModalLoading(true);
    setAllActiveModalError("");

    try {
      const token = await fetchToken();
      const payload = await fetchRemediationOperationsQueue({
        getJsonWithToken,
        token,
        framework,
        includeClosed: false,
        limit: 200,
        offset: 0,
      });

      const loadedTasks = normalizeRemediationTasks(payload).filter(
        (task) => !isClosedTask(task) && !isApprovedExceptionTask(task)
      );
      setAllActiveModalTasks(loadedTasks);
      setAllActiveModalTotal(safeNumber(payload?.total, loadedTasks.length));
    } catch (error) {
      console.error("Failed to load all active remediation modal tasks", error);
      setAllActiveModalError(
        String(error?.message || error || "Unable to load the full active remediation queue.")
      );
      setAllActiveModalTasks(allActiveTasks);
      setAllActiveModalTotal(allActiveTasks.length);
    } finally {
      setAllActiveModalLoading(false);
    }
  }, [allActiveTasks, fetchToken, framework, getJsonWithToken]);

  const allActiveModalVisibleTasks = useMemo(() => {
    return allActiveModalTasks.length > 0 ? allActiveModalTasks : allActiveTasks;
  }, [allActiveModalTasks, allActiveTasks]);


  const getQueueStatusTasksForType = useCallback(
    (sourceTasks = [], queueType = "open") => {
      const frameworkValue = normalizeText(framework);
      const normalizedQueueType = normalizeRouteQueue(queueType) || "open";
      const selectedQueueType = normalizedQueueType === "ready" ? "ready_for_validation" : normalizedQueueType;

      return sortRemediationTasksNewestFirst(
        (Array.isArray(sourceTasks) ? sourceTasks : []).filter((task) => {
          const frameworkMatches = !frameworkValue || normalizeText(task.framework) === frameworkValue;
          if (!frameworkMatches || isClosedTask(task) || isApprovedExceptionTask(task)) return false;

          const status = normalizeText(task.status || task.task_status || "open");
          const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
          const owner = cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "");
          const isUnassigned = !owner || normalizeText(owner) === "unassigned";
          const validationFailed = validationStatus === "failed";
          const overdue = isTaskOverdue(task);

          // Match the Work Queue Snapshot buckets exactly: these three modals
          // should not steal work from the dedicated Validation Failed or
          // Overdue modals.
          if (validationFailed || overdue) return false;

          if (selectedQueueType === "ready_for_validation") {
            return status === "ready_for_validation";
          }

          if (selectedQueueType === "unassigned") {
            return isUnassigned;
          }

          return status === "open" || status === "assigned" || status === "deferred";
        })
      );
    },
    [framework]
  );

  const queueStatusFallbackTasks = useMemo(() => {
    return getQueueStatusTasksForType(remediationTasks, queueStatusModalType);
  }, [getQueueStatusTasksForType, queueStatusModalType, remediationTasks]);

  const handleOpenQueueStatusTasksModal = useCallback(async (queueType = "open", options = {}) => {
    const normalizedQueueType = normalizeRouteQueue(queueType) || "open";
    const selectedQueueType = normalizedQueueType === "ready" ? "ready_for_validation" : normalizedQueueType;
    const expectedTotal = coalesceNumber(options?.expectedTotal, null);

    setQueueStatusModalType(selectedQueueType);
    setShowQueueStatusTasksModal(true);
    setQueueStatusModalLoading(true);
    setQueueStatusModalError("");
    setQueueStatusModalTasks([]);
    setQueueStatusModalTotal(Number.isFinite(expectedTotal) ? expectedTotal : 0);

    if (Number.isFinite(expectedTotal) && expectedTotal <= 0) {
      setQueueStatusModalLoading(false);
      return;
    }

    const fallbackForSelectedQueue = getQueueStatusTasksForType(remediationTasks, selectedQueueType);

    try {
      const token = await fetchToken();
      const queueParams =
        selectedQueueType === "ready_for_validation"
          ? { queue: "ready_for_validation" }
          : selectedQueueType === "unassigned"
          ? { assignmentState: "unassigned" }
          : { queue: "open" };

      const payload = await fetchRemediationOperationsQueue({
        getJsonWithToken,
        token,
        framework,
        includeClosed: false,
        limit: 200,
        offset: 0,
        ...queueParams,
      });

      const loadedTasks = getQueueStatusTasksForType(normalizeRemediationTasks(payload), selectedQueueType);
      const visibleTasks = loadedTasks.length > 0 ? loadedTasks : fallbackForSelectedQueue;

      setQueueStatusModalTasks(visibleTasks);
      setQueueStatusModalTotal(
        Number.isFinite(expectedTotal)
          ? expectedTotal
          : safeNumber(payload?.total, visibleTasks.length)
      );
    } catch (error) {
      console.error("Failed to load queue status remediation modal tasks", error);
      setQueueStatusModalError(
        String(error?.message || error || "Unable to load the selected remediation queue.")
      );
      setQueueStatusModalTasks(fallbackForSelectedQueue);
      setQueueStatusModalTotal(Number.isFinite(expectedTotal) ? expectedTotal : fallbackForSelectedQueue.length);
    } finally {
      setQueueStatusModalLoading(false);
    }
  }, [fetchToken, framework, getJsonWithToken, getQueueStatusTasksForType, remediationTasks]);

  const queueStatusModalVisibleTasks = useMemo(() => {
    if (queueStatusModalTasks.length > 0) return queueStatusModalTasks;
    if (queueStatusModalTotal === 0) return [];
    return queueStatusFallbackTasks;
  }, [queueStatusFallbackTasks, queueStatusModalTasks, queueStatusModalTotal]);

  const activeWorkQueueSnapshotKey = useMemo(() => {
    if (showAllActiveTasksModal) return "all";
    if (showQueueStatusTasksModal) {
      return queueStatusModalType === "ready" ? "ready_for_validation" : queueStatusModalType;
    }
    if (showInProgressTasksModal) return "in_progress";
    if (showValidationFailedModal) return "validation_failed";
    if (showOverdueTasksModal) return "overdue";
    if (showApprovedExceptionsModal) return "approved_exception";
    if (showClosedTasksModal) return "closed";
    return "";
  }, [
    queueStatusModalType,
    showAllActiveTasksModal,
    showApprovedExceptionsModal,
    showClosedTasksModal,
    showInProgressTasksModal,
    showOverdueTasksModal,
    showQueueStatusTasksModal,
    showValidationFailedModal,
  ]);

  const handleOpenOverdueTasksModal = useCallback(async () => {
    setShowOverdueTasksModal(true);
    setOverdueModalLoading(true);
    setOverdueModalError("");

    try {
      const token = await fetchToken();
      const payload = await fetchRemediationOperationsQueue({
        getJsonWithToken,
        token,
        framework,
        includeClosed: false,
        limit: 200,
        offset: 0,
        overdue: true,
      });

      const loadedTasks = normalizeRemediationTasks(payload);
      setOverdueModalTasks(loadedTasks);
      setOverdueModalTotal(safeNumber(payload?.total, loadedTasks.length));
    } catch (error) {
      console.error("Failed to load overdue remediation modal tasks", error);
      setOverdueModalError(
        String(error?.message || error || "Unable to load the full overdue remediation queue.")
      );
      setOverdueModalTasks(overdueTasks);
      setOverdueModalTotal(overdueTasks.length);
    } finally {
      setOverdueModalLoading(false);
    }
  }, [fetchToken, framework, getJsonWithToken, overdueTasks]);

  const overdueModalVisibleTasks = useMemo(() => {
    return overdueModalTasks.length > 0 ? overdueModalTasks : overdueTasks;
  }, [overdueModalTasks, overdueTasks]);

  const handleOpenInProgressTasksModal = useCallback(async () => {
    setShowInProgressTasksModal(true);
    setInProgressModalLoading(true);
    setInProgressModalError("");

    try {
      const token = await fetchToken();
      const payload = await fetchRemediationOperationsQueue({
        getJsonWithToken,
        token,
        framework,
        includeClosed: false,
        limit: 200,
        offset: 0,
        queue: "in_progress",
      });

      const loadedTasks = normalizeRemediationTasks(payload);
      setInProgressModalTasks(loadedTasks);
      setInProgressModalTotal(safeNumber(payload?.total, loadedTasks.length));
    } catch (error) {
      console.error("Failed to load in-progress remediation modal tasks", error);
      setInProgressModalError(
        String(error?.message || error || "Unable to load the full in-progress remediation queue.")
      );
      setInProgressModalTasks(inProgressTasks);
      setInProgressModalTotal(inProgressTasks.length);
    } finally {
      setInProgressModalLoading(false);
    }
  }, [fetchToken, framework, getJsonWithToken, inProgressTasks]);

  const inProgressModalVisibleTasks = useMemo(() => {
    return inProgressModalTasks.length > 0 ? inProgressModalTasks : inProgressTasks;
  }, [inProgressModalTasks, inProgressTasks]);

  const handleOpenClosedTasksModal = useCallback(async () => {
    setShowClosedTasksModal(true);
    setClosedModalLoading(true);
    setClosedModalError("");

    try {
      const token = await fetchToken();
      const payload = await fetchRemediationOperationsQueue({
        getJsonWithToken,
        token,
        framework,
        includeClosed: true,
        limit: 200,
        offset: 0,
        queue: "closed",
        status: "closed",
      });

      const loadedTasks = normalizeRemediationTasks(payload);
      setClosedModalTasks(loadedTasks);
      setClosedModalTotal(safeNumber(payload?.total, loadedTasks.length));
    } catch (error) {
      console.error("Failed to load closed remediation modal tasks", error);
      setClosedModalError(
        String(error?.message || error || "Unable to load the full closed remediation queue.")
      );
      setClosedModalTasks(closedRemediationTasks);
      setClosedModalTotal(closedRemediationTasks.length);
    } finally {
      setClosedModalLoading(false);
    }
  }, [closedRemediationTasks, fetchToken, framework, getJsonWithToken]);

  const closedModalVisibleTasks = useMemo(() => {
    return closedModalTasks.length > 0 ? closedModalTasks : closedRemediationTasks;
  }, [closedModalTasks, closedRemediationTasks]);

  const displayedRemediationTasks = useMemo(() => {
    const sourceTasks = showClosedTasks ? closedRemediationTasks : activeRemediationTasks;
    return sortRemediationTasksNewestFirst(sourceTasks);
  }, [activeRemediationTasks, closedRemediationTasks, showClosedTasks]);

  const existingFixPlanTask = useMemo(
    () => findTaskForItem(remediationTasks, fixPlanItem || {}, framework),
    [remediationTasks, fixPlanItem, framework]
  );

  const visibleModalTask = createdRemediationTask || existingFixPlanTask;

  const enrichedFixPlanItem = useMemo(() => {
    if (!fixPlanItem) return null;

    const matchingRisk =
      findTopRiskContextForFixPlan(topRisks, fixPlanItem, framework) ||
      findTopRiskContextForFixPlan(normalizeTopRisks(overview?.top_risks), fixPlanItem, framework);

    return mergeFixPlanWithSourceContext(fixPlanItem, matchingRisk);
  }, [fixPlanItem, framework, overview, topRisks]);

  const fixPlanAwsContext = useMemo(
    () => getAwsEvidenceContext(enrichedFixPlanItem || fixPlanItem || {}, visibleModalTask || {}),
    [enrichedFixPlanItem, fixPlanItem, visibleModalTask]
  );

  const fixPlanRuleId = String((enrichedFixPlanItem || fixPlanItem)?.rule_id || "").toUpperCase();
  const isFixPlanInlinePolicy = fixPlanRuleId.includes("INLINE_POLICY_ADDED_EVENT");
  const isFixPlanCredentialReport = isIamCredentialReportRule(fixPlanRuleId);
  const isFixPlanAccessKey = isIamAccessKeyRule(fixPlanRuleId);
  const isFixPlanActiveAccessKey = isFixPlanAccessKey && isCredentialActive(fixPlanAwsContext?.accessKeyActive);

  const fixPlanAwsResourceCopy = isFixPlanAccessKey
    ? "OASYS identified the exact IAM user and access key slot tied to this risk, so remediation can be completed without guessing."
    : isFixPlanCredentialReport
    ? "OASYS identified the exact IAM user and credential condition tied to this risk, so remediation can be completed without guessing."
    : isFixPlanInlinePolicy
    ? "OASYS identified the exact IAM identity and inline policy tied to this risk, so remediation can be completed without guessing."
    : normalizeText(visibleModalTask?.validation_status) === "passed"
    ? "OASYS identified the exact AWS role and policy tied to this risk and confirmed the policy is no longer attached."
    : "OASYS identified the exact AWS role and policy tied to this risk, so remediation can be completed without guessing.";

  const fixPlanIdentityLabel = isFixPlanCredentialReport
    ? "IAM User"
    : isFixPlanInlinePolicy
    ? "IAM Identity"
    : "Role";

  const fixPlanPolicyLabel = isFixPlanAccessKey
    ? "Access Key Slot"
    : isFixPlanCredentialReport
    ? "Credential"
    : isFixPlanInlinePolicy
    ? "Inline Policy"
    : "Policy";

  const fixPlanPolicyValue = isFixPlanAccessKey
    ? getAccessKeySlotLabel(fixPlanAwsContext?.accessKeySlot, fixPlanRuleId)
    : isFixPlanCredentialReport
    ? fixPlanAwsContext?.credentialIssue
    : fixPlanAwsContext?.policyName || fixPlanAwsContext?.policyArn;

  const fixPlanEventLabel = isFixPlanCredentialReport ? "Credential Issue" : "CloudTrail Event";
  const fixPlanEventValue = isFixPlanCredentialReport
    ? fixPlanAwsContext?.credentialIssue
    : fixPlanAwsContext?.eventName || (isFixPlanInlinePolicy ? "PutRolePolicy / PutUserPolicy" : "AttachRolePolicy");

  const fixPlanPassedEvidenceText = isFixPlanAccessKey
    ? "Latest IAM inventory shows this access key condition has been remediated."
    : isFixPlanInlinePolicy
    ? "Latest IAM inventory shows this inline policy is no longer present on the identity."
    : "Latest IAM inventory shows this policy is no longer attached to the role.";

  const fixPlanSteps = useMemo(
    () => buildFixPlanSteps(enrichedFixPlanItem || fixPlanItem || {}, fixPlanRelatedSops, visibleModalTask || {}),
    [enrichedFixPlanItem, fixPlanItem, fixPlanRelatedSops, visibleModalTask]
  );

  const fixPlanValidation = useMemo(
    () => buildValidationText(enrichedFixPlanItem || fixPlanItem || {}),
    [enrichedFixPlanItem, fixPlanItem]
  );

  const fixPlanRulePlanDiagnostic = useMemo(
    () =>
      getRulePlanDiagnostic(
        enrichedFixPlanItem || fixPlanItem || {},
        fixPlanRelatedSops,
        visibleModalTask || {}
      ),
    [enrichedFixPlanItem, fixPlanItem, fixPlanRelatedSops, visibleModalTask]
  );

  const remediationTaskSummary = useMemo(() => {
    const open = activeRemediationTasks.filter((task) => normalizeText(task.status) === "open").length;
    const inProgress = activeRemediationTasks.filter((task) => normalizeText(task.status) === "in_progress").length;
    const ready = activeRemediationTasks.filter((task) => normalizeText(task.status) === "ready_for_validation").length;
    const blocked = activeRemediationTasks.filter((task) => normalizeText(task.status) === "blocked").length;
    const validated = activeRemediationTasks.filter((task) => normalizeText(task.status) === "validated").length;
    const overdue = activeRemediationTasks.filter((task) => isTaskOverdue(task)).length;

    const closed = closedRemediationTasks.length;

    return {
      open,
      inProgress,
      ready,
      blocked,
      validated,
      overdue,
      closed,
      totalActive: activeRemediationTasks.length,
      totalDisplayed: displayedRemediationTasks.length,
    };
  }, [activeRemediationTasks, closedRemediationTasks, displayedRemediationTasks]);



  const rulePlanCoverageSummary = useMemo(() => {
    const frameworkValue = normalizeText(framework);
    const frameworkMatches = (item = {}) =>
      !frameworkValue || normalizeText(item.framework) === frameworkValue;

    const coverageItems = [
      ...(Array.isArray(filteredControlStatusRows) ? filteredControlStatusRows : []),
      ...(Array.isArray(topRisks) ? topRisks.filter(frameworkMatches) : []),
      ...(Array.isArray(riskQueue) ? riskQueue.filter(frameworkMatches) : []),
      ...(Array.isArray(staleControls) ? staleControls.filter(frameworkMatches) : []),
      ...(Array.isArray(activeRemediationTasks) ? activeRemediationTasks.filter(frameworkMatches) : []),
    ];

    return buildRulePlanCoverageSummary(coverageItems);
  }, [activeRemediationTasks, filteredControlStatusRows, framework, riskQueue, staleControls, topRisks]);

  const topRiskPreviewLimit = 5;

  const visibleTopRisks = useMemo(() => {
    if (!Array.isArray(topRisks)) return [];
    return topRisks.slice(0, topRiskPreviewLimit);
  }, [topRisks]);

  const topRiskPreviewKeys = useMemo(() => {
    return new Set(
      visibleTopRisks.map((risk = {}) =>
        [
          cleanValue(risk.framework || framework, ""),
          cleanValue(risk.control_id || risk.controlId, ""),
          cleanValue(risk.rule_id || risk.ruleId, ""),
          cleanValue(risk.resource_id || risk.resourceId, ""),
        ]
          .map((value) => normalizeText(value))
          .join("::")
      )
    );
  }, [framework, visibleTopRisks]);

  const activeRiskTotal = Math.max(
    safeNumber(heroMetrics.failingControls),
    safeNumber(topRisks?.length),
    safeNumber(riskQueue?.length)
  );
  const visibleTopRiskCount = Math.min(visibleTopRisks.length, activeRiskTotal);
  const remainingTopRiskCount = Math.max(0, activeRiskTotal - visibleTopRiskCount);

  const identityRiskRows = useMemo(() => {
    const rows = Array.isArray(identityFindings) ? identityFindings : [];

    return rows
      .map((finding) => buildIdentityFindingPostureItem(finding, framework))
      .sort((a, b) => {
        const aFailed = ["fail", "failed", "error"].includes(normalizeText(a.state));
        const bFailed = ["fail", "failed", "error"].includes(normalizeText(b.state));
        if (aFailed !== bFailed) return aFailed ? -1 : 1;
        return String(a.rule_id || "").localeCompare(String(b.rule_id || ""));
      });
  }, [framework, identityFindings]);

  const identityRiskSummary = useMemo(() => {
    const rows = Array.isArray(identityRiskRows) ? identityRiskRows : [];

    return rows.reduce(
      (acc, row = {}) => {
        const state = normalizeText(row.state || row.status || "");
        const source = normalizeText(row.source || row.provider || "");
        const bucket = getIdentityRiskBucket(row);

        acc.total += 1;

        if (state === "pass" || state === "passed") {
          acc.passing += 1;
        } else if (["fail", "failed", "error"].includes(state)) {
          acc.failed += 1;
        }

        if (source === "google_workspace") acc.googleWorkspace += 1;
        if (source === "okta") acc.okta += 1;
        if (bucket === "mfa") acc.mfa += 1;
        if (bucket === "admin") acc.admin += 1;
        if (bucket === "lifecycle") acc.lifecycle += 1;

        return acc;
      },
      {
        total: 0,
        passing: 0,
        failed: 0,
        googleWorkspace: 0,
        okta: 0,
        mfa: 0,
        admin: 0,
        lifecycle: 0,
      }
    );
  }, [identityRiskRows]);

  const visibleIdentityRiskRows = useMemo(() => {
    if (!Array.isArray(identityRiskRows)) return [];
    return identityRiskRows.slice(0, 5);
  }, [identityRiskRows]);

  const codeEdgeRiskRows = useMemo(() => {
    const rows = Array.isArray(codeEdgeFindings) ? codeEdgeFindings : [];

    return rows
      .map((finding) => buildCodeEdgeFindingPostureItem(finding, framework))
      .sort((a, b) => {
        const aFailed = ["fail", "failed", "error"].includes(normalizeText(a.state));
        const bFailed = ["fail", "failed", "error"].includes(normalizeText(b.state));
        if (aFailed !== bFailed) return aFailed ? -1 : 1;

        const aUnknown = normalizeText(a.state) === "unknown";
        const bUnknown = normalizeText(b.state) === "unknown";
        if (aUnknown !== bUnknown) return aUnknown ? -1 : 1;

        return String(a.rule_id || "").localeCompare(String(b.rule_id || ""));
      });
  }, [codeEdgeFindings, framework]);

  const remainingRiskRows = useMemo(() => {
    const loadedRiskQueueRemainder = (Array.isArray(riskQueue) ? riskQueue : [])
      .filter((row = {}) => {
        const rowKey = [
          cleanValue(row.framework || framework, ""),
          cleanValue(row.control_id || row.controlId, ""),
          cleanValue(row.rule_id || row.ruleId, ""),
          cleanValue(row.resource_id || row.resourceId, ""),
        ]
          .map((value) => normalizeText(value))
          .join("::");

        return !topRiskPreviewKeys.has(rowKey);
      });

    if (loadedRiskQueueRemainder.length > 0) {
      return loadedRiskQueueRemainder;
    }

    const loadedTopRiskRemainder = Array.isArray(topRisks)
      ? topRisks.slice(topRiskPreviewLimit)
      : [];

    if (loadedTopRiskRemainder.length > 0) {
      return loadedTopRiskRemainder;
    }

    return (Array.isArray(filteredControlStatusRows) ? filteredControlStatusRows : [])
      .filter((row = {}) => {
        const state = normalizeText(row.state || row.status || row.control_state || "");
        const openFindings = safeNumber(row.open_findings_total || row.findings_open || row.open_findings);
        const rowKey = [
          cleanValue(row.framework || framework, ""),
          cleanValue(row.control_id || row.controlId, ""),
          cleanValue(row.rule_id || row.ruleId, ""),
          cleanValue(row.resource_id || row.resourceId, ""),
        ]
          .map((value) => normalizeText(value))
          .join("::");

        const isNonPassing =
          openFindings > 0 ||
          ["fail", "failed", "error", "non_pass", "non-passing"].includes(state);

        return isNonPassing && !topRiskPreviewKeys.has(rowKey);
      })
      .map((row = {}) => ({
        ...row,
        framework: row.framework || framework,
        reason_summary:
          row.reason_summary ||
          `${buildControlLabel(row.control_id, row.rule_id)} is currently ${getDisplayStateLabel(row.state || row.status || row.control_state || "non-passing")}.`,
        remediation_hint:
          row.remediation_hint ||
          "Review the affected resource in the source system and open a Fix Plan for guided remediation.",
      }))
      .slice(0, remainingTopRiskCount);
  }, [filteredControlStatusRows, framework, remainingTopRiskCount, riskQueue, topRiskPreviewKeys, topRisks]);

  const loadedRemainingRiskCount = remainingRiskRows.length;

  const activeRiskQueueRows = useMemo(() => {
    const seen = new Set();
    const rows = [];

    [...visibleTopRisks, ...identityRiskRows, ...codeEdgeRiskRows, ...remainingRiskRows].forEach((row = {}, index) => {
      const rowKey = [
        cleanValue(row.framework || framework, ""),
        cleanValue(row.control_id || row.controlId, ""),
        cleanValue(row.rule_id || row.ruleId, ""),
        cleanValue(row.resource_id || row.resourceId, ""),
      ]
        .map((value) => normalizeText(value))
        .join("::") || `risk-row-${index}`;

      if (seen.has(rowKey)) return;
      seen.add(rowKey);
      rows.push(row);
    });

    return rows;
  }, [codeEdgeRiskRows, framework, identityRiskRows, remainingRiskRows, visibleTopRisks]);

  const loadedActiveRiskQueueCount = activeRiskQueueRows.length;

  const riskQueueFilterCounts = useMemo(() => {
    const rows = Array.isArray(activeRiskQueueRows) ? activeRiskQueueRows : [];

    return RISK_QUEUE_FILTERS.reduce((acc, filter) => {
      acc[filter.key] = rows.filter((row) => {
        const linkedTask = findTaskForItem(activeRemediationTasks, row, framework);

        return riskMatchesQueueFilterWithEc2(row, filter.key, {
          linkedTask,
          hasTask: Boolean(linkedTask),
        });
      }).length;
      return acc;
    }, {});
  }, [activeRemediationTasks, activeRiskQueueRows, framework]);

  const filteredRiskQueueRowsForActiveFilter = useMemo(() => {
    const rows = Array.isArray(activeRiskQueueRows) ? activeRiskQueueRows : [];

    return rows.filter((row) => {
      const linkedTask = findTaskForItem(activeRemediationTasks, row, framework);

      return riskMatchesQueueFilterWithEc2(row, riskQueueFilter, {
        linkedTask,
        hasTask: Boolean(linkedTask),
      });
    });
  }, [activeRemediationTasks, activeRiskQueueRows, framework, riskQueueFilter]);

  const filteredTopRiskRows = useMemo(() => {
    const rows = Array.isArray(filteredRiskQueueRowsForActiveFilter)
      ? [...filteredRiskQueueRowsForActiveFilter]
      : [];

    return rows
      .sort((a = {}, b = {}) => {
        const stateRank = (row = {}) => {
          const state = normalizeText(row.state || row.status || row.control_state || "");
          if (["fail", "failed", "error"].includes(state)) return 3;
          if (["unknown", "warning", "warn"].includes(state)) return 2;
          if (["pass", "passed"].includes(state)) return 0;
          return 1;
        };

        const severityRank = (row = {}) => {
          const severityWeight = safeNumber(row.severity_weight || row.severityWeight || 0);
          const severityText = normalizeText(row.severity || row.severity_label || row.severityLabel || "");
          if (severityWeight > 0) return severityWeight;
          if (severityText.includes("critical")) return 4;
          if (severityText.includes("high")) return 3;
          if (severityText.includes("medium")) return 2;
          if (severityText.includes("low")) return 1;
          return 0;
        };

        const scoreRank = (row = {}) => safeNumber(row.score || row.risk_score || row.weighted_score || 0);
        const noTaskRank = (row = {}) => {
          const linkedTask = findTaskForItem(activeRemediationTasks, row, framework);
          return linkedTask ? 0 : 1;
        };

        return (
          stateRank(b) - stateRank(a) ||
          severityRank(b) - severityRank(a) ||
          scoreRank(b) - scoreRank(a) ||
          noTaskRank(b) - noTaskRank(a)
        );
      })
      .slice(0, 5);
  }, [activeRemediationTasks, filteredRiskQueueRowsForActiveFilter, framework]);

  const filteredRemainingRiskRows = useMemo(() => {
    const topKeys = new Set(
      (Array.isArray(filteredTopRiskRows) ? filteredTopRiskRows : []).map((row = {}, index) =>
        [
          cleanValue(row.framework || framework, ""),
          cleanValue(row.control_id || row.controlId, ""),
          cleanValue(row.rule_id || row.ruleId, ""),
          cleanValue(row.resource_id || row.resourceId, ""),
        ]
          .map((value) => normalizeText(value))
          .join("::") || `top-risk-${index}`
      )
    );

    return (Array.isArray(filteredRiskQueueRowsForActiveFilter)
      ? filteredRiskQueueRowsForActiveFilter
      : []
    ).filter((row = {}, index) => {
      const rowKey =
        [
          cleanValue(row.framework || framework, ""),
          cleanValue(row.control_id || row.controlId, ""),
          cleanValue(row.rule_id || row.ruleId, ""),
          cleanValue(row.resource_id || row.resourceId, ""),
        ]
          .map((value) => normalizeText(value))
          .join("::") || `remaining-risk-${index}`;

      return !topKeys.has(rowKey);
    });
  }, [filteredRiskQueueRowsForActiveFilter, filteredTopRiskRows, framework]);

  const filteredActiveRiskQueueRows = useMemo(() => {
    return [...filteredTopRiskRows, ...filteredRemainingRiskRows];
  }, [filteredRemainingRiskRows, filteredTopRiskRows]);

  const filteredActiveRiskQueueEntries = useMemo(() => {
    return [
      ...filteredTopRiskRows.map((risk, index) => ({ risk, index, section: "top" })),
      ...filteredRemainingRiskRows.map((risk, index) => ({ risk, index, section: "remaining" })),
    ];
  }, [filteredRemainingRiskRows, filteredTopRiskRows]);

  const activeRiskQueueFilterLabel = useMemo(() => {
    return RISK_QUEUE_FILTERS.find((filter) => filter.key === riskQueueFilter)?.label || "All";
  }, [riskQueueFilter]);

  const getRemainingRiskSelectionKey = useCallback(
    (risk = {}, fallbackIndex = "") =>
      [
        cleanValue(risk.framework || framework, ""),
        cleanValue(risk.control_id || risk.controlId, ""),
        cleanValue(risk.rule_id || risk.ruleId, ""),
        cleanValue(risk.resource_id || risk.resourceId, ""),
        fallbackIndex,
      ]
        .map((value) => normalizeText(value))
        .join("::"),
    [framework]
  );

  const visibleNoTaskRemainingRiskRows = useMemo(() => {
    return filteredActiveRiskQueueEntries
      .map(({ risk, index, section }) => ({
        risk,
        index,
        section,
        key: getRemainingRiskSelectionKey(risk, `${section}-${index}`),
        linkedTask: findTaskForItem(activeRemediationTasks, risk, framework),
      }))
      .filter((item) => !item.linkedTask);
  }, [activeRemediationTasks, filteredActiveRiskQueueEntries, framework, getRemainingRiskSelectionKey]);

  const selectedNoTaskRemainingRiskRows = useMemo(() => {
    return visibleNoTaskRemainingRiskRows.filter((item) =>
      selectedRemainingRiskKeys.has(item.key)
    );
  }, [selectedRemainingRiskKeys, visibleNoTaskRemainingRiskRows]);

  const visibleNoTaskRiskCount = visibleNoTaskRemainingRiskRows.length;
  const selectedRemainingRiskCount = selectedNoTaskRemainingRiskRows.length;
  const allVisibleNoTaskRisksSelected =
    visibleNoTaskRiskCount > 0 &&
    visibleNoTaskRemainingRiskRows.every((item) => selectedRemainingRiskKeys.has(item.key));

  useEffect(() => {
    setSelectedRemainingRiskKeys(new Set());
    setRemainingRiskBulkError("");
  }, [framework, riskQueueFilter, showRemainingRisksModal]);

  useEffect(() => {
    if (showRemainingRisksModal) return undefined;

    setRemainingRiskBulkNotice("");
    setRemainingRiskBulkError("");
    setSelectedRemainingRiskKeys(new Set());

    return undefined;
  }, [showRemainingRisksModal]);

  useEffect(() => {
    if (!remainingRiskBulkNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setRemainingRiskBulkNotice("");
    }, SUCCESS_NOTICE_AUTO_DISMISS_MS);

    return () => window.clearTimeout(timeoutId);
  }, [remainingRiskBulkNotice]);

  const toggleRemainingRiskSelected = useCallback((riskKey) => {
    if (!riskKey) return;

    setSelectedRemainingRiskKeys((current) => {
      const next = new Set(current);
      if (next.has(riskKey)) next.delete(riskKey);
      else next.add(riskKey);
      return next;
    });
    setRemainingRiskBulkError("");
  }, []);

  const selectVisibleNoTaskRisks = useCallback(() => {
    setSelectedRemainingRiskKeys((current) => {
      const next = new Set(current);
      const shouldClear =
        visibleNoTaskRemainingRiskRows.length > 0 &&
        visibleNoTaskRemainingRiskRows.every((item) => next.has(item.key));

      visibleNoTaskRemainingRiskRows.forEach((item) => {
        if (shouldClear) next.delete(item.key);
        else next.add(item.key);
      });

      return next;
    });
    setRemainingRiskBulkError("");
  }, [visibleNoTaskRemainingRiskRows]);

  const clearRemainingRiskSelection = useCallback(() => {
    setSelectedRemainingRiskKeys(new Set());
    setRemainingRiskBulkNotice("");
    setRemainingRiskBulkError("");
  }, []);

  const handleBulkCreateRemainingRiskTasks = useCallback(async () => {
    setRemainingRiskBulkNotice("");
    setRemainingRiskBulkError("");

    if (selectedNoTaskRemainingRiskRows.length === 0) {
      setRemainingRiskBulkError("Select one or more No Task risks first.");
      return;
    }

    setRemainingRiskBulkCreating(true);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("bulk-create-remediation-tasks-timeout"),
      REQUEST_TIMEOUT_MS
    );

    try {
      const token = await fetchToken();
      const risks = selectedNoTaskRemainingRiskRows.map(({ risk }) => {
        const controlId = cleanValue(risk.control_id || risk.controlId, "");
        const ruleId = cleanValue(risk.rule_id || risk.ruleId, "");
        const resourceId = cleanValue(risk.resource_id || risk.resourceId, "");
        const taskTitle = `Remediate ${buildControlLabel(controlId, ruleId)}${resourceId ? ` for ${resourceId}` : ""}`;

        return {
          framework: risk.framework || framework,
          control_id: controlId || undefined,
          rule_id: ruleId || undefined,
          resource_id: resourceId || undefined,
          source: "risk_queue",
          title: taskTitle,
          description:
            risk.reason_summary ||
            `${buildControlLabel(controlId, ruleId)} is currently failing${resourceId ? ` for ${resourceId}` : ""}.`,
          remediation_steps: [
            "Open the Fix Plan.",
            "Complete the source-system remediation or approved exception documentation.",
            "Move the task to Ready for Validation.",
            "Run Full Cloud Assessment to verify the condition is cleared.",
          ],
          task_metadata: {
            created_from: "remaining_active_risks_modal",
            risk_queue_filter: riskQueueFilter,
            risk_family: getRiskQueueFamily(risk),
            remediation_hint: risk.remediation_hint || null,
            reason_summary: risk.reason_summary || null,
          },
        };
      });

      const payload = {
        risks,
        owner_user_id: userId || undefined,
        owner_name: userName || undefined,
        priority: 100,
      };

      const result = await postJsonWithToken(
        token,
        "/remediation/tasks/bulk-create",
        payload,
        controller.signal
      );

      const refreshedTasks = await getJsonWithToken(
        token,
        "/remediation/tasks",
        { framework, include_closed: true, limit: 100 },
        controller.signal
      );

      setRemediationTasks((current) =>
        mergeTaskCollectionsPreservingApprovedExceptions(
          current,
          normalizeRemediationTasks(refreshedTasks)
        )
      );
      setSelectedRemainingRiskKeys(new Set());
      setRiskQueueFilter("has_task");
      setAssessmentRefreshTick((value) => value + 1);

      setRemainingRiskBulkNotice(
        `Bulk task creation complete: ${safeNumber(result?.created_count, 0)} created, ${safeNumber(result?.skipped_existing_count, 0)} skipped existing, ${safeNumber(result?.failed_count, 0)} failed.`
      );
    } catch (error) {
      console.error("Failed to bulk-create remediation tasks", error);
      setRemainingRiskBulkError(
        error?.name === "AbortError"
          ? `Bulk task creation timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
          : String(error?.message || error || "Unable to bulk-create remediation tasks.")
      );
    } finally {
      window.clearTimeout(timeoutId);
      setRemainingRiskBulkCreating(false);
    }
  }, [
    fetchToken,
    framework,
    getJsonWithToken,
    postJsonWithToken,
    riskQueueFilter,
    selectedNoTaskRemainingRiskRows,
    userId,
    userName,
  ]);

  const focusedTask = useMemo(() => {
    return findTaskForItem(activeRemediationTasks, focusedContext?.item || {}, framework);
  }, [activeRemediationTasks, focusedContext, framework]);

  useEffect(() => {
    const baseContext = {
      mode: "posture",
      page: "posture",
      surface: "posture_page",
      intelligence_mode: "page",
      framework,
    };

    const explicitTaskId = cleanValue(
      managedTask?.id || normalizeRouteParamValue(routeOpenTaskIdParam),
      ""
    );

    if (explicitTaskId) {
      setCopilotContext({
        ...baseContext,
        intelligence_mode: "contextual_explain",
        record_type: "remediation_task",
        task_id: explicitTaskId,
        focus_label: cleanValue(
          managedTask?.title,
          `Remediation task ${explicitTaskId}`
        ),
        focus_type: "Remediation Task",
      });
      return () => clearCopilotContext();
    }

    if (postureExplainOpen && postureExplainTarget?.control_id) {
      setCopilotContext({
        ...baseContext,
        intelligence_mode: "contextual_explain",
        record_type: "posture_control",
        framework: postureExplainTarget.framework || framework,
        control_id: postureExplainTarget.control_id,
        rule_id: postureExplainTarget.rule_id || undefined,
        resource_id: postureExplainTarget.resource_id || undefined,
        focus_label: buildControlLabel(
          postureExplainTarget.control_id,
          postureExplainTarget.rule_id
        ),
        focus_type: "Posture Control",
      });
      return () => clearCopilotContext();
    }

    if (hasExplicitPostureSelection && focusedContext?.item?.control_id) {
      const item = focusedContext.item;
      setCopilotContext({
        ...baseContext,
        intelligence_mode: "contextual_explain",
        record_type: "posture_control",
        framework: cleanValue(item.framework, framework),
        control_id: item.control_id,
        rule_id: cleanValue(item.rule_id, "") || undefined,
        resource_id: cleanValue(item.resource_id, "") || undefined,
        focus_label: buildControlLabel(item.control_id, item.rule_id),
        focus_type: "Posture Control",
      });
      return () => clearCopilotContext();
    }

    setCopilotContext(baseContext);
    return () => clearCopilotContext();
  }, [
    clearCopilotContext,
    focusedContext,
    framework,
    hasExplicitPostureSelection,
    managedTask,
    postureExplainOpen,
    postureExplainTarget,
    routeOpenTaskIdParam,
    setCopilotContext,
  ]);

  const guidanceModalTask = useMemo(() => {
    return findTaskForItem(activeRemediationTasks, guidanceModalItem || {}, framework);
  }, [activeRemediationTasks, framework, guidanceModalItem]);

  const focusedPrimarySop = useMemo(() => {
    return relatedSops?.[0] || null;
  }, [relatedSops]);

  const focusedRecommendedAction = useMemo(() => {
    if (recommendedActions.length > 0) return recommendedActions[0];

    const steps = buildFixPlanSteps(focusedContext?.item || {}, relatedSops);
    return steps?.[0] || "Open the Fix Plan to review the recommended remediation steps.";
  }, [focusedContext, recommendedActions, relatedSops]);

  const focusedSourceLabel = useMemo(() => {
    if (!focusedContext?.matched) return "Drift context";
    if (focusedContext.kind === "control") return "Control status";
    if (focusedContext.kind === "risk") return "Top risk";
    if (focusedContext.kind === "stale") return "Stale control";
    return "Posture item";
  }, [focusedContext]);

  const handleCreateRemediationTask = useCallback(async () => {
    if (!fixPlanItem) return;

    if (existingFixPlanTask) {
      setCreatedRemediationTask(existingFixPlanTask);
      setRemediationTaskNotice(`Existing remediation task found: ${existingFixPlanTask.id}`);
      return;
    }

    setRemediationTaskCreating(true);
    setRemediationTaskNotice("");
    setRemediationTaskError("");

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("create-remediation-task-timeout"),
      REQUEST_TIMEOUT_MS
    );

    try {
      const token = await fetchToken();
      const primarySopId =
        relatedSopRemediation?.remediation_card?.open_sop_id ||
        getRelatedSopId(fixPlanRelatedSops?.[0]);

      const controlId = cleanValue(fixPlanItem.control_id, "");
      const ruleId = cleanValue(fixPlanItem.rule_id, "");
      const taskTitle = `Remediate ${buildControlLabel(controlId, ruleId)}`;

      const payload = {
        framework: cleanValue(fixPlanItem.framework, framework),
        control_id: controlId,
        rule_id: ruleId || null,
        resource_id: cleanValue(fixPlanItem.resource_id, "") || null,
        source: fixPlanItem.source || "posture",
        title: taskTitle,
        description: fixPlanItem.reason_summary || fixPlanValidation,
        remediation_steps: fixPlanSteps,
        linked_sop_id: primarySopId || null,
        status: "open",
        priority:
          normalizeText(fixPlanItem.state).includes("fail") ||
          safeNumber(fixPlanItem.open_findings_total) > 0
            ? 50
            : 100,
        owner_user_id: userId || null,
        owner_name: userName || "",
        validation_status: "pending",
        last_assessment_job_id: lastSyncJobId || null,
        task_metadata: {
          created_from: "posture_fix_plan",
          source: fixPlanItem.source || "posture",
          validation: fixPlanValidation,
          related_sop_count: fixPlanRelatedSops.length,
          score: fixPlanItem.score ?? null,
          severity: fixPlanItem.severity ?? null,
          aws_context: fixPlanAwsContext || null,
        },
      };

      const response = await postJsonWithToken(
        token,
        "/remediation/tasks",
        payload,
        controller.signal
      );

      const task = response?.task || null;
      setCreatedRemediationTask(task);
      if (task) {
        setRemediationTasks((current) => {
          const existing = current.some((item) => item.id === task.id);
          return existing ? current : [task, ...current];
        });
      }
      setRemediationTaskNotice(
        task?.id
          ? `Remediation task created: ${task.id}`
          : "Remediation task created."
      );
    } catch (error) {
      console.error("Failed to create remediation task", error);
      setRemediationTaskError(
        error?.name === "AbortError"
          ? `Task creation timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
          : String(error?.message || error || "Unable to create remediation task.")
      );
    } finally {
      window.clearTimeout(timeoutId);
      setRemediationTaskCreating(false);
    }
  }, [
    existingFixPlanTask,
    fetchToken,
    fixPlanAwsContext,
    fixPlanItem,
    fixPlanRelatedSops,
    fixPlanSteps,
    fixPlanValidation,
    framework,
    lastSyncJobId,
    postJsonWithToken,
    relatedSopRemediation,
    userId,
    userName,
  ]);

  const handleLoadManagedTaskEvents = useCallback(
    async (taskId) => {
      if (!taskId) return;

      setManagedTaskEventsLoading(true);
      setManagedTaskEventsError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("remediation-task-events-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const payload = await getJsonWithToken(
          token,
          `/remediation/tasks/${taskId}/events`,
          { limit: 50 },
          controller.signal
        );

        setManagedTaskEvents(sortTaskEventsNewestFirst(normalizeRemediationTaskEvents(payload)));
      } catch (error) {
        console.error("Failed to load remediation task events", error);
        setManagedTaskEvents([]);
        setManagedTaskEventsError(
          error?.name === "AbortError"
            ? `Task audit trail timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to load task audit trail.")
        );
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskEventsLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const handleLoadManagedTaskValidationHistory = useCallback(
    async (taskId) => {
      if (!taskId) return;

      setManagedTaskValidationHistoryLoading(true);
      setManagedTaskValidationHistoryError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("remediation-task-validation-history-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const payload = await getJsonWithToken(
          token,
          `/remediation/tasks/${taskId}/validation-history`,
          { limit: 50 },
          controller.signal
        );

        setManagedTaskValidationHistory(payload || { validation_runs: [] });
      } catch (error) {
        console.error("Failed to load remediation task validation history", error);
        setManagedTaskValidationHistory([]);
        setManagedTaskValidationHistoryError(
          error?.name === "AbortError"
            ? `Task validation history timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to load task validation history.")
        );
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskValidationHistoryLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const handleLoadManagedTaskEvidence = useCallback(
    async (taskId) => {
      if (!taskId) return;

      setManagedTaskEvidenceLoading(true);
      setManagedTaskEvidenceError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("remediation-task-evidence-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const payload = await getJsonWithToken(
          token,
          `/remediation/tasks/${taskId}/evidence`,
          { limit: 50 },
          controller.signal
        );

        setManagedTaskEvidence(payload || { evidence: [] });
      } catch (error) {
        console.error("Failed to load remediation task evidence", error);
        setManagedTaskEvidence([]);
        setManagedTaskEvidenceError(
          error?.name === "AbortError"
            ? `Task evidence timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to load linked task evidence.")
        );
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskEvidenceLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const handleLoadManagedTaskClosureGuard = useCallback(
    async (taskId) => {
      if (!taskId) return null;

      setManagedTaskClosureGuardLoading(true);
      setManagedTaskClosureGuardError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("remediation-task-closure-guard-timeout"),
        CLOSURE_GUARD_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const payload = await fetchRemediationTaskClosureGuard({
          getJsonWithToken,
          token,
          taskId,
          signal: controller.signal,
        });

        setManagedTaskClosureGuard(payload || null);
        return payload || null;
      } catch (error) {
        console.error("Failed to load remediation task closure guard", error);
        setManagedTaskClosureGuardError(
          error?.name === "AbortError"
            ? `Task closure guard timed out after ${CLOSURE_GUARD_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to load validation closure guard.")
        );
        return null;
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskClosureGuardLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const handleCreateManagedTaskEvidence = useCallback(
    async (taskIdOrPayload, maybePayload = {}) => {
      const calledWithTaskId =
        typeof taskIdOrPayload === "string" || typeof taskIdOrPayload === "number";
      const taskId = calledWithTaskId
        ? String(taskIdOrPayload || "").trim()
        : String(managedTask?.id || "").trim();
      const payload = calledWithTaskId ? maybePayload || {} : taskIdOrPayload || {};

      if (!taskId) {
        setManagedTaskEvidenceCreateError("Open a remediation task before attaching evidence.");
        return null;
      }

      setManagedTaskEvidenceCreating(true);
      setManagedTaskEvidenceCreateError("");
      setManagedTaskEvidenceError("");
      setRemediationTaskNotice("");
      setRemediationTaskError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("create-remediation-task-evidence-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const evidenceType = cleanValue(payload.evidence_type || payload.evidenceType, "note");
        const filename = cleanValue(
          payload.filename || payload.label || payload.name,
          "manual-evidence"
        );
        const storagePath = cleanValue(
          payload.storage_path ||
            payload.storagePath ||
            payload.evidence_ref ||
            payload.evidenceRef ||
            payload.reference ||
            payload.url ||
            payload.ticket_url,
          ""
        );
        const normalizedEvidenceType = normalizeText(evidenceType || "note");
        const filenameSlug =
          normalizeText(filename || "evidence-reference")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "evidence-reference";
        const autoStoragePath = ["note", "screenshot", "log", "export", "other"].includes(
          normalizedEvidenceType
        )
          ? `manual://${normalizedEvidenceType}/${filenameSlug}`
          : "";

        const cleanPayload = {
          evidence_type: evidenceType,
          filename,
          storage_path: storagePath || autoStoragePath,
          sha256: cleanValue(payload.sha256 || payload.sha_256, null),
          linked_control_id: cleanValue(
            payload.linked_control_id || payload.linkedControlId,
            managedTask?.control_id || null
          ),
          linked_finding_id: cleanValue(
            payload.linked_finding_id || payload.linkedFindingId,
            null
          ),
          metadata: {
            ...(payload.metadata || {}),
            source: cleanValue(payload.metadata?.source, "remediation_task_modal"),
          },
        };

        if (!cleanPayload.storage_path) {
          throw new Error("Storage path, URL, ticket reference, or manual evidence reference is required.");
        }

        await postJsonWithToken(
          token,
          `/remediation/tasks/${taskId}/evidence`,
          cleanPayload,
          controller.signal
        );

        await Promise.allSettled([
          handleLoadManagedTaskEvidence(taskId),
          handleLoadManagedTaskEvents(taskId),
          handleLoadManagedTaskValidationHistory(taskId),
        ]);

        setRemediationTaskNotice("Evidence attached to remediation task.");
        return true;
      } catch (error) {
        console.error("Failed to attach remediation task evidence", error);
        const message =
          error?.name === "AbortError"
            ? `Evidence attachment timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to attach task evidence.");
        setManagedTaskEvidenceCreateError(message);
        return null;
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskEvidenceCreating(false);
      }
    },
    [
      fetchToken,
      handleLoadManagedTaskEvents,
      handleLoadManagedTaskEvidence,
      handleLoadManagedTaskValidationHistory,
      managedTask?.control_id,
      managedTask?.id,
      postJsonWithToken,
    ]
  );

  const handleUpdateRemediationTask = useCallback(
    async (task, updates, successMessage = "Remediation task updated.") => {
      if (!task?.id) return;

      setRemediationTaskUpdating(true);
      setRemediationTaskNotice("");
      setRemediationTaskError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("update-remediation-task-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const response = await patchJsonWithToken(
          token,
          `/remediation/tasks/${task.id}`,
          updates,
          controller.signal
        );

        const updatedTask = response?.task || null;

        if (updatedTask) {
          setCreatedRemediationTask((current) =>
            current?.id === updatedTask.id
              ? mergeTaskPreservingApprovedException(current, updatedTask)
              : updatedTask
          );
          setManagedTask((current) =>
            current?.id === updatedTask.id
              ? mergeTaskPreservingApprovedException(current, updatedTask)
              : current
          );
          setManagedTaskDraft((current) => {
            if (!current || current.id !== updatedTask.id) return current;
            return {
              id: updatedTask.id,
              status: normalizeText(updatedTask.status) || "open",
              owner_name: updatedTask.owner_name || "",
              due_date: formatDateInput(updatedTask.due_date),
              priority: String(updatedTask.priority || 100),
              block_reason: updatedTask.task_metadata?.block_reason || "",
              closure_reason: updatedTask.closure_reason || "",
            };
          });
          setManagedTaskInvestigationDraft((current) =>
            current && updatedTask.id === managedTask?.id
              ? buildInvestigationDraftFromTask(updatedTask)
              : current
          );
          setRemediationTasks((current) =>
            current.map((item) =>
              item.id === updatedTask.id
                ? mergeTaskPreservingApprovedException(item, updatedTask)
                : item
            )
          );

          if (isClosedTask(updatedTask)) {
            setShowClosedTasks(true);
            setFullAssessmentStatus("");
            setAssessmentRefreshNotice("Task closed. Posture refreshed and risk state updated.");
            setTopRisks((current) =>
              current.filter((risk) => !taskMatchesItem(updatedTask, risk, framework))
            );
            setFixPlanItem((current) => {
              if (!current || !taskMatchesItem(updatedTask, current, framework)) return current;
              return {
                ...current,
                state: "closed",
                validation_status: updatedTask.validation_status || "passed",
                open_findings_total: "—",
                task_status: updatedTask.status,
              };
            });
            setAssessmentRefreshTick((value) => value + 1);
            window.setTimeout(() => {
              setAssessmentRefreshTick((value) => value + 1);
            }, 900);
          }
        }

        if (updatedTask?.id && managedTask?.id === updatedTask.id) {
          await Promise.allSettled([
            handleLoadManagedTaskEvents(updatedTask.id),
            handleLoadManagedTaskValidationHistory(updatedTask.id),
            handleLoadManagedTaskEvidence(updatedTask.id),
            handleLoadManagedTaskClosureGuard(updatedTask.id),
          ]);
        }

        setRemediationTaskNotice(successMessage);
        return updatedTask;
      } catch (error) {
        console.error("Failed to update remediation task", error);
        setRemediationTaskError(
          error?.name === "AbortError"
            ? `Task update timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to update remediation task.")
        );
        return null;
      } finally {
        window.clearTimeout(timeoutId);
        setRemediationTaskUpdating(false);
      }
    },
    [
      fetchToken,
      framework,
      handleLoadManagedTaskEvents,
      handleLoadManagedTaskEvidence,
      handleLoadManagedTaskValidationHistory,
      handleLoadManagedTaskClosureGuard,
      managedTask,
      patchJsonWithToken,
    ]
  );

  const handleRemediationTaskUpdatedFromModal = useCallback((updatedTask) => {
    if (!updatedTask?.id) return;

    const updatedIsApprovedException = isApprovedExceptionTask(updatedTask);

    setCreatedRemediationTask((current) =>
      current?.id === updatedTask.id
        ? mergeTaskPreservingApprovedException(current, updatedTask)
        : updatedTask
    );
    setManagedTask((current) =>
      current?.id === updatedTask.id
        ? mergeTaskPreservingApprovedException(current, updatedTask)
        : current
    );
    setManagedTaskDraft((current) => {
      if (!current || current.id !== updatedTask.id) return current;
      return {
        id: updatedTask.id,
        status: normalizeText(updatedTask.status) || "open",
        owner_name: updatedTask.owner_name || "",
        due_date: formatDateInput(updatedTask.due_date),
        priority: String(updatedTask.priority || 100),
        block_reason: updatedTask.task_metadata?.block_reason || "",
        closure_reason: updatedTask.closure_reason || "",
      };
    });
    setManagedTaskInvestigationDraft((current) =>
      current && updatedTask.id === managedTask?.id
        ? buildInvestigationDraftFromTask(updatedTask)
        : current
    );
    setRemediationTasks((current) => {
      const exists = current.some((item) => item.id === updatedTask.id);
      if (!exists) return [updatedTask, ...current];
      return current.map((item) =>
        item.id === updatedTask.id
          ? mergeTaskPreservingApprovedException(item, updatedTask)
          : item
      );
    });
    setRemediationTaskNotice(
      updatedIsApprovedException
        ? "Approved exception recorded. Next step: mark the task Validated, then close it with a closure reason."
        : "Remediation task updated. Continue the workflow from Review / Manage."
    );
  }, [managedTask]);

  const handleOpenManageTask = useCallback((task, options = {}) => {
    if (!task?.id) return;

    const shouldReturnToRiskQueue = options?.returnTo === "risk_queue";
    const shouldReturnToAllActiveTasks = options?.returnTo === "all_active_tasks";
    const shouldReturnToQueueStatusTasks = options?.returnTo === "queue_status_tasks";
    const shouldReturnToActiveFindings = options?.returnTo === "active_findings";
    const shouldReturnToApprovedExceptions = options?.returnTo === "approved_exceptions";
    const shouldReturnToValidationFailed = options?.returnTo === "validation_failed";
    const shouldReturnToOverdueTasks = options?.returnTo === "overdue_tasks";
    const shouldReturnToInProgressTasks = options?.returnTo === "in_progress_tasks";
    const shouldReturnToClosedTasks = options?.returnTo === "closed_tasks";
    const shouldReturnToPostureStatus = options?.returnTo === "posture_status";
    const shouldReturnToStaleControls = options?.returnTo === "stale_controls";
    const shouldReturnToWorkflowCenter = options?.returnTo === "workflows";

    setTaskModalReturnTarget(
      shouldReturnToRiskQueue
        ? "risk_queue"
        : shouldReturnToAllActiveTasks
        ? "all_active_tasks"
        : shouldReturnToQueueStatusTasks
        ? "queue_status_tasks"
        : shouldReturnToActiveFindings
        ? "active_findings"
        : shouldReturnToApprovedExceptions
        ? "approved_exceptions"
        : shouldReturnToValidationFailed
        ? "validation_failed"
        : shouldReturnToOverdueTasks
        ? "overdue_tasks"
        : shouldReturnToInProgressTasks
        ? "in_progress_tasks"
        : shouldReturnToClosedTasks
        ? "closed_tasks"
        : shouldReturnToPostureStatus
        ? "posture_status"
        : shouldReturnToStaleControls
        ? "stale_controls"
        : shouldReturnToWorkflowCenter
        ? "workflows"
        : null
    );
    if (shouldReturnToRiskQueue) {
      setShowRemainingRisksModal(false);
    }
    if (shouldReturnToAllActiveTasks) {
      setShowAllActiveTasksModal(false);
    }
    if (shouldReturnToQueueStatusTasks) {
      setShowQueueStatusTasksModal(false);
    }
    if (shouldReturnToActiveFindings) {
      setShowActiveFindingsModal(false);
    }
    if (shouldReturnToApprovedExceptions) {
      setShowApprovedExceptionsModal(false);
    }
    if (shouldReturnToValidationFailed) {
      setShowValidationFailedModal(false);
    }
    if (shouldReturnToOverdueTasks) {
      setShowOverdueTasksModal(false);
    }
    if (shouldReturnToInProgressTasks) {
      setShowInProgressTasksModal(false);
    }
    if (shouldReturnToClosedTasks) {
      setShowClosedTasksModal(false);
    }
    if (shouldReturnToPostureStatus) {
      setShowPostureStatusModal(false);
    }
    if (shouldReturnToStaleControls) {
      setShowStaleControlsModal(false);
    }
    setManagedTask(task);
    setManagedTaskDraft({
      id: task.id,
      status: normalizeText(task.status) || "open",
      owner_name: task.owner_name || "",
      due_date: formatDateInput(task.due_date),
      priority: String(task.priority || 100),
      block_reason: task.task_metadata?.block_reason || "",
      closure_reason: task.closure_reason || "",
    });
    setManagedTaskInvestigationDraft(buildInvestigationDraftFromTask(task));
    setManagedTaskEvents([]);
    setManagedTaskEventsError("");
    setManagedTaskValidationHistory([]);
    setManagedTaskValidationHistoryError("");
    setManagedTaskEvidence([]);
    setManagedTaskEvidenceError("");
    setManagedTaskEvidenceCreateError("");
    setManagedTaskWorkflowRecoveryError("");
    setManagedTaskClosureGuard(null);
    setManagedTaskClosureGuardError("");
    setRemediationTaskNotice("");
    setRemediationTaskError("");
    handleLoadManagedTaskEvents(task.id);
    handleLoadManagedTaskValidationHistory(task.id);
    handleLoadManagedTaskEvidence(task.id);
    handleLoadManagedTaskClosureGuard(task.id);
  }, [
    handleLoadManagedTaskClosureGuard,
    handleLoadManagedTaskEvents,
    handleLoadManagedTaskEvidence,
    handleLoadManagedTaskValidationHistory,
  ]);

  useEffect(() => {
    if (loading) return undefined;

    const taskId = normalizeRouteParamValue(routeOpenTaskIdParam);
    if (!taskId) return undefined;

    const navigationKey = [taskId, routeNavNonceParam].filter(Boolean).join("::");
    if (routeTaskOpenHandledRef.current === navigationKey) return undefined;
    routeTaskOpenHandledRef.current = navigationKey;

    let cancelled = false;

    const openRouteTask = async () => {
      const localTask = remediationTasksRef.current.find((task) => String(task?.id || "") === taskId);
      if (localTask) {
        if (!cancelled) {
          handleOpenManageTask(localTask, { returnTo: "workflows" });
        }
        return;
      }

      try {
        const token = await fetchToken();
        const payload = await getJsonWithToken(token, `/remediation/tasks/${taskId}`);
        const task = payload?.task || payload?.item || payload?.data || payload;

        if (!cancelled && task?.id) {
          handleOpenManageTask(task, { returnTo: "workflows" });
        } else if (!cancelled) {
          setRemediationTaskError("Unable to open the linked remediation task from Workflow Center.");
        }
      } catch (error) {
        if (!cancelled) {
          setRemediationTaskError(
            `Unable to open the linked remediation task from Workflow Center: ${error?.message || error}`
          );
        }
      }
    };

    openRouteTask();

    return () => {
      cancelled = true;
    };
  }, [
    fetchToken,
    getJsonWithToken,
    handleOpenManageTask,
    loading,
    routeNavNonceParam,
    routeOpenTaskIdParam,
  ]);

  const handleCloseManageTask = useCallback((options = {}) => {
    const shouldReturnToRiskQueue =
      taskModalReturnTarget === "risk_queue" && options?.suppressReturn !== true;
    const shouldReturnToAllActiveTasks =
      taskModalReturnTarget === "all_active_tasks" && options?.suppressReturn !== true;
    const shouldReturnToQueueStatusTasks =
      taskModalReturnTarget === "queue_status_tasks" && options?.suppressReturn !== true;
    const shouldReturnToActiveFindings =
      taskModalReturnTarget === "active_findings" && options?.suppressReturn !== true;
    const shouldReturnToApprovedExceptions =
      taskModalReturnTarget === "approved_exceptions" && options?.suppressReturn !== true;
    const shouldReturnToValidationFailed =
      taskModalReturnTarget === "validation_failed" && options?.suppressReturn !== true;
    const shouldReturnToOverdueTasks =
      taskModalReturnTarget === "overdue_tasks" && options?.suppressReturn !== true;
    const shouldReturnToInProgressTasks =
      taskModalReturnTarget === "in_progress_tasks" && options?.suppressReturn !== true;
    const shouldReturnToClosedTasks =
      taskModalReturnTarget === "closed_tasks" && options?.suppressReturn !== true;
    const shouldReturnToPostureStatus =
      taskModalReturnTarget === "posture_status" && options?.suppressReturn !== true;
    const shouldReturnToStaleControls =
      taskModalReturnTarget === "stale_controls" && options?.suppressReturn !== true;
    const shouldReturnToWorkflowCenter =
      taskModalReturnTarget === "workflows" && options?.suppressReturn !== true;

    setManagedTask(null);
    setManagedTaskDraft(null);
    setManagedTaskInvestigationDraft(null);
    setManagedTaskEvents([]);
    setManagedTaskEventsError("");
    setManagedTaskValidationHistory([]);
    setManagedTaskValidationHistoryError("");
    setManagedTaskEvidence([]);
    setManagedTaskEvidenceError("");
    setManagedTaskEvidenceCreateError("");
    setManagedTaskEvidenceCreating(false);
    setManagedTaskWorkflowRecoveryError("");
    setManagedTaskWorkflowRecovering(false);
    setManagedTaskClosureGuard(null);
    setManagedTaskClosureGuardError("");
    setManagedTaskClosureGuardLoading(false);
    setTaskModalReturnTarget(null);

    if (shouldReturnToRiskQueue) {
      setShowRemainingRisksModal(true);
    }
    if (shouldReturnToAllActiveTasks) {
      setShowAllActiveTasksModal(true);
    }
    if (shouldReturnToQueueStatusTasks) {
      setShowQueueStatusTasksModal(true);
    }
    if (shouldReturnToActiveFindings) {
      setShowActiveFindingsModal(true);
    }
    if (shouldReturnToApprovedExceptions) {
      setShowApprovedExceptionsModal(true);
    }
    if (shouldReturnToValidationFailed) {
      setShowValidationFailedModal(true);
    }
    if (shouldReturnToOverdueTasks) {
      setShowOverdueTasksModal(true);
    }
    if (shouldReturnToInProgressTasks) {
      setShowInProgressTasksModal(true);
    }
    if (shouldReturnToClosedTasks) {
      setShowClosedTasksModal(true);
    }
    if (shouldReturnToPostureStatus) {
      setShowPostureStatusModal(true);
    }
    if (shouldReturnToStaleControls) {
      setShowStaleControlsModal(true);
    }
    if (shouldReturnToWorkflowCenter) {
      window.setTimeout(returnToWorkflowCenter, 0);
    }
  }, [returnToWorkflowCenter, taskModalReturnTarget]);

  const handleOpenWorkflowCenterFromTask = useCallback(
    (payload = {}) => {
      const sourceTask = payload?.task || managedTask || {};
      const sourceWorkflow = payload?.workflow || {};
      const taskMetadata = sourceTask?.task_metadata || sourceTask?.metadata || {};
      const workflowMetadata =
        taskMetadata?.workflow ||
        taskMetadata?.workflow_metadata ||
        taskMetadata?.workflowMetadata ||
        {};
      const taskStateSync = taskMetadata?.workflow_task_state || taskMetadata?.workflowTaskState || {};
      const validationSync = taskMetadata?.workflow_validation || taskMetadata?.workflowValidation || {};

      const workflowId = cleanValue(
        payload?.workflowId ||
          payload?.workflow_id ||
          sourceWorkflow?.workflow_id ||
          sourceWorkflow?.workflowId ||
          sourceWorkflow?.id ||
          workflowMetadata?.workflow_id ||
          workflowMetadata?.workflowId ||
          taskStateSync?.workflow_id ||
          taskStateSync?.workflowId ||
          validationSync?.workflow_id ||
          validationSync?.workflowId,
        ""
      );

      if (!workflowId || typeof setMode !== "function") return;

      handleCloseManageTask({ suppressReturn: true });

      setMode("workflows", {
        workflowId,
        workflow_id: workflowId,
        remediationTaskId: sourceTask?.id || undefined,
        remediation_task_id: sourceTask?.id || undefined,
        framework: sourceTask?.framework || framework,
        source: "remediation_task_modal",
        returnMode: "posture",
        returnTo: "posture",
        routeNavNonce: String(Date.now()),
        scrollTop: "1",
      });
    },
    [framework, handleCloseManageTask, managedTask, setMode]
  );


  const handleOpenValidationDashboardFromTask = useCallback(
    (task = managedTask) => {
      const frameworkValue = task?.framework || framework || "SOC2";

      if (typeof setMode === "function") {
        handleCloseManageTask({ suppressReturn: true });
        setMode("validation", {
          framework: frameworkValue,
          taskId: task?.id || undefined,
          task_id: task?.id || undefined,
          controlId: task?.control_id || task?.controlId || undefined,
          control_id: task?.control_id || task?.controlId || undefined,
          ruleId: task?.rule_id || task?.ruleId || undefined,
          rule_id: task?.rule_id || task?.ruleId || undefined,
          resourceId: task?.resource_id || task?.resourceId || undefined,
          resource_id: task?.resource_id || task?.resourceId || undefined,
          source: "remediation_task_modal",
          returnMode: "posture",
          returnTo: "posture",
          routeNavNonce: String(Date.now()),
          scrollTop: "1",
        });
        return;
      }

      window.location.hash = `validation?framework=${encodeURIComponent(frameworkValue)}`;
    },
    [framework, handleCloseManageTask, managedTask, setMode]
  );


  const handleRecoverManagedTaskWorkflow = useCallback(
    async (task = managedTask) => {
      if (!task?.id || managedTaskWorkflowRecovering) return null;

      setManagedTaskWorkflowRecovering(true);
      setManagedTaskWorkflowRecoveryError("");
      setRemediationTaskNotice("");
      setRemediationTaskError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("recover-remediation-workflow-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const response = await recoverRemediationTaskWorkflow({
          postJsonWithToken,
          token,
          taskId: task.id,
          payload: {
            reason: "Recover remediation workflow after failed validation and return task to active work.",
            task_status: "in_progress",
            validation_status: "pending",
            actor_role: "compliance_lead",
            metadata: {
              created_from: "remediation_task_modal_workflow_recovery",
              framework: task.framework || framework,
              control_id: task.control_id || task.controlId || "",
              rule_id: task.rule_id || task.ruleId || "",
              resource_id: task.resource_id || task.resourceId || "",
              source: task.source || task.provider || "remediation_task_modal",
            },
          },
          signal: controller.signal,
        });

        const updatedTask = response?.task || null;
        if (updatedTask) {
          handleRemediationTaskUpdatedFromModal(updatedTask);
          setManagedTaskDraft({
            id: updatedTask.id,
            status: normalizeText(updatedTask.status) || "in_progress",
            owner_name: updatedTask.owner_name || "",
            due_date: formatDateInput(updatedTask.due_date),
            priority: String(updatedTask.priority || 100),
            block_reason: updatedTask.task_metadata?.block_reason || "",
            closure_reason: updatedTask.closure_reason || "",
          });
          setManagedTaskInvestigationDraft(buildInvestigationDraftFromTask(updatedTask));
        }

        await Promise.allSettled([
          handleLoadManagedTaskEvents(task.id),
          handleLoadManagedTaskValidationHistory(task.id),
          handleLoadManagedTaskEvidence(task.id),
        ]);

        setAssessmentRefreshTick((value) => value + 1);
        setRemediationTaskNotice("Workflow recovered. Task moved back to In Progress and validation reset to Pending.");
        return response;
      } catch (error) {
        console.error("Failed to recover remediation workflow", error);
        const message =
          error?.name === "AbortError"
            ? `Workflow recovery timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to recover remediation workflow.");
        setManagedTaskWorkflowRecoveryError(message);
        setRemediationTaskError(message);
        return null;
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskWorkflowRecovering(false);
      }
    },
    [
      fetchToken,
      framework,
      handleLoadManagedTaskEvents,
      handleLoadManagedTaskEvidence,
      handleLoadManagedTaskValidationHistory,
      handleRemediationTaskUpdatedFromModal,
      managedTask,
      managedTaskWorkflowRecovering,
      postJsonWithToken,
    ]
  );

  const handleRunManagedTaskValidation = useCallback(
    async (task = managedTask) => {
      if (!task?.id || managedTaskValidationActionRunning) return null;

      setManagedTaskValidationActionRunning(true);
      setManagedTaskValidationActionError("");
      setRemediationTaskNotice("");
      setRemediationTaskError("");

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("run-remediation-validation-timeout"),
        FULL_CLOUD_ASSESSMENT_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        let effectiveTask = task;
        const currentStatus = normalizeText(task.status || task.task_status || "");

        if (currentStatus !== "ready_for_validation") {
          const updatedTask = await handleUpdateRemediationTask(
            task,
            {
              status: "ready_for_validation",
              validation_status: "pending",
            },
            "Task marked Ready for Validation. Running validation…"
          );

          if (updatedTask?.id) {
            effectiveTask = updatedTask;
          }
        }

        const validationResponse = await rerunRemediationTaskValidation({
          postJsonWithToken,
          token,
          taskId: effectiveTask.id,
          signal: controller.signal,
        });

        const refreshedTask = await getJsonWithToken(
          token,
          `/remediation/tasks/${effectiveTask.id}`,
          {},
          controller.signal
        );

        const latestTask = refreshedTask?.id ? refreshedTask : effectiveTask;
        if (latestTask?.id) {
          handleRemediationTaskUpdatedFromModal(latestTask);
        }

        await Promise.allSettled([
          handleLoadManagedTaskEvents(effectiveTask.id),
          handleLoadManagedTaskValidationHistory(effectiveTask.id),
          handleLoadManagedTaskEvidence(effectiveTask.id),
        ]);

        const rerunSummary = validationResponse?.summary || {};
        const refreshedGuard =
          rerunSummary.closure_guard ||
          (await handleLoadManagedTaskClosureGuard(effectiveTask.id));

        if (refreshedGuard) {
          setManagedTaskClosureGuard(refreshedGuard);
          setManagedTaskClosureGuardError("");
        }

        const resultLabel = cleanValue(rerunSummary.result || rerunSummary.status, "unknown");
        const confidenceScore = cleanValue(rerunSummary.confidence_score, "");
        const confidenceLevel = cleanValue(rerunSummary.confidence_level, "");

        setRemediationTaskNotice(
          `Validation re-run complete. Result: ${resultLabel}.` +
            (confidenceScore || confidenceLevel
              ? ` Confidence: ${[confidenceScore, confidenceLevel].filter(Boolean).join(" / ")}.`
              : "") +
            " Closure guard refreshed."
        );

        return {
          validation: validationResponse,
          task: latestTask,
          closureGuard: refreshedGuard,
        };
      } catch (error) {
        console.error("Failed to run remediation validation", error);
        const message =
          error?.name === "AbortError"
            ? `Remediation validation timed out after ${FULL_CLOUD_ASSESSMENT_TIMEOUT_MS / 1000}s.`
            : String(error?.message || error || "Unable to run remediation validation.");
        setManagedTaskValidationActionError(message);
        setRemediationTaskError(message);
        return null;
      } finally {
        window.clearTimeout(timeoutId);
        setManagedTaskValidationActionRunning(false);
      }
    },
    [
      fetchToken,
      getJsonWithToken,
      handleLoadManagedTaskClosureGuard,
      handleLoadManagedTaskEvents,
      handleLoadManagedTaskEvidence,
      handleLoadManagedTaskValidationHistory,
      handleRemediationTaskUpdatedFromModal,
      handleUpdateRemediationTask,
      managedTask,
      managedTaskValidationActionRunning,
      postJsonWithToken,
    ]
  );


  const handleSaveManagedTask = useCallback(async (draftOverride = null, noticeOverride = "Remediation task saved.") => {
    const isEventLike = Boolean(
      draftOverride &&
        typeof draftOverride === "object" &&
        (typeof draftOverride.preventDefault === "function" || draftOverride.nativeEvent)
    );
    const effectiveDraft = !isEventLike && draftOverride && typeof draftOverride === "object"
      ? draftOverride
      : managedTaskDraft;

    if (!managedTask?.id || !effectiveDraft) return;

    const nextStatus = normalizeText(effectiveDraft.status) || "open";
    if (nextStatus === "ready_for_validation") {
      const blockedMessage = buildReadyForValidationBlockedMessage(
        managedTask,
        managedTaskInvestigationDraft || buildInvestigationDraftFromTask(managedTask)
      );

      if (blockedMessage) {
        setRemediationTaskNotice("");
        setRemediationTaskError(blockedMessage);
        return;
      }
    }

    const nextMetadata = {
      ...(managedTask.task_metadata || {}),
    };

    if (nextStatus === "blocked") {
      nextMetadata.block_reason = String(effectiveDraft.block_reason || "").trim();
    }

    const updates = {
      status: nextStatus,
      owner_name: String(effectiveDraft.owner_name || "").trim() || null,
      due_date: effectiveDraft.due_date || null,
      priority: Number(effectiveDraft.priority) || 100,
      task_metadata: nextMetadata,
    };

    if (nextStatus === "closed") {
      updates.closure_reason =
        String(effectiveDraft.closure_reason || "").trim() ||
        "Closed from remediation task manager.";
    }

    await handleUpdateRemediationTask(
      managedTask,
      updates,
      noticeOverride || "Remediation task saved."
    );
  }, [handleUpdateRemediationTask, managedTask, managedTaskDraft, managedTaskInvestigationDraft]);

  const handleMarkTaskReadyForValidation = useCallback(
    async (task) => {
      if (!task?.id) return;

      const investigationDraft =
        managedTask?.id === task.id && managedTaskInvestigationDraft
          ? managedTaskInvestigationDraft
          : buildInvestigationDraftFromTask(task);

      const blockedMessage = buildReadyForValidationBlockedMessage(task, investigationDraft);

      if (blockedMessage) {
        setRemediationTaskNotice("");
        setRemediationTaskError(blockedMessage);
        if (managedTask?.id !== task.id) {
          handleOpenManageTask(task);
        }
        return;
      }

      const updatedTask = await handleUpdateRemediationTask(
        task,
        {
          status: "ready_for_validation",
          validation_status: "pending",
        },
        "Task marked Ready for Validation."
      );

      if (updatedTask) {
        setTaskNextStepModal({
          stage: "ready_for_validation",
          task: updatedTask,
          title: "Task is ready for validation",
          detail:
            "OASYS is ready to check the source evidence. Run Full Cloud Assessment to sync IAM inventory, recalculate posture, and validate this task.",
        });
      }
    },
    [
      handleOpenManageTask,
      handleUpdateRemediationTask,
      managedTask,
      managedTaskInvestigationDraft,
    ]
  );

  const handleSaveManagedTaskInvestigation = useCallback(async () => {
    if (!managedTask?.id || !managedTaskInvestigationDraft) return;

    setManagedTaskInvestigationSaving(true);
    setRemediationTaskNotice("");
    setRemediationTaskError("");

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort("update-remediation-investigation-timeout"),
      REQUEST_TIMEOUT_MS
    );

    try {
      const evidenceLinks = String(managedTaskInvestigationDraft.evidence_links || "")
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);

      const investigationPayload = {
        investigation_status: managedTaskInvestigationDraft.investigation_status || "not_started",
        dependency_owner: String(managedTaskInvestigationDraft.dependency_owner || "").trim(),
        workload_name: String(managedTaskInvestigationDraft.workload_name || "").trim(),
        secret_location: String(managedTaskInvestigationDraft.secret_location || "").trim(),
        credential_location: String(managedTaskInvestigationDraft.credential_location || "").trim(),
        last_used_interpretation: String(managedTaskInvestigationDraft.last_used_interpretation || "").trim(),
        rotation_plan: String(managedTaskInvestigationDraft.rotation_plan || "").trim(),
        replacement_key_created: Boolean(managedTaskInvestigationDraft.replacement_key_created),
        replacement_key_slot: String(managedTaskInvestigationDraft.replacement_key_slot || "").trim(),
        replacement_key_created_at: String(managedTaskInvestigationDraft.replacement_key_created_at || "").trim(),
        replacement_key_id_hint: String(managedTaskInvestigationDraft.replacement_key_id_hint || "").trim(),
        old_key_retirement_action: String(managedTaskInvestigationDraft.old_key_retirement_action || "").trim(),
        workload_updated: Boolean(managedTaskInvestigationDraft.workload_updated),
        test_result: String(managedTaskInvestigationDraft.test_result || "").trim(),
        monitoring_window: String(managedTaskInvestigationDraft.monitoring_window || "").trim(),
        monitoring_result: String(managedTaskInvestigationDraft.monitoring_result || "").trim(),
        ready_for_validation_reason: String(managedTaskInvestigationDraft.ready_for_validation_reason || "").trim(),
        notes: String(managedTaskInvestigationDraft.notes || "").trim(),
        evidence_links: evidenceLinks,
      };

      const token = await fetchToken();
      const response = await patchJsonWithToken(
        token,
        `/remediation/tasks/${managedTask.id}/investigation`,
        investigationPayload,
        controller.signal
      );

      const updatedTask = response?.task || null;

      if (updatedTask) {
        setManagedTask(updatedTask);
        setManagedTaskInvestigationDraft(buildInvestigationDraftFromTask(updatedTask));
        setRemediationTasks((current) =>
          current.map((item) => (item.id === updatedTask.id ? updatedTask : item))
        );
        await Promise.allSettled([
          handleLoadManagedTaskEvents(updatedTask.id),
          handleLoadManagedTaskValidationHistory(updatedTask.id),
          handleLoadManagedTaskEvidence(updatedTask.id),
        ]);

        const updatedDraft = buildInvestigationDraftFromTask(updatedTask);
        if (canShowReadyForValidationNextStep(updatedTask, updatedDraft)) {
          setTaskNextStepModal({
            stage: "rotation_complete",
            task: updatedTask,
            title: "Rotation evidence complete",
            detail:
              "OASYS has enough investigation evidence to submit this task for validation. Mark it Ready for Validation so OASYS can verify the old key is retired in the latest IAM credential report.",
          });
        }
      }

      setRemediationTaskNotice("Investigation notes saved.");
    } catch (error) {
      console.error("Failed to save remediation investigation", error);
      setRemediationTaskError(
        error?.name === "AbortError"
          ? `Investigation update timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
          : String(error?.message || error || "Unable to save investigation notes.")
      );
    } finally {
      window.clearTimeout(timeoutId);
      setManagedTaskInvestigationSaving(false);
    }
  }, [
    fetchToken,
    handleLoadManagedTaskEvents,
    handleLoadManagedTaskEvidence,
    handleLoadManagedTaskValidationHistory,
    managedTask,
    managedTaskInvestigationDraft,
    patchJsonWithToken,
  ]);

  useEffect(() => {
    if (!fixPlanItem) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setFixPlanItem(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [fixPlanItem]);

  useEffect(() => {
    if (!managedTask) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseManageTask();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [managedTask, handleCloseManageTask]);


  const renderRiskQueueRow = (
    risk,
    idx,
    section = "remaining",
    returnTarget = "risk_queue"
  ) => {
    const isMatched = matchRiskRow(risk, effectiveFocusContext);
    const linkedTask = findTaskForItem(
      activeRemediationTasks,
      risk,
      framework
    );
    const riskKey = getRemainingRiskSelectionKey(
      risk,
      `${section}-${idx}`
    );
    const canBulkCreateTask = !linkedTask;
    const isBulkSelected = selectedRemainingRiskKeys.has(riskKey);
    const controlLabel = buildControlLabel(
      risk.control_id || risk.controlId,
      risk.rule_id || risk.ruleId
    );
    const severityValue =
      risk.severity || risk.severity_weight || "Unknown";
    const severityTone = isHighSeverityRisk(risk)
      ? "danger"
      : "warning";
    const scoreValue = safeNumber(risk.score).toFixed(2);
    const frameworkValue = risk.framework || framework;
    const resourceValue = cleanValue(
      risk.resource_id || risk.resourceId,
      ""
    );

    const openGuidance = () => {
      handleViewGuidance(risk, { returnTo: returnTarget });
    };

    return (
      <article
        key={riskKey}
        className={[
          "oasys-risk-card",
          isMatched ? "oasys-risk-card--matched" : "",
          isBulkSelected ? "oasys-risk-card--selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={controlLabel}
      >
        <div className="oasys-risk-card__selection">
          {canBulkCreateTask ? (
            <label className="oasys-risk-select">
              <input
                type="checkbox"
                checked={isBulkSelected}
                onChange={() => toggleRemainingRiskSelected(riskKey)}
                aria-label={`Select ${controlLabel} for bulk task creation`}
              />
              <span aria-hidden="true" />
            </label>
          ) : (
            <span
              className="oasys-risk-card__task-indicator"
              title="Tracked remediation task exists"
              aria-label="Tracked remediation task exists"
            />
          )}
        </div>

        <div className="oasys-risk-card__content">
          <div className="oasys-risk-card__header">
            <div className="oasys-risk-card__heading">
              <h4>{controlLabel}</h4>

              <div className="oasys-risk-card__chips">
                <StatusChip tone="informational">
                  {frameworkValue}
                </StatusChip>

                <StatusChip tone={severityTone}>
                  {String(severityValue)}
                </StatusChip>

                <StatusChip tone="neutral">
                  Score {scoreValue}
                </StatusChip>

                {section === "top" ? (
                  <StatusChip tone="warning">Top Risk</StatusChip>
                ) : null}

                {linkedTask ? (
                  <TaskStatusPill task={linkedTask} />
                ) : (
                  <StatusChip tone="neutral">No Task</StatusChip>
                )}
              </div>

              {section === "top" ? (
                <div style={{ marginTop: "0.4rem" }}>
                  <Button
                    size="small"
                    variant="ghost"
                    onClick={() => openPostureExplain(risk, "risk")}
                  >
                    Explain
                  </Button>
                </div>
              ) : null}
            </div>

            <div
              className="oasys-risk-card__actions"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "0.3rem",
                minWidth: "250px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                {linkedTask ? (
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() =>
                      handleOpenManageTask(linkedTask, {
                        returnTo: returnTarget,
                      })
                    }
                  >
                    View Task
                  </Button>
                ) : null}

                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    if (returnTarget === "risk_queue") {
                      setShowRemainingRisksModal(false);
                    }
                    if (returnTarget === "active_findings") {
                      setShowActiveFindingsModal(false);
                    }
                    handleOpenFixPlan(risk, "risk", {
                      returnTo: returnTarget,
                    });
                  }}
                >
                  Fix Plan
                </Button>

                <Button
                  size="small"
                  variant="secondary"
                  onClick={openGuidance}
                >
                  Guidance
                </Button>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                }}
              >
                {section !== "top" ? (
                  <Button
                    size="small"
                    variant="ghost"
                    onClick={() => openPostureExplain(risk, "risk")}
                  >
                    Explain
                  </Button>
                ) : null}

                <Button
                  size="small"
                  variant="tertiary"
                  onClick={() => {
                    if (returnTarget === "risk_queue") {
                      setShowRemainingRisksModal(false);
                    }
                    if (returnTarget === "active_findings") {
                      setShowActiveFindingsModal(false);
                    }
                    setMode("drift", {
                      framework: frameworkValue,
                      controlId: risk.control_id || risk.controlId,
                      ruleId: risk.rule_id || risk.ruleId,
                      resourceId: resourceValue || undefined,
                    });
                  }}
                >
                  Open in Drift
                </Button>
              </div>
            </div>
          </div>

          <div className="oasys-risk-card__details">
            <div>
              <span>Finding</span>
              <p>
                {risk.reason_summary ||
                  "No reason summary is available for this risk."}
              </p>
            </div>

            {risk.remediation_hint ? (
              <div>
                <span>Recommended response</span>
                <p>{risk.remediation_hint}</p>
              </div>
            ) : null}
          </div>

          {resourceValue ? (
            <div className="oasys-risk-card__resource">
              <span>Resource</span>
              <strong>{resourceValue}</strong>
            </div>
          ) : null}
        </div>
      </article>
    );
  };


  if (loading) {
    return (
      <div className={`oasys-dashboard${dashboardThemeClass}`}>
        <div className="dash-shell">
          <div className="dash-header">
            <div>
              <div className="dash-title">Posture</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            Loading posture…
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className={`oasys-dashboard${dashboardThemeClass}`}>
        <div className="dash-shell">
          <div className="dash-header">
            <div>
              <div className="dash-title">Posture</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ color: "#ff7b7b" }}>Failed to load posture: {err}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`oasys-dashboard${dashboardThemeClass} posture-page`}>
      <div className="dash-shell posture-page__shell">
        <style>
          {`
            .posture-selectable-card:hover {
              background: color-mix(in srgb, var(--accent), transparent 96%) !important;
              border-left-color: color-mix(in srgb, var(--accent), transparent 30%) !important;
              transform: translateY(-1px);
            }

            .posture-selectable-card:focus-visible {
              outline: 2px solid color-mix(in srgb, var(--accent), transparent 20%);
              outline-offset: 2px;
            }

            .posture-selectable-row:hover {
              background: color-mix(in srgb, var(--accent), transparent 95%) !important;
            }

            .posture-selectable-row:focus-visible {
              outline: 2px solid color-mix(in srgb, var(--accent), transparent 20%);
              outline-offset: -2px;
            }

            .posture-card-actions {
              display: grid;
              grid-template-columns: repeat(2, max-content);
              gap: 7px;
              justify-content: end;
              align-content: center;
              align-items: center;
            }
          `}
        </style>

        <header className="posture-page-header">
          <div className="posture-page-header__copy">
            <h1 className="posture-page-header__title">Posture</h1>
            <p className="posture-page-header__description">
              Explain the score, surface stale controls, and identify the highest-risk control conditions first.
            </p>
          </div>

          <div className="posture-page-header__actions" aria-label="Posture navigation">
            <Button
              variant="secondary"
              size="small"
              onClick={() => setMode("dashboard")}
            >
              Mission Control
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() =>
                setMode("drift", {
                  framework,
                  controlId: routeSelectionContext.controlId || undefined,
                  ruleId: routeSelectionContext.ruleId || undefined,
                  resourceId: routeSelectionContext.resourceId || undefined,
                })
              }
            >
              Open Drift &amp; State
            </Button>
          </div>
        </header>

        <PostureIntelligencePanel
          requestJson={requestPostureIntelligence}
          framework={framework}
          refreshKey={assessmentRefreshTick}
        />

        {contextLine ? (
          <div className="card posture-context-banner">
            <span>{contextLine}</span>
            <div className="posture-context-actions">
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                onClick={() => jumpToInvestigationSummary("smooth")}
              >
                Jump to Investigation
              </button>
              {selectedGuidanceTarget ? (
                <button
                  className="btn btn-ghost btn-xs"
                  type="button"
                  onClick={() => {
                    setSelectedGuidanceTarget(null);
                    setGuidanceModalItem(null);
                    setGuidanceReturnTarget(null);
                  }}
                >
                  Clear guidance
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <ValidationRunModal
          validationRunModal={validationRunModal}
          setValidationRunModal={setValidationRunModal}
          fullAssessmentRunning={fullAssessmentRunning}
          remediationTaskUpdating={remediationTaskUpdating}
          activeRemediationTasks={activeRemediationTasks}
          createdRemediationTask={createdRemediationTask}
          handleUpdateRemediationTask={handleUpdateRemediationTask}
          onRemediationTaskUpdated={handleRemediationTaskUpdatedFromModal}
          handleOpenFixPlan={handleOpenFixPlan}
          fixPlanItem={fixPlanItem}
          setMode={setMode}
          framework={framework}
          lastSyncJobId={lastSyncJobId}
          handleRunFullCloudAssessment={handleRunFullCloudAssessment}
        />

        <ScoreDetailsModal
          open={showScoreDetailsModal}
          heroMetrics={heroMetrics}
          riskTone={riskTone}
          onClose={() => closeRouteModal(() => setShowScoreDetailsModal(false))}
        />
        <ContextualExplainDrawer
          open={postureExplainOpen}
          record={postureExplainTarget}
          recordLabel={buildPostureExplainLabel(postureExplainTarget)}
          question={postureExplainQuestion}
          card={postureExplainCard}
          metadata={postureExplainCard?.metadata || null}
          loading={postureExplainLoading}
          error={postureExplainError}
          themeMode={resolvedThemeMode}
          prompts={POSTURE_EXPLAIN_PROMPTS}
          contextDescription="Grounded reasoning for this exact selected posture item. Canonical control state, evidence freshness, active risk context, accepted-risk separation, and framework posture remain authoritative."
          onAsk={askPostureExplain}
          onClose={closePostureExplain}
        />

        <PostureStatusModal
          open={showPostureStatusModal}
          framework={framework}
          statusType={postureStatusModalType}
          rows={postureStatusModalRows}
          totalCount={
            postureStatusModalTotal ||
            getPostureStatusSnapshotTotal(postureStatusModalType) ||
            postureStatusModalRows.length
          }
          loading={postureStatusModalLoading}
          error={postureStatusModalError}
          hasMore={postureStatusModalHasMore}
          onLoadMore={() =>
            loadPostureStatusModalPage({
              statusType: postureStatusModalType,
              offset: postureStatusModalOffset,
              append: true,
            })
          }
          activeRemediationTasks={activeRemediationTasks}
          onClose={() => closeRouteModal(() => setShowPostureStatusModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "posture_status",
            })
          }
          onFixPlan={(item, source = "control", options = {}) =>
            handleOpenFixPlan(item, source, {
              ...options,
              returnTo: options?.returnTo || "posture_status",
            })
          }
          onViewGuidance={(item, options = {}) =>
            handleViewGuidance(item, {
              ...options,
              returnTo: options?.returnTo || "posture_status",
            })
          }
          onOpenDrift={(params) => {
            setShowPostureStatusModal(false);
            setMode("drift", params);
          }}
        />
        <RiskGuidanceModal
          item={guidanceModalItem}
          framework={framework}
          relatedSops={relatedSops}
          recommendedActions={recommendedActions}
          linkedTask={guidanceModalTask}
          returnTarget={guidanceReturnTarget}
          onClose={handleCloseGuidanceModal}
          onViewTask={(task, options = {}) => {
            setGuidanceModalItem(null);
            setGuidanceReturnTarget(null);
            handleOpenManageTask(task, options);
          }}
          onOpenFixPlan={(item, source, options = {}) => {
            setGuidanceModalItem(null);
            setGuidanceReturnTarget(null);
            handleOpenFixPlan(item, source, options);
          }}
          onOpenDrift={(params) => {
            setGuidanceModalItem(null);
            setGuidanceReturnTarget(null);
            setShowRemainingRisksModal(false);
            setShowAllActiveTasksModal(false);
            setShowQueueStatusTasksModal(false);
            setShowActiveFindingsModal(false);
            setShowApprovedExceptionsModal(false);
            setShowValidationFailedModal(false);
            setShowOverdueTasksModal(false);
            setShowInProgressTasksModal(false);
            setShowClosedTasksModal(false);
            setShowPostureStatusModal(false);
            setShowStaleControlsModal(false);
            setMode("drift", params);
          }}
        />

        <AllActiveTasksModal
          open={showAllActiveTasksModal}
          framework={framework}
          activeTasks={allActiveModalVisibleTasks}
          totalCount={allActiveModalTotal || allActiveModalVisibleTasks.length}
          loading={allActiveModalLoading}
          error={allActiveModalError}
          onClose={() => closeRouteModal(() => setShowAllActiveTasksModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "all_active_tasks",
            })
          }
          onFixPlan={(task, source = "task", options = {}) =>
            handleOpenFixPlan(task, source, {
              ...options,
              returnTo: options?.returnTo || "all_active_tasks",
            })
          }
          onViewGuidance={(task, options = {}) =>
            handleViewGuidance(task, {
              ...options,
              returnTo: options?.returnTo || "all_active_tasks",
            })
          }
          onOpenDrift={(params) => {
            setShowAllActiveTasksModal(false);
            setMode("drift", params);
          }}
        />

        <QueueStatusTasksModal
          open={showQueueStatusTasksModal}
          framework={framework}
          queueType={queueStatusModalType}
          tasks={queueStatusModalVisibleTasks}
          totalCount={queueStatusModalTotal || queueStatusModalVisibleTasks.length}
          loading={queueStatusModalLoading}
          error={queueStatusModalError}
          onClose={() => closeRouteModal(() => setShowQueueStatusTasksModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "queue_status_tasks",
            })
          }
          onFixPlan={(task, source = "task", options = {}) =>
            handleOpenFixPlan(task, source, {
              ...options,
              returnTo: options?.returnTo || "queue_status_tasks",
            })
          }
          onViewGuidance={(task, options = {}) =>
            handleViewGuidance(task, {
              ...options,
              returnTo: options?.returnTo || "queue_status_tasks",
            })
          }
          onOpenDrift={(params) => {
            setShowQueueStatusTasksModal(false);
            setMode("drift", params);
          }}
        />

        <ActiveFindingsModal
          open={showActiveFindingsModal}
          framework={framework}
          activeFindingRows={activeRiskQueueRows}
          activeFindingCount={scoreBreakdown.fail}
          loadedActiveRiskQueueCount={loadedActiveRiskQueueCount}
          highSeverityCount={riskQueueFilterCounts.high || 0}
          noTaskCount={riskQueueFilterCounts.no_task || 0}
          renderActiveFindingRow={(risk, idx, section) =>
            renderRiskQueueRow(risk, idx, section, "active_findings")
          }
          onClose={() => closeRouteModal(() => setShowActiveFindingsModal(false))}
        />
        <ApprovedExceptionsModal
          open={showApprovedExceptionsModal}
          framework={framework}
          approvedExceptionTasks={approvedExceptionTasks}
          acceptedRiskFindings={scoreBreakdown.acceptedRiskFindings}
          approvedExceptionControls={scoreBreakdown.approvedExceptionControls}
          onClose={() => closeRouteModal(() => setShowApprovedExceptionsModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "approved_exceptions",
            })
          }
          onViewGuidance={(item, options = {}) =>
            handleViewGuidance(item, {
              ...options,
              returnTo: options?.returnTo || "approved_exceptions",
            })
          }
          onOpenDrift={(params) => {
            setShowApprovedExceptionsModal(false);
            setMode("drift", params);
          }}
        />

        <ValidationFailedModal
          open={showValidationFailedModal}
          framework={framework}
          validationFailedTasks={validationFailedTasks}
          onClose={() => closeRouteModal(() => setShowValidationFailedModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "validation_failed",
            })
          }
          onFixPlan={(task, source = "task", options = {}) =>
            handleOpenFixPlan(task, source, {
              ...options,
              returnTo: options?.returnTo || "validation_failed",
            })
          }
          onViewGuidance={(task, options = {}) =>
            handleViewGuidance(task, {
              ...options,
              returnTo: options?.returnTo || "validation_failed",
            })
          }
          onOpenDrift={(params) => {
            setShowValidationFailedModal(false);
            setMode("drift", params);
          }}
        />

        <OverdueTasksModal
          open={showOverdueTasksModal}
          framework={framework}
          overdueTasks={overdueModalVisibleTasks}
          totalCount={overdueModalTotal || overdueModalVisibleTasks.length}
          loading={overdueModalLoading}
          error={overdueModalError}
          onClose={() => closeRouteModal(() => setShowOverdueTasksModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "overdue_tasks",
            })
          }
          onFixPlan={(task, source = "task", options = {}) =>
            handleOpenFixPlan(task, source, {
              ...options,
              returnTo: options?.returnTo || "overdue_tasks",
            })
          }
          onViewGuidance={(task, options = {}) =>
            handleViewGuidance(task, {
              ...options,
              returnTo: options?.returnTo || "overdue_tasks",
            })
          }
          onOpenDrift={(params) => {
            setShowOverdueTasksModal(false);
            setMode("drift", params);
          }}
        />

        <InProgressTasksModal
          open={showInProgressTasksModal}
          framework={framework}
          inProgressTasks={inProgressModalVisibleTasks}
          totalCount={inProgressModalTotal || inProgressModalVisibleTasks.length}
          loading={inProgressModalLoading}
          error={inProgressModalError}
          onClose={() => closeRouteModal(() => setShowInProgressTasksModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "in_progress_tasks",
            })
          }
          onFixPlan={(task, source = "task", options = {}) =>
            handleOpenFixPlan(task, source, {
              ...options,
              returnTo: options?.returnTo || "in_progress_tasks",
            })
          }
          onViewGuidance={(task, options = {}) =>
            handleViewGuidance(task, {
              ...options,
              returnTo: options?.returnTo || "in_progress_tasks",
            })
          }
          onOpenDrift={(params) => {
            setShowInProgressTasksModal(false);
            setMode("drift", params);
          }}
        />

        <ClosedTasksModal
          open={showClosedTasksModal}
          framework={framework}
          closedTasks={closedModalVisibleTasks}
          totalCount={closedModalTotal || closedModalVisibleTasks.length}
          loading={closedModalLoading}
          error={closedModalError}
          onClose={() => closeRouteModal(() => setShowClosedTasksModal(false))}
          onReviewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "closed_tasks",
            })
          }
          onViewGuidance={(task, options = {}) =>
            handleViewGuidance(task, {
              ...options,
              returnTo: options?.returnTo || "closed_tasks",
            })
          }
          onOpenDrift={(params) => {
            setShowClosedTasksModal(false);
            setMode("drift", params);
          }}
        />

        <StaleControlsModal
          open={showStaleControlsModal}
          framework={framework}
          staleControls={staleControls}
          activeRemediationTasks={activeRemediationTasks}
          effectiveFocusContext={effectiveFocusContext}
          onClose={() => closeRouteModal(() => setShowStaleControlsModal(false))}
          onViewTask={(task, options = {}) =>
            handleOpenManageTask(task, {
              ...options,
              returnTo: options?.returnTo || "stale_controls",
            })
          }
          onFixPlan={(item, source = "stale", options = {}) =>
            handleOpenFixPlan(item, source, {
              ...options,
              returnTo: options?.returnTo || "stale_controls",
            })
          }
          onViewGuidance={(item, options = {}) =>
            handleViewGuidance(item, {
              ...options,
              returnTo: options?.returnTo || "stale_controls",
            })
          }
          setMode={setMode}
        />

        <RiskQueueModal
          open={showRemainingRisksModal}
          framework={framework}
          filters={RISK_QUEUE_FILTERS}
          riskQueueFilter={riskQueueFilter}
          setRiskQueueFilter={setRiskQueueFilter}
          riskQueueFilterCounts={riskQueueFilterCounts}
          filteredActiveRiskQueueRows={filteredActiveRiskQueueRows}
          loadedActiveRiskQueueCount={loadedActiveRiskQueueCount}
          loadedRemainingRiskCount={loadedRemainingRiskCount}
          remainingTopRiskCount={remainingTopRiskCount}
          activeRiskQueueFilterLabel={activeRiskQueueFilterLabel}
          selectedRemainingRiskCount={selectedRemainingRiskCount}
          visibleNoTaskRiskCount={visibleNoTaskRiskCount}
          allVisibleNoTaskRisksSelected={allVisibleNoTaskRisksSelected}
          selectVisibleNoTaskRisks={selectVisibleNoTaskRisks}
          clearRemainingRiskSelection={clearRemainingRiskSelection}
          remainingRiskBulkCreating={remainingRiskBulkCreating}
          remainingRiskBulkNotice={remainingRiskBulkNotice}
          remainingRiskBulkError={remainingRiskBulkError}
          handleBulkCreateRemainingRiskTasks={handleBulkCreateRemainingRiskTasks}
          filteredTopRiskRows={filteredTopRiskRows}
          filteredRemainingRiskRows={filteredRemainingRiskRows}
          renderRiskQueueRow={renderRiskQueueRow}
          onClose={handleCloseRemainingRisksModal}
        />
        <TaskNextStepModal
          modal={taskNextStepModal}
          remediationTaskUpdating={remediationTaskUpdating}
          fullAssessmentRunning={fullAssessmentRunning}
          onClose={() => setTaskNextStepModal(null)}
          onMarkReadyForValidation={handleMarkTaskReadyForValidation}
          onRunFullCloudAssessment={() => {
            setTaskNextStepModal(null);
            handleRunFullCloudAssessment();
          }}
          onReviewTask={(taskToOpen) => {
            setTaskNextStepModal(null);
            if (taskToOpen?.id) handleOpenManageTask(taskToOpen);
          }}
        />

        <RemediationTaskModal
          framework={framework}
          managedTask={managedTask}
          managedTaskDraft={managedTaskDraft}
          setManagedTaskDraft={setManagedTaskDraft}
          managedTaskInvestigationDraft={managedTaskInvestigationDraft}
          setManagedTaskInvestigationDraft={setManagedTaskInvestigationDraft}
          managedTaskInvestigationSaving={managedTaskInvestigationSaving}
          managedTaskEvents={managedTaskEvents}
          managedTaskEventsLoading={managedTaskEventsLoading}
          managedTaskEventsError={managedTaskEventsError}
          remediationTaskUpdating={remediationTaskUpdating}
          handleCloseManageTask={handleCloseManageTask}
          handleSaveManagedTask={handleSaveManagedTask}
          handleSaveManagedTaskInvestigation={handleSaveManagedTaskInvestigation}
          handleMarkTaskReadyForValidation={handleMarkTaskReadyForValidation}
          handleLoadManagedTaskEvents={handleLoadManagedTaskEvents}
          managedTaskValidationHistory={managedTaskValidationHistory}
          managedTaskValidationHistoryLoading={managedTaskValidationHistoryLoading}
          managedTaskValidationHistoryError={managedTaskValidationHistoryError}
          handleLoadManagedTaskValidationHistory={handleLoadManagedTaskValidationHistory}
          managedTaskEvidence={managedTaskEvidence}
          managedTaskEvidenceLoading={managedTaskEvidenceLoading}
          managedTaskEvidenceError={managedTaskEvidenceError}
          handleLoadManagedTaskEvidence={handleLoadManagedTaskEvidence}
          managedTaskEvidenceCreating={managedTaskEvidenceCreating}
          managedTaskEvidenceCreateError={managedTaskEvidenceCreateError}
          handleCreateManagedTaskEvidence={handleCreateManagedTaskEvidence}
          handleOpenWorkflowCenter={handleOpenWorkflowCenterFromTask}
          handleRecoverManagedTaskWorkflow={handleRecoverManagedTaskWorkflow}
          managedTaskWorkflowRecovering={managedTaskWorkflowRecovering}
          managedTaskWorkflowRecoveryError={managedTaskWorkflowRecoveryError}
          managedTaskClosureGuard={managedTaskClosureGuard}
          managedTaskClosureGuardLoading={managedTaskClosureGuardLoading}
          managedTaskClosureGuardError={managedTaskClosureGuardError}
          handleLoadManagedTaskClosureGuard={handleLoadManagedTaskClosureGuard}
          managedTaskValidationActionRunning={managedTaskValidationActionRunning}
          managedTaskValidationActionError={managedTaskValidationActionError}
          handleRunManagedTaskValidation={handleRunManagedTaskValidation}
          handleOpenValidationDashboard={handleOpenValidationDashboardFromTask}
          handleOpenFixPlan={(item, source, options = {}) =>
            handleOpenFixPlan(item, source, {
              ...options,
              returnTo:
                options?.returnTo ||
                (taskModalReturnTarget === "risk_queue"
                  ? "risk_queue"
                  : taskModalReturnTarget === "active_findings"
                  ? "active_findings"
                  : taskModalReturnTarget === "approved_exceptions"
                  ? "approved_exceptions"
                  : taskModalReturnTarget === "validation_failed"
                  ? "validation_failed"
                  : taskModalReturnTarget === "overdue_tasks"
                  ? "overdue_tasks"
                  : taskModalReturnTarget === "in_progress_tasks"
                  ? "in_progress_tasks"
                  : taskModalReturnTarget === "closed_tasks"
                  ? "closed_tasks"
                  : taskModalReturnTarget === "posture_status"
                  ? "posture_status"
                  : taskModalReturnTarget === "stale_controls"
                  ? "stale_controls"
                  : undefined),
            })
          }
        />

        {panelWarnings.length > 0 ? (
          <div
            className="card"
            style={{
              padding: "10px 14px",
              marginBottom: 16,
              display: "grid",
              gap: 6,
            }}
          >
            <div
              style={{
                color: "var(--warn)",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              Posture loaded with partial data
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                color: "var(--muted)",
                fontSize: 12,
              }}
            >
              {panelWarnings.map((warning, index) => (
                <span
                  key={`${warning}-${index}`}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    background: "color-mix(in srgb, var(--card), transparent 12%)",
                  }}
                >
                  {warning}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <FixPlanModal
          fixPlanItem={fixPlanItem}
          framework={framework}
          apiBaseUrl={apiBaseUrl}
          getAccessToken={getAccessToken}
          tenantId={tenantId}
          visibleModalTask={visibleModalTask}
          fixPlanAwsContext={fixPlanAwsContext}
          fixPlanAwsResourceCopy={fixPlanAwsResourceCopy}
          fixPlanIdentityLabel={fixPlanIdentityLabel}
          fixPlanPolicyLabel={fixPlanPolicyLabel}
          fixPlanPolicyValue={fixPlanPolicyValue}
          fixPlanEventLabel={fixPlanEventLabel}
          fixPlanEventValue={fixPlanEventValue}
          fixPlanPassedEvidenceText={fixPlanPassedEvidenceText}
          isFixPlanCredentialReport={isFixPlanCredentialReport}
          isFixPlanAccessKey={isFixPlanAccessKey}
          isFixPlanActiveAccessKey={isFixPlanActiveAccessKey}
          isFixPlanInlinePolicy={isFixPlanInlinePolicy}
          enrichedFixPlanItem={enrichedFixPlanItem}
          fixPlanSteps={fixPlanSteps}
          fixPlanRelatedSops={fixPlanRelatedSops}
          fixPlanValidation={fixPlanValidation}
          fixPlanRulePlanDiagnostic={fixPlanRulePlanDiagnostic}
          remediationTaskNotice={remediationTaskNotice}
          remediationTaskError={remediationTaskError}
          remediationTaskCreating={remediationTaskCreating}
          remediationTaskUpdating={remediationTaskUpdating}
          relatedSopRemediation={relatedSopRemediation}
          fullAssessmentRunning={fullAssessmentRunning}
          userId={userId}
          userName={userName}
          handleCloseFixPlan={handleCloseFixPlan}
          handleCreateRemediationTask={handleCreateRemediationTask}
          handleUpdateRemediationTask={handleUpdateRemediationTask}
          onRemediationTaskUpdated={handleRemediationTaskUpdatedFromModal}
          handleMarkTaskReadyForValidation={handleMarkTaskReadyForValidation}
          handleRunFullCloudAssessment={handleRunFullCloudAssessment}
          setMode={setMode}
          getRelatedSopTitle={getRelatedSopTitle}
          getRelatedSopVersionLabel={getRelatedSopVersionLabel}
          getRelatedSopReleaseLabel={getRelatedSopReleaseLabel}
        />

        <section className="posture-framework-scope" aria-labelledby="posture-framework-scope-title">
          <div className="posture-framework-scope__heading">
            <p className="posture-framework-scope__eyebrow">Posture scope</p>
            <h2 id="posture-framework-scope-title">Framework</h2>
            <p>Select the compliance framework used throughout this investigation view.</p>
          </div>

          <div className="posture-framework-scope__options" role="group" aria-label="Framework">
            {FRAMEWORKS.map((fw) => {
              const isSelected = framework === fw;

              return (
                <Button
                  key={fw}
                  variant="secondary"
                  size="small"
                  className={`posture-framework-scope__option ${
                    isSelected ? "posture-framework-scope__option--selected" : ""
                  }`}
                  aria-pressed={isSelected}
                  onClick={() => handleFrameworkChange(fw)}
                >
                  {fw}
                </Button>
              );
            })}
          </div>
        </section>

        <section
          className="posture-command-view"
          aria-labelledby="posture-command-view-title"
        >
          <header className="posture-command-view__header">
            <div className="posture-command-view__heading">
              <p className="posture-command-view__eyebrow">Posture command view</p>
              <h2 id="posture-command-view-title">Current posture</h2>
              <p>
                Refresh recalculates control status, freshness, and scoring from the latest
                ingested evidence.
              </p>
            </div>

            <div className="posture-command-view__actions">
              <StatusChip
                tone={
                  riskTone === "low"
                    ? "success"
                    : riskTone === "medium"
                    ? "warning"
                    : "danger"
                }
              >
                {riskLabel}
              </StatusChip>
              <Button
                variant="secondary"
                size="small"
                onClick={() => setShowScoreDetailsModal(true)}
                title="View raw score, operational score, deltas, and accepted-risk math"
              >
                Score details
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={handleRunFreshAssessment}
                disabled={assessmentRefreshing || fullAssessmentRunning || loading}
                loading={assessmentRefreshing}
                loadingLabel="Refreshing…"
                title="Recalculate posture from latest ingested evidence"
              >
                Run fresh assessment
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={handleRunFullCloudAssessment}
                disabled={assessmentRefreshing || fullAssessmentRunning || loading}
                loading={fullAssessmentRunning}
                loadingLabel="Running cloud check…"
                title="Run AWS Config sync, then recalculate posture"
              >
                Run full cloud assessment
              </Button>
            </div>
          </header>

          <div className="posture-command-view__body">
            {assessmentRefreshNotice ? (
              <div className="posture-command-view__notice posture-command-view__notice--success" role="status">
                {assessmentRefreshNotice}
              </div>
            ) : null}

            {assessmentRefreshError ? (
              <div className="posture-command-view__notice posture-command-view__notice--danger" role="alert">
                {assessmentRefreshError}
              </div>
            ) : null}

            {fullAssessmentRunning && fullAssessmentStatus ? (
              <div className="posture-command-view__notice posture-command-view__notice--progress" role="status">
                <span>{fullAssessmentStatus}</span>
                {lastSyncJobId ? <small>Sync job: {lastSyncJobId}</small> : null}
              </div>
            ) : null}

            {fullAssessmentError ? (
              <div className="posture-command-view__notice posture-command-view__notice--danger" role="alert">
                {fullAssessmentError}
              </div>
            ) : null}

            <dl className="posture-command-view__metrics">
              <div className="posture-command-view__metric posture-command-view__metric--success">
                <dt>Framework</dt>
                <dd>{framework}</dd>
                <small>Selected scope</small>
              </div>

              <div className={`posture-command-view__metric posture-command-view__metric--${
                riskTone === "low" ? "success" : riskTone === "medium" ? "warning" : "danger"
              }`}>
                <dt>Raw score</dt>
                <dd>{heroMetrics.latestScore.toFixed(1)}%</dd>
                <small>Includes all findings</small>
              </div>

              <div className={`posture-command-view__metric posture-command-view__metric--${
                heroMetrics.operationalScore >= 95
                  ? "success"
                  : heroMetrics.operationalScore >= 85
                  ? "warning"
                  : "danger"
              }`}>
                <dt>Operational score</dt>
                <dd>{heroMetrics.operationalScore.toFixed(1)}%</dd>
                <small>
                  {heroMetrics.acceptedRiskFindings > 0
                    ? `${heroMetrics.acceptedRiskFindings} accepted-risk finding${
                        heroMetrics.acceptedRiskFindings === 1 ? "" : "s"
                      } separated`
                    : "Same as raw score"}
                </small>
              </div>

              <div className={`posture-command-view__metric posture-command-view__metric--${
                heroMetrics.operationalScoreDelta > 0
                  ? "success"
                  : heroMetrics.operationalScoreDelta < 0
                  ? "danger"
                  : "neutral"
              }`}>
                <dt>Operational delta</dt>
                <dd>{formatSignedNumber(heroMetrics.operationalScoreDelta, 2)}</dd>
                <small>Versus previous operational score</small>
              </div>

              <div className={`posture-command-view__metric posture-command-view__metric--${
                heroMetrics.failingControls > 0 ? "danger" : "success"
              }`}>
                <dt>Active failing controls</dt>
                <dd>{heroMetrics.failingControls}</dd>
                <small>
                  {heroMetrics.acceptedRiskFindings > 0
                    ? `${heroMetrics.acceptedRiskFindings} accepted-risk finding${
                        heroMetrics.acceptedRiskFindings === 1 ? "" : "s"
                      } separated`
                    : "Unresolved fail and error counts"}
                </small>
              </div>

              <button
                type="button"
                className={`posture-command-view__metric posture-command-view__metric--interactive posture-command-view__metric--${
                  heroMetrics.staleControls > 0 ? "warning" : "success"
                }`}
                onClick={() => setShowStaleControlsModal(true)}
                title="Open stale controls"
              >
                <span className="posture-command-view__metric-label">Stale controls</span>
                <strong>{heroMetrics.staleControls}</strong>
                <small>Freshness debt</small>
              </button>

              <div className="posture-command-view__metric posture-command-view__metric--neutral">
                <dt>Data as of</dt>
                <dd>{formatRelativeTime(heroMetrics.dataAsOf)}</dd>
                <small>{formatTimestamp(heroMetrics.dataAsOf)}</small>
              </div>
            </dl>
          </div>
        </section>

        <section
          className="posture-priority-sequence"
          aria-labelledby="posture-priority-sequence-title"
        >
          <header className="posture-priority-sequence__header">
            <p className="posture-priority-sequence__eyebrow">Priority sequence</p>
            <h2 id="posture-priority-sequence-title">Posture action plan</h2>
            <p>
              Work these steps to move toward 100%, no failing controls, and no stale evidence.
            </p>
          </header>

          <ol className="posture-priority-sequence__list">
            {postureActionPlan.map((item, index) => (
              <li
                key={`${item.title}-${index}`}
                className={`posture-priority-sequence__item posture-priority-sequence__item--${
                  item.tone || "neutral"
                }`}
              >
                <div className="posture-priority-sequence__item-heading">
                  <span className="posture-priority-sequence__number" aria-hidden="true">
                    {index + 1}
                  </span>
                  <h3>{item.title}</h3>
                </div>

                <p>{item.detail}</p>

                <Button
                  variant="secondary"
                  size="small"
                  className="posture-priority-sequence__action"
                  onClick={item.onClick}
                  disabled={
                    (item.action === "Run fresh assessment" && assessmentRefreshing) ||
                    fullAssessmentRunning
                  }
                  loading={item.action === "Run fresh assessment" && assessmentRefreshing}
                  loadingLabel="Refreshing…"
                >
                  {item.action}
                </Button>
              </li>
            ))}
          </ol>
        </section>


        {rulePlanCoverageSummary.total > 0 ? (
          <section className="posture-diagnostics" aria-labelledby="posture-diagnostics-title">
            <div className="posture-diagnostics__header">
              <div className="posture-diagnostics__copy">
                <p className="posture-diagnostics__eyebrow">Remediation coverage</p>
                <h2 id="posture-diagnostics-title">Rule plan coverage</h2>
                <p>
                  Review whether active rules use specialized or standard remediation guidance.
                </p>
              </div>

              <div className="posture-diagnostics__summary">
                {RULE_PLAN_FAMILY_ORDER.filter(
                  (family) => rulePlanCoverageSummary.counts[family] > 0
                ).map((family) => {
                  const count = rulePlanCoverageSummary.counts[family] || 0;
                  const tone =
                    family === "generic"
                      ? "neutral"
                      : family === "config" || family === "cloudtrail"
                      ? "informational"
                      : "success";

                  return (
                    <StatusChip
                      key={family}
                      tone={tone}
                      title={`${RULE_PLAN_FAMILY_SHORT_LABELS[family] || family} remediation plans currently loaded`}
                    >
                      {RULE_PLAN_FAMILY_SHORT_LABELS[family] || family} {count}
                    </StatusChip>
                  );
                })}

                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setShowRulePlanDiagnostics((current) => !current)}
                  aria-expanded={showRulePlanDiagnostics}
                  title={
                    showRulePlanDiagnostics
                      ? "Hide rule plan diagnostics"
                      : "Show rule plan diagnostics"
                  }
                >
                  {showRulePlanDiagnostics ? "Hide details" : "Show details"}
                </Button>
              </div>
            </div>

            {showRulePlanDiagnostics ? (
              <div className="posture-diagnostics__body">
                {rulePlanCoverageSummary.standardRows.length > 0 ? (
                  <div className="posture-diagnostics__standard-guidance">
                    <div>
                      <strong>Rules using standard remediation plans</strong>
                      <p>
                        These rules have valid baseline guidance. Upgrade them to dedicated
                        rule-family plans when they become recurring or high priority.
                      </p>
                    </div>

                    <div className="posture-diagnostics__rule-list">
                      {rulePlanCoverageSummary.standardRows.slice(0, 8).map((row) => (
                        <StatusChip
                          key={row.key}
                          tone="neutral"
                          title={[row.framework, row.control_id, row.rule_id, row.resource_id]
                            .filter(Boolean)
                            .join(" · ")}
                        >
                          {row.rule_id || row.control_id || "Standard plan"}
                        </StatusChip>
                      ))}

                      {rulePlanCoverageSummary.standardRows.length > 8 ? (
                        <StatusChip tone="neutral">
                          +{rulePlanCoverageSummary.standardRows.length - 8} more
                        </StatusChip>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="posture-diagnostics__complete">
                    All loaded rules are currently using specialized remediation plans.
                  </div>
                )}
              </div>
            ) : null}
          </section>
        ) : null}


        {SHOW_LEGACY_REMEDIATION_TASKS ? (

        <section className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <div>
              <div className="card-title">Remediation Tasks</div>
              <div
                style={{
                  color: "var(--muted)",
                  fontSize: 12,
                  lineHeight: 1.45,
                  marginTop: 4,
                }}
              >
                Tracked work items created from Fix Plans for the selected framework.
              </div>
            </div>

            <div
              className="hero-pill"
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <span
                className="task-severity"
                style={getSummaryChipStyle(remediationTaskSummary.totalActive > 0, "accent")}
              >
                Active {remediationTaskSummary.totalActive}
              </span>
              <span
                className="task-severity"
                style={getSummaryChipStyle(false)}
              >
                Open {remediationTaskSummary.open}
              </span>
              <span
                className="task-severity"
                style={getSummaryChipStyle(remediationTaskSummary.inProgress > 0, "accent")}
              >
                In Progress {remediationTaskSummary.inProgress}
              </span>
              <span
                className="task-severity"
                style={getSummaryChipStyle(remediationTaskSummary.ready > 0, "warn")}
              >
                Ready {remediationTaskSummary.ready}
              </span>
              <span
                className="task-severity"
                style={getSummaryChipStyle(remediationTaskSummary.blocked > 0, "danger")}
              >
                Blocked {remediationTaskSummary.blocked}
              </span>
              <span
                className="task-severity"
                style={getSummaryChipStyle(remediationTaskSummary.validated > 0, "ok")}
              >
                Validated {remediationTaskSummary.validated}
              </span>
              {remediationTaskSummary.overdue > 0 ? (
                <span
                  className="task-severity"
                  style={getSummaryChipStyle(true, "danger")}
                >
                  Overdue {remediationTaskSummary.overdue}
                </span>
              ) : null}
              <span className="task-severity" style={{ padding: "4px 9px", fontSize: 11, lineHeight: 1.1 }}>
                Closed {remediationTaskSummary.closed}
              </span>
              {showClosedTasks ? (
                <span
                  className="task-severity"
                  style={{
                    padding: "4px 9px",
                    fontSize: 11,
                    lineHeight: 1.1,
                    color: "var(--muted)",
                    borderColor: "color-mix(in srgb, var(--muted), transparent 72%)",
                  }}
                  title="Closed tasks are sorted with the most recently closed task first."
                >
                  Newest First
                </span>
              ) : null}
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                onClick={() => setShowClosedTasks((value) => !value)}
              >
                {showClosedTasks ? "Hide Closed" : "Show Closed"}
              </button>
            </div>
          </div>

          <div className="card-body" style={{ display: "grid", gap: 8 }}>
            {remediationTaskLoadError ? (
              <div style={{ color: "var(--warn)", fontSize: 13 }}>
                Remediation tasks unavailable: {remediationTaskLoadError}
              </div>
            ) : displayedRemediationTasks.length === 0 ? (
              <div className="muted">
                {showClosedTasks
                  ? "No closed remediation tasks for this framework yet."
                  : "No active remediation tasks for this framework yet. Open a Fix Plan and create a task to start tracking work."}
              </div>
            ) : (
              displayedRemediationTasks.slice(0, 8).map((task) => {
                const blockReason = getTaskBlockReason(task);
                const overdue = isTaskOverdue(task);

                return (
                  <div
                    key={task.id}
                    className="open-item-pill"
                    style={{
                      cursor: "default",
                      display: "grid",
                      gap: 5,
                      alignItems: "stretch",
                      padding: "10px 12px",
                      borderColor: overdue
                        ? "color-mix(in srgb, var(--danger), transparent 62%)"
                        : undefined,
                    }}
                  >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
                      {task.title || buildControlLabel(task.control_id, task.rule_id)}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <TaskStatusPill task={task} />
                      <button
                        className="btn btn-ghost btn-xs"
                        type="button"
                        onClick={() => handleOpenManageTask(task)}
                      >
                        Review / Manage
                      </button>
                    </div>
                  </div>

                  {isClosedTask(task) ? (
                    <div
                      style={{
                        color: "var(--muted)",
                        fontSize: 12,
                        lineHeight: 1.35,
                      }}
                    >
                      Closed {formatRelativeTime(task.closed_at || task.updated_at)} · {task.closure_reason || "No closure reason recorded"}
                    </div>
                  ) : null}

                  <div
                    style={{
                      color: "var(--muted)",
                      fontSize: 12,
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      lineHeight: 1.35,
                    }}
                  >
                    <span>{task.framework}</span>
                    <span>{buildControlLabel(task.control_id, task.rule_id)}</span>
                    <span>Owner: {task.owner_name || "Unassigned"}</span>
                    <span
                      style={{
                        color: overdue ? "var(--danger)" : undefined,
                        fontWeight: overdue ? 900 : undefined,
                      }}
                    >
                      Due: {task.due_date || "No due date"}
                      {overdue ? " · Overdue" : ""}
                    </span>
                    <span>Priority: {task.priority || "—"}</span>
                    <span>Validation: {getTaskStatusLabel(task.validation_status || "pending")}</span>
                    <span>Updated: {formatRelativeTime(task.updated_at || task.created_at)}</span>
                  </div>

                  {normalizeText(task.status) === "blocked" && blockReason ? (
                    <div
                      style={{
                        color: "var(--danger)",
                        fontSize: 12,
                        lineHeight: 1.4,
                        fontWeight: 800,
                      }}
                    >
                      Blocked: {blockReason}
                    </div>
                  ) : null}
                </div>
              );
              })
            )}
          </div>
        </section>

        ) : null}

        <section className="posture-state-overview" aria-labelledby="posture-state-overview-title">
          <div className="posture-state-overview__header">
            <div>
              <p className="posture-state-overview__eyebrow">Measured posture state</p>
              <h2 id="posture-state-overview-title">Control and freshness breakdown</h2>
              <p>
                Open any metric to inspect the underlying controls, findings, or approved exceptions.
              </p>
            </div>
          </div>

          <div className="posture-state-overview__grid">
            <button
              type="button"
              className="posture-state-metric posture-state-metric--success"
              onClick={() => openPostureStatusModal("pass")}
              title="Open passed controls"
            >
              <span>Pass</span>
              <strong>{scoreBreakdown.pass}</strong>
              <small>Controls currently passing</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--danger"
              onClick={() => setShowActiveFindingsModal(true)}
              title="Open active findings"
            >
              <span>Active findings</span>
              <strong>{scoreBreakdown.fail}</strong>
              <small>Unresolved findings</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--warning"
              onClick={() => setShowApprovedExceptionsModal(true)}
              title="Open approved exceptions"
            >
              <span>Accepted risk</span>
              <strong>{scoreBreakdown.acceptedRiskFindings}</strong>
              <small>Approved exceptions</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--warning"
              onClick={() => openPostureStatusModal("error")}
              title="Open controls with error state"
            >
              <span>Error</span>
              <strong>{scoreBreakdown.error}</strong>
              <small>Controls requiring source review</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--neutral"
              onClick={() => openPostureStatusModal("unknown")}
              title="Open controls with unknown state"
            >
              <span>Unknown</span>
              <strong>{scoreBreakdown.unknown}</strong>
              <small>Control state not established</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--success"
              onClick={() => openPostureStatusModal("fresh")}
              title="Open fresh controls"
            >
              <span>Fresh</span>
              <strong>{scoreBreakdown.fresh}</strong>
              <small>Evidence within freshness policy</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--warning"
              onClick={() => setShowStaleControlsModal(true)}
              title="Open stale controls"
            >
              <span>Stale</span>
              <strong>{scoreBreakdown.stale}</strong>
              <small>Evidence requiring refresh</small>
            </button>

            <button
              type="button"
              className="posture-state-metric posture-state-metric--neutral"
              onClick={() => openPostureStatusModal("freshness_unknown")}
              title="Open controls with unknown freshness"
            >
              <span>Freshness unknown</span>
              <strong>{scoreBreakdown.freshnessUnknown}</strong>
              <small>Freshness state not established</small>
            </button>
          </div>
        </section>

        {focusedContext.kind ? (
          <section
            ref={investigationSummaryRef}
            className={`card posture-investigation-summary${
              investigationSummaryHighlighted ? " posture-investigation-summary-highlight" : ""
            }`}
            style={{ marginBottom: 18 }}
          >
            <div className="card-header">
              <div>
                <div className="card-title">Investigation Summary</div>
                <div
                  style={{
                    color: "var(--muted)",
                    fontSize: 12,
                    lineHeight: 1.45,
                    marginTop: 4,
                  }}
                >
                  Current context for the selected posture item, related work, and next remediation action.
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {focusedTask ? <TaskStatusPill task={focusedTask} /> : null}
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() =>
                    openPostureExplain(
                      focusedContext.item,
                      focusedContext.kind || "posture_control"
                    )
                  }
                >
                  Explain
                </Button>
                <span
                  className="task-severity"
                  style={{
                    borderRadius: 999,
                    padding: "3px 8px",
                    fontSize: 11,
                    lineHeight: 1.1,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  {focusedSourceLabel}
                </span>
              </div>
            </div>

            <div className="card-body" style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.25fr) minmax(280px, 0.75fr)",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    borderRadius: 16,
                    padding: 14,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 900,
                        lineHeight: 1.25,
                      }}
                    >
                      {buildControlLabel(
                        focusedContext.item.control_id,
                        focusedContext.item.rule_id
                      )}
                    </div>
                    <div
                      style={{
                        color: "var(--muted)",
                        fontSize: 12,
                        lineHeight: 1.45,
                        marginTop: 4,
                      }}
                    >
                      {focusedContext.matched
                        ? `Matched from ${focusedSourceLabel.toLowerCase()}.`
                        : "No exact posture row matched the selected drift item; OASYS is showing the selected context directly."}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                      gap: 8,
                    }}
                  >
                    <MetricCard
                      label="Framework"
                      value={cleanValue(focusedContext.item.framework, framework)}
                    />
                    <MetricCard
                      label="State"
                      value={getDisplayStateLabel(
                        focusedContext.item.state || focusedContext.item.freshness_status,
                        "—"
                      )}
                      tone={normalizeText(focusedContext.item.state).includes("fail") ? "danger" : "default"}
                    />
                    <MetricCard
                      label="Freshness"
                      value={cleanValue(
                        focusedContext.item.freshness_state || focusedContext.item.freshness_status,
                        "—"
                      )}
                      tone={
                        normalizeText(
                          focusedContext.item.freshness_state || focusedContext.item.freshness_status
                        ).includes("stale")
                          ? "warn"
                          : "default"
                      }
                    />
                    <MetricCard
                      label="Open Findings"
                      value={
                        focusedContext.item.open_findings_total === "—"
                          ? "—"
                          : safeNumber(focusedContext.item.open_findings_total, 0)
                      }
                    />
                  </div>

                  <div
                    style={{
                      border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
                      borderRadius: 14,
                      padding: "10px 12px",
                      display: "grid",
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        color: "var(--muted)",
                      }}
                    >
                      Recommended next action
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}>
                      {focusedRecommendedAction}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    borderRadius: 16,
                    padding: 14,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      color: "var(--muted)",
                    }}
                  >
                    Work status
                  </div>

                  {focusedTask ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <MetaField label="Task" value={getTaskStatusLabel(focusedTask.status)} />
                      <MetaField label="Owner" value={focusedTask.owner_name || "Unassigned"} />
                      <MetaField label="Priority" value={focusedTask.priority || "—"} />
                      <MetaField
                        label="Updated"
                        value={formatRelativeTime(focusedTask.updated_at || focusedTask.created_at)}
                      />
                    </div>
                  ) : (
                    <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
                      No remediation task is tracking this item yet. Open the Fix Plan to create one.
                    </div>
                  )}

                  <div
                    style={{
                      borderTop: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
                      paddingTop: 10,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <MetaField
                      label="Linked SOP"
                      value={focusedPrimarySop ? getRelatedSopTitle(focusedPrimarySop) : "No linked SOP"}
                      wrap
                    />
                    <MetaField
                      label="Last Checked"
                      value={formatTimestamp(focusedContext.item.last_checked_at)}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      paddingTop: 2,
                    }}
                  >
                    {focusedTask ? (
                      <button
                        className="btn btn-ghost btn-xs"
                        type="button"
                        onClick={() => handleOpenManageTask(focusedTask)}
                      >
                        View Task
                      </button>
                    ) : null}
                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      onClick={() => handleOpenFixPlan(focusedContext.item, focusedContext.kind)}
                    >
                      Fix Plan
                    </button>
                    {focusedPrimarySop ? (
                      <button
                        className="btn btn-ghost btn-xs"
                        type="button"
                        onClick={() =>
                          setMode("sop", {
                            sopId: getRelatedSopId(focusedPrimarySop),
                            controlId: focusedContext.item.control_id,
                            ruleId: focusedContext.item.rule_id || undefined,
                            framework: focusedContext.item.framework || framework,
                          })
                        }
                      >
                        Open SOP
                      </button>
                    ) : null}
                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      onClick={() =>
                        setMode("drift", {
                          framework: focusedContext.item.framework || framework,
                          controlId: focusedContext.item.control_id,
                          ruleId: focusedContext.item.rule_id || undefined,
                          resourceId: focusedContext.item.resource_id || undefined,
                        })
                      }
                    >
                      Open in Drift
                    </button>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <MetaField label="Control" value={cleanValue(focusedContext.item.control_id)} />
                <MetaField label="Rule" value={cleanValue(focusedContext.item.rule_id)} wrap />
                <MetaField label="Resource" value={cleanValue(focusedContext.item.resource_id)} wrap />
                <MetaField label="Validation" value={buildValidationText(focusedContext.item)} wrap />
              </div>
            </div>
          </section>
        ) : null}

        <section
          ref={relatedSopCardRef}
          className="posture-related-sops"
          aria-labelledby="posture-related-sops-title"
        >
          <div className="posture-related-sops__header">
            <div>
              <p className="posture-related-sops__eyebrow">Governed remediation</p>
              <h2 id="posture-related-sops-title">Related SOPs &amp; remediation</h2>
              <p>
                OASYS connects the selected posture item to controlled procedures and
                deterministic remediation guidance.
              </p>
            </div>

            {hasExplicitPostureSelection && relatedSops.length > 0 ? (
              <Button variant="secondary" size="small" onClick={() => setMode("sop")}>
                Open SOP Center
              </Button>
            ) : null}
          </div>

          <div className="posture-related-sops__body">
            {!hasExplicitPostureSelection ? (
              <div className="posture-related-sops__empty">
                <strong>Select an item to reveal governed guidance</strong>
                <p>
                  Choose a risk, control, drift item, or remediation task to view the
                  governing SOP and recommended next action.
                </p>
              </div>
            ) : relatedSopLoading ? (
              <div className="posture-related-sops__empty">
                <strong>Loading related SOP guidance…</strong>
              </div>
            ) : relatedSopError ? (
              <div className="posture-related-sops__error" role="alert">
                Related SOP guidance unavailable: {relatedSopError}
              </div>
            ) : (
              <div className="posture-related-sops__layout">
                <section className="posture-related-sops__panel">
                  <div className="posture-related-sops__panel-heading">
                    <span>Controlled procedures</span>
                    <strong>Related SOPs</strong>
                  </div>

                  {relatedSops.length === 0 ? (
                    <p className="posture-related-sops__muted">
                      No SOP matched this exact control or rule yet. OASYS also checks
                      the framework-prefixed control ID before falling back to
                      control-level guidance.
                    </p>
                  ) : (
                    <div className="posture-related-sops__list">
                      {relatedSops.map((sop, index) => (
                        <article
                          key={`${sop.sop_id || sop.id || index}`}
                          className="posture-related-sops__item"
                        >
                          <div className="posture-related-sops__item-heading">
                            <div>
                              <strong>{getRelatedSopTitle(sop)}</strong>
                              <small>
                                {getRelatedSopVersionLabel(sop)} ·{" "}
                                {getRelatedSopReleaseLabel(sop)}
                              </small>
                            </div>

                            <StatusChip tone={sop?.is_effective ? "success" : "warning"}>
                              {sop?.is_effective ? "Effective" : "Not effective"}
                            </StatusChip>
                          </div>

                          <p>
                            Rule: {cleanValue(sop?.link?.rule_id || focusControlContext?.ruleId)}
                            {" · "}
                            Framework:{" "}
                            {cleanValue(
                              sop?.link?.framework ||
                                focusControlContext?.framework ||
                                framework
                            )}
                          </p>
                        </article>
                      ))}
                    </div>
                  )}
                </section>

                <section className="posture-related-sops__panel">
                  <div className="posture-related-sops__panel-heading">
                    <span>Deterministic guidance</span>
                    <strong>Recommended action</strong>
                  </div>

                  {recommendedActions.length === 0 ? (
                    <p className="posture-related-sops__muted">
                      No deterministic remediation guidance was returned for this
                      control yet.
                    </p>
                  ) : (
                    <ol className="posture-related-sops__actions-list">
                      {recommendedActions.map((action, index) => (
                        <li key={`${action}-${index}`}>{action}</li>
                      ))}
                    </ol>
                  )}

                  {relatedSopRemediation?.remediation_card?.open_sop_id ? (
                    <Button
                      variant="secondary"
                      size="small"
                      className="posture-related-sops__open-action"
                      onClick={() =>
                        setMode("sop", {
                          sopId: relatedSopRemediation.remediation_card.open_sop_id,
                          controlId: focusControlContext?.controlId,
                          ruleId: focusControlContext?.ruleId || undefined,
                          framework: focusControlContext?.framework || framework,
                        })
                      }
                    >
                      Open governing SOP
                    </Button>
                  ) : null}
                </section>
              </div>
            )}
          </div>
        </section>

        <section ref={remediationOperationsCardRef}>
        <RemediationOperationsPanel
            framework={framework}
            fetchToken={fetchToken}
            getJsonWithToken={getJsonWithToken}
            postJsonWithToken={postJsonWithToken}
            refreshKey={assessmentRefreshTick}
            onOpenTask={handleOpenManageTask}
            onOpenFixPlan={(task) => handleOpenFixPlan(task, "task")}
            onStartTask={(task) =>
              handleUpdateRemediationTask(
                task,
                {
                  status: "in_progress",
                  task_metadata: {
                    ...(task.task_metadata || {}),
                    stage: "in_progress",
                    source: "remediation_operations_center",
                  },
                },
                "Task moved to In Progress."
              )
            }
            onMarkReady={handleMarkTaskReadyForValidation}
            onRunValidation={handleRunFullCloudAssessment}
            isValidationRunning={fullAssessmentRunning}
            currentUserId={userId}
            currentUserName={userName}
            activeSnapshotKey={activeWorkQueueSnapshotKey}
            onOpenAllActiveTasksModal={handleOpenAllActiveTasksModal}
            onOpenQueueStatusTasksModal={handleOpenQueueStatusTasksModal}
            onOpenValidationFailedModal={() => setShowValidationFailedModal(true)}
            onOpenOverdueTasksModal={handleOpenOverdueTasksModal}
            onOpenInProgressTasksModal={handleOpenInProgressTasksModal}
            onOpenClosedTasksModal={handleOpenClosedTasksModal}
            onOpenApprovedExceptionsModal={() => setShowApprovedExceptionsModal(true)}
            onOpenRiskQueue={() => {
              setSelectedRemainingRiskKeys(new Set());
              setRemainingRiskBulkError("");
              setRemainingRiskBulkNotice("");
              setRiskQueueFilter((riskQueueFilterCounts.no_task || 0) > 0 ? "no_task" : "all");
              setShowRemainingRisksModal(true);
            }}
          />
        </section>

        <div className="content-column posture-lower-stack">
            <section ref={topRisksCardRef} className="section-card">
              <div className="section-header" style={{ alignItems: "flex-start", gap: 16 }}>
                <div>
                  <div className="section-title">Top Risks</div>
                  <div className="section-subtitle">
                    Highest-weighted posture risks for the selected framework. Select a row to update the Investigation Summary.
                  </div>
                  {topRisks.length > 0 ? (
                    <div
                      style={{
                        color: "var(--muted)",
                        fontSize: 12,
                        lineHeight: 1.45,
                        marginTop: 8,
                      }}
                    >
                      Showing {visibleTopRisks.length} of {activeRiskTotal} active risk
                      {activeRiskTotal === 1 ? "" : "s"}.
                    </div>
                  ) : null}
                </div>

                {activeRiskTotal > 0 ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => {
                      setSelectedRemainingRiskKeys(new Set());
                      setRemainingRiskBulkError("");
                      setRemainingRiskBulkNotice("");
                      setRiskQueueFilter("all");
                      setShowRemainingRisksModal(true);
                    }}
                    title={`Open all ${activeRiskTotal} active risk${activeRiskTotal === 1 ? "" : "s"} in the Risk Queue`}
                    style={{
                      borderRadius: 999,
                      paddingInline: 16,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open Risk Queue
                  </button>
                ) : null}
              </div>

              <div className="tasks-list">
                {topRisks.length === 0 ? (
                  <div className="muted" style={{ padding: 18 }}>
                    No live top-risk records available for this framework.
                  </div>
                ) : (
                  visibleTopRisks.map((risk, idx) => {
                    const isMatched = matchRiskRow(risk, effectiveFocusContext);
                    const linkedTask = findTaskForItem(activeRemediationTasks, risk, framework);

                    return (
                      <div
                        key={`${risk.framework}-${risk.control_id}-${risk.rule_id}-${idx}`}
                        className="task-row posture-selectable-card"
                        role="button"
                        tabIndex={0}
                        aria-pressed={isMatched}
                        onClick={() => handleViewGuidance(risk)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleViewGuidance(risk);
                          }
                        }}
                        style={{
                          ...getSelectableCardStyle(isMatched),
                          alignItems: "center",
                          gap: 12,
                          padding: "14px 16px",
                        }}
                      >
                        <div className="task-main">
                          <div className="task-title">
                            {buildControlLabel(risk.control_id, risk.rule_id)}
                          </div>
                          <div className="task-meta" style={{ alignItems: "center", gap: 8, rowGap: 4 }}>
                            <span>{risk.framework}</span>
                            <span>Severity: {risk.severity || risk.severity_weight || "—"}</span>
                            <span>Score: {safeNumber(risk.score).toFixed(2)}</span>
                            {isMatched ? <span>Drift match</span> : null}
                            {linkedTask ? <TaskStatusPill task={linkedTask} /> : null}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--muted)",
                              marginTop: 4,
                              lineHeight: 1.45,
                            }}
                          >
                            {risk.reason_summary || "No reason summary available."}
                          </div>
                          {risk.remediation_hint ? (
                            <div
                              style={{
                                fontSize: 12,
                                marginTop: 6,
                                color: "var(--text)",
                                lineHeight: 1.45,
                              }}
                            >
                              Remediation: {risk.remediation_hint}
                            </div>
                          ) : null}
                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              size="small"
                              variant="ghost"
                              onClick={(event) => {
                                event.stopPropagation();
                                openPostureExplain(risk, "risk");
                              }}
                            >
                              Explain
                            </Button>
                          </div>
                        </div>

                        <div className="task-right posture-card-actions">
                          {linkedTask ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenManageTask(linkedTask);
                              }}
                            >
                              View Task
                            </button>
                          ) : null}
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenFixPlan(risk, "risk");
                            }}
                          >
                            Fix Plan
                          </button>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleViewGuidance(risk);
                            }}
                          >
                            Guidance
                          </button>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              setMode("drift", {
                                framework: risk.framework,
                                controlId: risk.control_id,
                                ruleId: risk.rule_id,
                                resourceId: risk.resource_id || undefined,
                              });
                            }}
                          >
                            Open in Drift
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}

              </div>
            </section>


          <div className="posture-identity-stale-grid">
            <section className="section-card identity-exposure-card">
              <div className="section-header identity-exposure-header" style={{ alignItems: "flex-start", gap: 16 }}>
                <div>
                  <div className="section-title">Identity Exposure</div>
                  <div className="section-subtitle">
                    Google Workspace and Okta identity checks now flow into posture, freshness, and remediation workflows.
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setRiskQueueFilter("identity");
                    setShowRemainingRisksModal(true);
                  }}
                  disabled={identityRiskSummary.total === 0}
                >
                  Open Identity Risks
                </Button>
              </div>

              <div className="metric-grid identity-exposure-metrics">
                <MetricCard
                  label="Identity Checks"
                  value={identityRiskSummary.total}
                  footnote={`${identityRiskSummary.googleWorkspace} Google Workspace · ${identityRiskSummary.okta} Okta`}
                  tone={identityRiskSummary.failed > 0 ? "warn" : "ok"}
                />
                <MetricCard
                  label="Failed"
                  value={identityRiskSummary.failed}
                  footnote={`${identityRiskSummary.passing} passing`}
                  tone={identityRiskSummary.failed > 0 ? "danger" : "ok"}
                />
                <MetricCard
                  label="MFA / 2SV"
                  value={identityRiskSummary.mfa}
                  footnote="Authentication posture"
                  tone={identityRiskSummary.mfa > 0 && identityRiskSummary.failed > 0 ? "warn" : "default"}
                />
                <MetricCard
                  label="Admin / Lifecycle"
                  value={identityRiskSummary.admin + identityRiskSummary.lifecycle}
                  footnote={`${identityRiskSummary.admin} admin · ${identityRiskSummary.lifecycle} lifecycle`}
                  tone="default"
                />
              </div>

              <div className="identity-exposure-findings-header">
                <div>
                  <div className="identity-exposure-findings-title">Identity findings</div>
                  <div className="identity-exposure-findings-copy">
                    Prioritized connector evidence for the selected framework.
                  </div>
                </div>
                <StatusChip tone={identityRiskSummary.failed > 0 ? "warning" : "success"}>
                  {visibleIdentityRiskRows.length} shown
                </StatusChip>
              </div>

              <div className="tasks-list identity-exposure-list">
                {visibleIdentityRiskRows.length === 0 ? (
                  <div className="muted" style={{ padding: 18 }}>
                    No Google Workspace or Okta identity findings are available yet.
                  </div>
                ) : (
                  visibleIdentityRiskRows.map((finding, idx) => {
                    const state = normalizeText(finding.state || finding.status || "unknown");
                    const isFailed = ["fail", "failed", "error"].includes(state);
                    const linkedTask = findTaskForItem(activeRemediationTasks, finding, framework);
                    const sourceLabel = getIdentitySourceLabel(finding);
                    const ruleLabel = getIdentityRuleLabel(finding.rule_id);
                    const isMatched = matchRiskRow(finding, effectiveFocusContext);

                    return (
                      <div
                        key={`identity-${finding.source}-${finding.stream}-${finding.rule_id}-${finding.resource_id}-${idx}`}
                        className="task-row posture-selectable-card identity-exposure-row"
                        role="button"
                        tabIndex={0}
                        aria-pressed={isMatched}
                        onClick={() => handleViewGuidance(finding)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleViewGuidance(finding);
                          }
                        }}
                        style={{
                          ...getSelectableCardStyle(isMatched),
                          alignItems: "center",
                          gap: 12,
                          padding: "14px 16px",
                        }}
                      >
                        <div className="task-main">
                          <div className="task-title">
                            {ruleLabel}
                            <StatusChip tone={isFailed ? "danger" : "success"}>
                              {getDisplayStateLabel(state)}
                            </StatusChip>
                          </div>
                          <div className="task-meta" style={{ alignItems: "center", gap: 8, rowGap: 4 }}>
                            <span>{sourceLabel}</span>
                            <span>{finding.stream || "identity_inventory"}</span>
                            <span>{finding.resource_id || "population check"}</span>
                            {finding.control_id ? <span>{framework} {finding.control_id}</span> : null}
                            {linkedTask ? <TaskStatusPill task={linkedTask} /> : null}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--muted)",
                              marginTop: 4,
                              lineHeight: 1.45,
                            }}
                          >
                            {finding.reason_summary || "Identity evidence is connected to posture for the selected framework."}
                          </div>
                          {isFailed ? (
                            <div
                              style={{
                                fontSize: 12,
                                marginTop: 6,
                                color: "var(--text)",
                                lineHeight: 1.45,
                              }}
                            >
                              Remediation: {finding.remediation_hint}
                            </div>
                          ) : null}
                        </div>

                        <div className="task-right posture-card-actions">
                          {linkedTask ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenManageTask(linkedTask);
                              }}
                            >
                              View Task
                            </button>
                          ) : null}
                          {isFailed ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenFixPlan(finding, "risk");
                              }}
                            >
                              Fix Plan
                            </button>
                          ) : null}
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleViewGuidance(finding);
                            }}
                          >
                            Guidance
                          </button>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              setMode("drift", {
                                framework,
                                controlId: finding.control_id || undefined,
                                ruleId: finding.rule_id || undefined,
                                resourceId: finding.resource_id || undefined,
                              });
                            }}
                          >
                            Open in Drift
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section ref={staleControlsCardRef} className="section-card posture-stale-controls-card">
              <div className="filters-header posture-stale-controls-header">
                <div>
                  <div className="filters-title">Stale Controls</div>
                  <div className="section-subtitle" style={{ marginTop: 4 }}>
                    Select a row to update the Investigation Summary.
                  </div>
                </div>
                <StatusChip tone={staleControls.length > 0 ? "warning" : "success"}>
                  {staleControls.length} stale
                </StatusChip>
              </div>

              <div className="filters-actions posture-stale-controls-list">
                {staleControls.length === 0 ? (
                  <div className="muted">No stale controls returned for this framework.</div>
                ) : (
                  staleControls.map((control, idx) => {
                    const isMatched = matchRiskRow(control, effectiveFocusContext);
                    const linkedTask = findTaskForItem(activeRemediationTasks, control, framework);

                    return (
                      <div
                        key={`${control.framework}-${control.control_id}-${control.rule_id}-${idx}`}
                        className="task-row posture-selectable-card posture-stale-control-row"
                        role="button"
                        tabIndex={0}
                        aria-pressed={isMatched}
                        onClick={() => handleViewGuidance(control)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleViewGuidance(control);
                          }
                        }}
                        style={{
                          ...getSelectableCardStyle(isMatched),
                          display: "grid",
                          gap: 7,
                          padding: "14px 16px",
                        }}
                      >
                        <div className="task-main">
                          <div className="task-title">
                            {buildControlLabel(control.control_id, control.rule_id)}
                          </div>
                          <div className="task-meta" style={{ alignItems: "center", gap: 8, rowGap: 4 }}>
                            <span>{control.framework}</span>
                            <span>SLA: {control.sla_hours}h</span>
                            <span>Last seen: {formatTimestamp(control.last_seen_at)}</span>
                            {isMatched ? <span>Drift match</span> : null}
                            {linkedTask ? <TaskStatusPill task={linkedTask} /> : null}
                          </div>
                        </div>

                        <div
                          className="task-right"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 7,
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <div style={{ gridColumn: "1", gridRow: "1", justifySelf: "start" }}>
                            <StatusChip tone="warning">
                              {control.freshness_status || "stale"}
                            </StatusChip>
                          </div>

                          {linkedTask ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              style={{ gridColumn: "2", gridRow: "1", width: "100%" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenManageTask(linkedTask);
                              }}
                            >
                              View Task
                            </button>
                          ) : null}

                          <button
                            className="btn btn-ghost btn-xs"
                            style={{
                              gridColumn: linkedTask ? "1" : "2",
                              gridRow: linkedTask ? "2" : "1",
                              width: "100%",
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenFixPlan(control, "stale");
                            }}
                          >
                            Fix Plan
                          </button>

                          <button
                            className="btn btn-ghost btn-xs"
                            style={{ gridColumn: "2", gridRow: "2", width: "100%" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setMode("drift", {
                                framework: control.framework,
                                controlId: control.control_id,
                                ruleId: control.rule_id,
                                resourceId: control.resource_id || undefined,
                              });
                            }}
                          >
                            Open in Drift
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

          </div>

            <section ref={controlStatusCardRef} className="section-card">
              <div className="section-header">
                <div>
                  <div className="section-title">Control Status</div>
                  <div className="section-subtitle">
                    Current control-level posture and freshness state.
                  </div>
                </div>
              </div>

              <div className="card-body" style={{ overflowX: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Control</th>
                      <th>State</th>
                      <th>Freshness</th>
                      <th>Last Checked</th>
                      <th>Open Findings</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredControlStatusRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ color: "var(--muted)" }}>
                          No control-level posture rows returned.
                        </td>
                      </tr>
                    ) : (
                      filteredControlStatusRows.map((row, idx) => {
                        const isMatched = matchControlStatusRow(row, effectiveFocusContext);
                        const linkedTask = findTaskForItem(activeRemediationTasks, row, framework);

                        return (
                          <tr
                            key={`${row.framework}-${row.control_id}-${idx}`}
                            className="posture-selectable-row"
                            tabIndex={0}
                            aria-selected={isMatched}
                            onClick={() => handleViewGuidance(row)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleViewGuidance(row);
                              }
                            }}
                            style={getSelectableTableRowStyle(isMatched)}
                          >
                            <td>
                              <div style={{ display: "grid", gap: 4 }}>
                                <span>{buildControlLabel(row.control_id, row.rule_id)}</span>
                                {linkedTask ? <TaskStatusPill task={linkedTask} /> : null}
                              </div>
                            </td>
                            <td>{row.state}</td>
                            <td>{row.freshness_state}</td>
                            <td>{formatTimestamp(row.last_checked_at)}</td>
                            <td>{row.open_findings_total}</td>
                            <td>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                {linkedTask ? (
                                  <button
                                    className="btn btn-ghost btn-xs"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenManageTask(linkedTask);
                                    }}
                                  >
                                    View Task
                                  </button>
                                ) : null}
                                <button
                                  className="btn btn-ghost btn-xs"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenFixPlan(row, "control");
                                  }}
                                >
                                  Fix Plan
                                </button>
                                <button
                                  className="btn btn-ghost btn-xs"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleViewGuidance(row);
                                  }}
                                >
                                  Guidance
                                </button>
                                <Button
                                  size="small"
                                  variant="ghost"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openPostureExplain(row, "control_status");
                                  }}
                                >
                                  Explain
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
        </div>
      </div>
    </div>
  );
} 